// ============================================================
// ANNIHILATION — THE OLD FOLD
// The home village. Login here, walk around, rest, shop, train.
// Converted from Terror's Plaza engine (Vyren pattern: internal
// ids like "plaza" stay put; every display string is the Fold's).
//
// Building/villager display data lives in ONE place:
// src/annihilation/villageData.js — this file only owns geometry.
//
// ── SPRITE SWAP GUIDE ────────────────────────────────────────
// Drop .webp files onto annihilation-assets.pages.dev, then
// update the SPRITES/UI objects below. Missing art falls back
// to pulsing labeled tiles — by design.
// player sprites: player_{m|f}_{down|up|left|right}_{a|b}.webp
// ============================================================

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ref, set, onValue, remove, get } from "firebase/database";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
} from "firebase/auth";
import { db, auth } from "./firebase";
import { SD, GM, shimmerBorder, GOLD_METAL, GOLD_METAL_H, OBSIDIAN_SLAB, OBSIDIAN_RECESS, facetTB, textGlow, TEXT_SET } from "./theme";
import {
  CDN,
  PLAYER_SPRITES,
  COMPANION_SPRITES,
  getPlayerSprite,
  getIdleSprite,
  MobileDpad,
  useChatSystem,
  ChatSystem,
  useOrientationLock,
  RotateGate,
  DockPortal,
} from "./engineBarrel";
import CompanionSprite from "./CompanionSprite";
import CompanionCloseup from "./CompanionInteraction";
import StartMenu from "./StartMenu";
import AchievementGallery from "./AchievementGallery";
import AnnihilationCodex from "./AnnihilationCodex";
import AchievementToast from "./AchievementToast";
import { useAchievements } from "./useAchievements";
import { CosmeticOverlay, getEquippedTitle } from "./CosmeticOverlay";
import { STARTER_DECKS, getStarterDeck } from "./starterDecks";
import { DUNGEONS, INNER_VILLAINS } from "./storyData";
import EndlessDepths from "./EndlessDepths";
import Onboarding from "./Onboarding";
import { ensureStarterCompanion, awardSweetTreat } from "./progression";
import { COMPANIONS, getStarterCompanionId } from "./companions";
import RoomConsole, { getSessionLog, setSessionLog } from "./RoomConsole";
import RoomBackdrop from "./RoomBackdrop";
import { BUILDINGS as VILLAGE_BUILDINGS, NPCS as VILLAGERS } from "./villageData";
// ── THE 3D SWAP (ie-3d-conversion) ───────────────────────────
// OldFold3D renders the SAME village from the SAME geometry
// (oldFoldGeometry.js) and fires the SAME callbacks. Flip the
// flag to false and the shipped 2D tile world returns untouched.
import OldFold3D from "./OldFold3D";
// Being continuity (July 16): a Transcended player renders AS their
// being — here, in the Grove, in the Fold below, everywhere. And the
// companion is REVERSED: your Terror character walks beside the beast.
import { BeingSprite, TerrorTravelerVisual } from "./BeingSprite";
import { levelForXp, tierForLevel } from "./stats";

const VILLAGE_3D = true; // rollback switch — 2D world is one flag away

// ── WRITE-LANE LAW ───────────────────────────────────────────
// Annihilation owns annihilation/* only. The Old Fold's presence,
// ready-up, and challenge traffic all live under this node —
// NEVER under Terror's plaza/*. Every path below routes through
// NET. (Flagged for the Firebase hardening whitelist: players,
// ready, challenges.)
const NET = "annihilation/village";

// ── Grimoire palette — CONSOLIDATED (July 5, 2026) ───────────
// The local GM copy died in the GILDED OBSIDIAN wave. GM now
// imports from theme.js (v4) — one source of truth, every future
// theme shift is a one-file deploy. Do NOT re-declare GM here.


// ── Firebase Auth helpers ─────────────────────────────────────
const getProfileByUid = async (uid) => {
  const snap = await get(ref(db, `profiles/${uid}`));
  return snap.exists() ? snap.val() : null;
};

const createProfile = async (uid, displayName, starterDeckId = null) => {
  const profile = {
    uid, name: displayName,
    divineCrests: 600,
    totalWins: 0,
    storyProgress: { currentDungeon: 1, defeatedOpponents: [], completedDungeons: [] },
    collection: {},
    lastLogin: Date.now(),
    createdAt: Date.now(),
    starterDeckId: starterDeckId || null,
  };
  await set(ref(db, `profiles/${uid}`), profile);
  return profile;
};

const listenToProfileByUid = (uid, callback) => {
  return onValue(ref(db, `profiles/${uid}`), snap => {
    if (snap.exists()) callback({ ...snap.val(), uid });
    else callback(null);
  });
};

// ─────────────────────────────────────────────────────────────
// SPRITE MAP
// ─────────────────────────────────────────────────────────────
// CDN imported from engine

const UI = {
  interact: `${CDN}/a_main.webp`,
  npc:      `${CDN}/a_npc_box.webp`,
  bulletin: `${CDN}/a_bulletin.webp`,
  solrend:  `${CDN}/a_solrend.webp`,
  boss:     `${CDN}/a_boss_box.webp`,
};

// plaza_bg — Plaza-specific background
const PLAZA_BG = `${CDN}/p_center.webp`;

// Portal sprite map — null until art drops
const PORTAL_SPRITES = {
  portal_gate:   null,
  portal_pierre: null,
};

// ─────────────────────────────────────────────────────────────
// MAP CONFIG
// ─────────────────────────────────────────────────────────────
const COLS = 34;
const ROWS = 20;

// Mobile detection
const isMobile = () => ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

// Portrait = tall screen on a touch device
const isPortrait = () => isMobile() && window.innerHeight > window.innerWidth;

// ── PORTRAIT SPLIT-SCREEN ─────────────────────────────────────
// In portrait the world only occupies the TOP slice of the screen;
// the bottom is the reserved dock (d-pad + popups). This MUST match
// WORLD_FRACTION in engine/PortraitFrame.js or the camera and the
// visible world clip will disagree.
const PORTRAIT_WORLD_FRACTION = 0.62;

// Effective viewport HEIGHT for camera/tile math. In portrait this is
// just the world-zone height (top slice), so the player stays centered
// in the visible world instead of behind the dock.
function getViewportH() {
  if (isPortrait()) return Math.round(window.innerHeight * PORTRAIT_WORLD_FRACTION);
  return window.innerHeight;
}

// Portrait mobile → fixed 48px tiles, camera follows player
// Landscape/desktop → target tile size, camera follows player (was: fit whole map)
const PORTRAIT_TILE = 48;

// ── PORTRAIT MOBILE compact layout (20×12) ────────────────────
const P_COLS = 20;
const P_ROWS = 12;

// ── DESKTOP camera viewport target ────────────────────────────
// We aim for ~16 tiles wide / ~10 tiles tall worth of viewport.
// Tile size derived so the character reads big and the world
// extends past the screen edges (camera follows, clamped to map).
// WORKSHOP-STYLE FIT (July 8, final form) — landscape shows the WHOLE
// plaza at once, centered like Willie's Workshop, with SYMMETRIC vertical
// bars on BOTH sides. THE RECORD lives in the right bar; the left bar is
// reserved real estate for what's coming — animations, menus, data, the
// works ("holy smokes im cooking" — caP'n, canon). No scrolling camera;
// calcCamOffset's "map smaller than viewport → center it" clamp places it.

// Width the Record panel claims on the right (RoomConsole is min(280,24vw)
// at right:12). calcTile subtracts this so the map fits left of it, and
// calcCamOffset centers the map in that same (viewport − reserve) space so
// it sits flush-left of the panel with a matching gap on the left. ONE
// source of truth — the two must always agree or the map drifts.
function RECORD_RESERVE() {
  if (typeof window === "undefined") return 0;
  if (isPortrait()) return 0;  // panel is bottom-docked in portrait
  return Math.min(280, window.innerWidth * 0.24) + 32;  // panel + right:12 + gap
}

// ── PLAZA CAMERA ZOOM (caP'n, July 8 — "zoom in like it used to") ──────
// Multiplier on the fit-to-screen tile size. 1.0 = the old whole-plaza
// fit (map centered, no scrolling). >1.0 = tiles get bigger, the map
// grows past the viewport, and calcCamOffset's deadzone-follow takes over
// so the camera tracks the character — bigger sprite, same screen frame,
// same backdrop, same map (objectFit:"fill" still draws all of it, just
// larger). Nothing else moves. Nudge this one number to taste.
const PLAZA_ZOOM = 1.85;

function calcTile() {
  // Portrait: fixed tile, camera follows the player (zoomed in, PC-like).
  // Do NOT fit the whole compact map here — that pins the world to the top
  // and zooms all the way out. This regressed once; keep the fixed tile.
  if (isPortrait()) return PORTRAIT_TILE;
  // Desktop/landscape. Base fit: the tile size at which the whole 34×20 map
  // would fit left of the Record panel (Record is RIGHT-only, so reserve one
  // side). We then SCALE that up by PLAZA_ZOOM to zoom in on the character;
  // the surplus size spills off-screen and the follow-cam handles it.
  const byWidth  = Math.floor((window.innerWidth - RECORD_RESERVE()) / COLS);
  const byHeight = Math.floor(window.innerHeight / ROWS);
  const fit = Math.min(byWidth, byHeight);
  return Math.max(12, Math.round(fit * PLAZA_ZOOM));
}

// ── Deadzone camera — camera only moves when player leaves
// a centered box in the viewport. Feels solid for small moves,
// smooth for long traversals. Clamps to map bounds so the
// camera never shows past the room edge.
// Returns {x, y} offset to apply as negative translate on the world.
function calcCamOffset(playerX, playerY, tile, prevOffset = null, opts = {}) {
  const cols = opts.cols ?? (isPortrait() ? P_COLS : COLS);
  const rows = opts.rows ?? (isPortrait() ? P_ROWS : ROWS);
  const vw = window.innerWidth;
  const vh = getViewportH();
  const mapW = cols * tile;
  const mapH = rows * tile;

  // Player center in world pixels
  const px = playerX * tile + tile / 2;
  const py = playerY * tile + tile / 2;

  // Portrait: snap-center (old behavior — small screen, no deadzone needed)
  if (isPortrait()) {
    const rawX = px - vw / 2;
    const rawY = py - vh / 2;
    return {
      x: Math.max(0, Math.min(Math.max(0, mapW - vw), rawX)),
      y: Math.max(0, Math.min(Math.max(0, mapH - vh), rawY)),
    };
  }

  // Desktop: deadzone follow
  // Camera center starts centered on player; if previous offset exists,
  // only move when player leaves the deadzone around the current camera center.
  const DZ_W = tile * 4; // 4-tile wide deadzone
  const DZ_H = tile * 3; // 3-tile tall deadzone

  // The Record panel eats the RIGHT edge. To keep the character centered in
  // the VISIBLE play band (viewport − panel) instead of under the panel, we
  // aim the camera at (vw − reserve)/2 horizontally, not vw/2. reserve = 0 in
  // portrait (panel is bottom-docked), so this is a no-op there.
  const reserve = RECORD_RESERVE();
  const anchorX = (vw - reserve) / 2;  // where the player should sit on screen

  let camX, camY;
  if (prevOffset) {
    // Current camera center in world pixels (add back the same anchor bias)
    const camCenterX = prevOffset.x + anchorX;
    const camCenterY = prevOffset.y + vh / 2;
    // How far the player is from camera center
    const dx = px - camCenterX;
    const dy = py - camCenterY;
    // Only move camera if player exceeds deadzone
    let newCenterX = camCenterX;
    let newCenterY = camCenterY;
    if (dx >  DZ_W / 2) newCenterX = px - DZ_W / 2;
    if (dx < -DZ_W / 2) newCenterX = px + DZ_W / 2;
    if (dy >  DZ_H / 2) newCenterY = py - DZ_H / 2;
    if (dy < -DZ_H / 2) newCenterY = py + DZ_H / 2;
    camX = newCenterX - anchorX;
    camY = newCenterY - vh / 2;
  } else {
    // First frame: center player in the visible band
    camX = px - anchorX;
    camY = py - vh / 2;
  }

  // Clamp to map bounds. Horizontal centering happens in (viewport − the
  // Record's right reserve), NOT the full viewport — that's what places the
  // map flush-left of the panel instead of dead-center under it. Same
  // RECORD_RESERVE calcTile used to size the map, so map and gap agree.
  const avail = Math.max(0, vw - RECORD_RESERVE());
  const maxX = Math.max(0, mapW - vw);
  const maxY = Math.max(0, mapH - vh);
  if (mapW <= avail) camX = (mapW - avail) / 2;   // center in the left-of-panel band
  else               camX = Math.max(0, Math.min(maxX, camX));
  if (mapH <= vh) camY = (mapH - vh) / 2;
  else            camY = Math.max(0, Math.min(maxY, camY));

  return { x: camX, y: camY };
}

// Module-level default — used by components outside Plaza
const TILE_DEFAULT = calcTile();

const T = { FLOOR: 0, WALL: 1, PORTAL: 2, SHOP: 3, NPC: 4 };

// ── THE OLD FOLD — DESTINATIONS ──────────────────────────────
// Display strings (names) come from villageData — ONE source of
// truth. This file owns only the geometry (where things stand).
// LAW: landscape and portrait sets stay in sync — every
// destination exists in BOTH, or it only exists in one world.

// The two ways OUT of the village. The Gate carries the co-op
// ready-up flow (that is its whole canonical job); Pierre keeps
// its IE-universal id and skip-ready special-casing.
const PORTALS = [
  { id: "gate",   label: "The Gate",            subtitle: "The Outskirts · Co-op", color: "#e8a33d", dungeonId: 900, spriteKey: "portal_gate",   x: 12, y: 3 },
  { id: "pierre", label: "The Unstable Pierre", subtitle: "Cross-Game Hub",        color: "#06b6d4", dungeonId: -1,  spriteKey: "portal_pierre", x: 22, y: 3 },
];

// The seven walk-in buildings (the Gate is a portal, above).
const BUILDING_COLORS = {
  bonfire: "#e8a33d", marketplace: "#c9a84c", forge: "#f0946a",
  trainer: "#8fb8d8", registrar: "#b8a0e8", bulletin: "#a8c686",
  garden: "#7fe8c8",
};
const BUILDING_GEO = {
  bonfire:     { x: 17, y: 10, rx: 2, ry: 2 },
  marketplace: { x: 5,  y: 15, rx: 3, ry: 2, minX: 1,  maxX: 9 },
  forge:       { x: 29, y: 15, rx: 3, ry: 2, minX: 25 },
  trainer:     { x: 5,  y: 7,  rx: 3, ry: 2, minX: 1,  maxX: 9 },
  registrar:   { x: 29, y: 7,  rx: 3, ry: 2, minX: 25 },
  bulletin:    { x: 24, y: 11, rx: 2, ry: 2, minX: 22, maxX: 26 },
  garden:      { x: 10, y: 17, rx: 3, ry: 1, minX: 6,  maxX: 14 },
};
const SHOPS = VILLAGE_BUILDINGS
  .filter(b => b.id !== "gate" && BUILDING_GEO[b.id])
  .map(b => ({ id: b.id, label: b.name, color: BUILDING_COLORS[b.id] || GM.inkGold, ...BUILDING_GEO[b.id] }));

// Villagers standing in the square (walk-up dialogue).
const VILLAGER_GEO = {
  maerie: { x: 9,  y: 13, rx: 2, ry: 2 },
  sigfer: { x: 9,  y: 6,  rx: 2, ry: 2, maxX: 11 },
  vel:    { x: 17, y: 6,  rx: 2, ry: 2, minX: 15, maxX: 19 },
};
const NPCS = VILLAGERS
  .filter(v => VILLAGER_GEO[v.id])
  .map(v => ({ id: v.id, label: v.name, color: GM.inkGold, ...VILLAGER_GEO[v.id] }));

const SPAWN = { x: 14, y: 13 };

// Where The Gate leads. Shaped like a DUNGEONS entry so the
// ready-up effect and PortalCinematic consume it unmodified.
const GATE_DESTINATION = {
  id: "gate",
  name: "The Outskirts",
  subtitle: "The First Ring",
  flavor: "Beyond the Gate, the world begins. The Whispering Grove waits to the north; the Glass Flats shimmer east. ◆ Walk carefully, Aenarchist. ◆",
  unlocked: true,
};

// ── Portrait mirrors — same destinations, portrait geometry ──
const P_SPAWN = { x: 12, y: 8 };
const P_PORTALS = [
  { id: "gate",   label: "The Gate",            subtitle: "The Outskirts · Co-op", color: "#e8a33d", dungeonId: 900, spriteKey: "portal_gate",   x: 6,  y: 2 },
  { id: "pierre", label: "The Unstable Pierre", subtitle: "Cross-Game Hub",        color: "#06b6d4", dungeonId: -1,  spriteKey: "portal_pierre", x: 13, y: 2 },
];
const P_BUILDING_GEO = {
  trainer:     { x: 2,  y: 7,  rx: 1, ry: 1, maxX: 4 },
  bonfire:     { x: 6,  y: 7,  rx: 1, ry: 1, minX: 5,  maxX: 8 },
  forge:       { x: 10, y: 7,  rx: 1, ry: 1, minX: 9,  maxX: 12 },
  registrar:   { x: 14, y: 7,  rx: 1, ry: 1, minX: 13 },
  marketplace: { x: 3,  y: 10, rx: 2, ry: 1, maxX: 6 },
  bulletin:    { x: 9,  y: 10, rx: 2, ry: 1, minX: 7,  maxX: 11 },
  garden:      { x: 15, y: 10, rx: 2, ry: 1, minX: 13 },
};
const P_SHOPS = VILLAGE_BUILDINGS
  .filter(b => b.id !== "gate" && P_BUILDING_GEO[b.id])
  .map(b => ({ id: b.id, label: b.name, color: BUILDING_COLORS[b.id] || GM.inkGold, ...P_BUILDING_GEO[b.id] }));
const P_VILLAGER_GEO = {
  maerie: { x: 3,  y: 4, rx: 1, ry: 1 },
  sigfer: { x: 9,  y: 4, rx: 1, ry: 1 },
  vel:    { x: 15, y: 4, rx: 1, ry: 1 },
};
const P_NPCS = VILLAGERS
  .filter(v => P_VILLAGER_GEO[v.id])
  .map(v => ({ id: v.id, label: v.name, color: GM.inkGold, ...P_VILLAGER_GEO[v.id] }));
function buildPortraitMap() {
  const t = { FLOOR: 0, WALL: 1, PORTAL: 2, SHOP: 3, NPC: 4 };
  const map = Array.from({ length: P_ROWS }, () => Array(P_COLS).fill(t.FLOOR));
  for (let c = 0; c < P_COLS; c++) { map[0][c] = t.WALL; map[P_ROWS-1][c] = t.WALL; }
  for (let r = 0; r < P_ROWS; r++) { map[r][0] = t.WALL; map[r][P_COLS-1] = t.WALL; }
  P_PORTALS.forEach(p => {
    for (let dr = 0; dr < 2; dr++)
      for (let dc = -1; dc < 2; dc++)
        if (p.y+dr < P_ROWS && p.x+dc >= 0 && p.x+dc < P_COLS)
          map[p.y+dr][p.x+dc] = t.PORTAL;
  });
  P_SHOPS.forEach(s => { if (s.y < P_ROWS && s.x < P_COLS) map[s.y][s.x] = t.SHOP; if (s.x+1 < P_COLS) map[s.y][s.x+1] = t.SHOP; });
  P_NPCS.forEach(n => { if (n.y < P_ROWS && n.x < P_COLS) map[n.y][n.x] = t.NPC; });
  return map;
}
const P_MAP = buildPortraitMap();

const PLAYER_COLORS = ["#c9a84c", "#a78bfa", "#22d3ee", "#f87171"];

function buildMap() {
  const map = Array.from({ length: ROWS }, () => Array(COLS).fill(T.FLOOR));
  for (let c = 0; c < COLS; c++) { map[0][c] = T.WALL; map[ROWS-1][c] = T.WALL; }
  for (let r = 0; r < ROWS; r++) { map[r][0] = T.WALL; map[r][COLS-1] = T.WALL; }
  [[1,1],[1,32],[17,1],[17,32],[6,10],[6,21],[12,10],[12,21]].forEach(([r,c]) => {
    if (r < ROWS && c < COLS) map[r][c] = T.WALL;
  });
  PORTALS.forEach(p => {
    for (let dr = 0; dr < 2; dr++)
      for (let dc = -1; dc < 2; dc++)
        if (p.y+dr < ROWS && p.x+dc >= 0 && p.x+dc < COLS)
          map[p.y+dr][p.x+dc] = T.PORTAL;
  });
  SHOPS.forEach(s => { map[s.y][s.x] = T.SHOP; map[s.y][s.x+1] = T.SHOP; });
  NPCS.forEach(n => { map[n.y][n.x] = T.NPC; });
  return map;
}
const MAP = buildMap();

function isWalkable(x, y) {
  if (isPortrait()) {
    if (x < 0 || y < 0 || x >= P_COLS || y >= P_ROWS) return false;
    return P_MAP[y][x] !== T.WALL;
  }
  if (x < 0 || y < 0 || x >= COLS || y >= ROWS) return false;
  return MAP[y][x] !== T.WALL;
}
function getPortalAt(x, y) {
  const _portals = isPortrait() ? P_PORTALS : PORTALS;
  return _portals.find(p => Math.abs(x - p.x) <= 2 && Math.abs(y - p.y) <= 1) || null;
}
function getShopAt(x, y) {
  const _shops = isPortrait() ? P_SHOPS : SHOPS;
  return _shops.find(s => {
    if (s.minX !== undefined && x < s.minX) return false;
    if (s.maxX !== undefined && x > s.maxX) return false;
    if (s.minY !== undefined && y < s.minY) return false;
    if (s.maxY !== undefined && y > s.maxY) return false;
    return Math.abs(x - s.x) <= (s.rx ?? 2) && Math.abs(y - s.y) <= (s.ry ?? 1);
  }) || null;
}
function getNPCAt(x, y) {
  const _npcs = isPortrait() ? P_NPCS : NPCS;
  return _npcs.find(n => {
    if (n.minX !== undefined && x < n.minX) return false;
    if (n.maxX !== undefined && x > n.maxX) return false;
    if (n.minY !== undefined && y < n.minY) return false;
    if (n.maxY !== undefined && y > n.maxY) return false;
    return Math.abs(x - n.x) <= (n.rx ?? 1) && Math.abs(y - n.y) <= (n.ry ?? 1);
  }) || null;
}

// ─────────────────────────────────────────────────────────────
// SPRITE COMPONENT
// ─────────────────────────────────────────────────────────────
function Sprite({ src, fallbackColor, size = TILE_DEFAULT, style = {}, label = "" }) {
  const [err, setErr] = useState(false);
  const prevSrc = useRef(src);
  useEffect(() => { if (prevSrc.current !== src) { prevSrc.current = src; setErr(false); } }, [src]);
  if (!src || err) {
    // Generated character — head, tunic, legs, drop shadow. Reads as a
    // person from day one; real sprite art replaces it the moment the
    // CDN files exist (the <img> path below takes over automatically).
    return (
      <div style={{ position: "relative", width: size, height: size,
        overflow: "visible", flexShrink: 0, ...style }}>
        <div style={{ position: "absolute", bottom: 0, left: "24%", width: "52%", height: "8%",
          borderRadius: "50%", background: "rgba(0,0,0,0.45)", filter: "blur(1px)" }} />
        <div style={{ position: "absolute", bottom: "6%", left: "34%", width: "12%", height: "20%",
          background: "#241c12", borderRadius: 2 }} />
        <div style={{ position: "absolute", bottom: "6%", right: "34%", width: "12%", height: "20%",
          background: "#241c12", borderRadius: 2 }} />
        <div style={{ position: "absolute", bottom: "22%", left: "26%", width: "48%", height: "36%",
          background: `linear-gradient(180deg, ${fallbackColor}, ${fallbackColor}99)`,
          borderRadius: "4px 4px 2px 2px",
          boxShadow: "inset -2px -3px 4px rgba(0,0,0,0.35)" }} />
        <div style={{ position: "absolute", bottom: "52%", left: "50%", transform: "translateX(-50%)",
          width: "36%", height: "32%", borderRadius: "50%",
          background: "linear-gradient(145deg, #d8b890, #b89468)",
          boxShadow: "inset -2px -2px 3px rgba(0,0,0,0.3)" }}>
          <div style={{ position: "absolute", top: "-24%", left: "8%", width: "84%", height: "52%",
            borderRadius: "50% 50% 20% 20%", background: "#2e2418" }} />
          <div style={{ position: "absolute", top: "46%", left: "26%", width: "9%", height: "9%",
            minWidth: 2, minHeight: 2, borderRadius: "50%", background: "#241a10" }} />
          <div style={{ position: "absolute", top: "46%", right: "26%", width: "9%", height: "9%",
            minWidth: 2, minHeight: 2, borderRadius: "50%", background: "#241a10" }} />
        </div>
        {label && <span style={{ position: "absolute", bottom: 0, left: 0, right: 0,
          fontSize: 7, textAlign: "center", color: "rgba(255,255,255,0.6)", fontFamily: GM.font }}>{label}</span>}
      </div>
    );
  }
  return <img src={src} alt={label} onError={() => setErr(true)}
    style={{ width: size, height: size, imageRendering: "pixelated", ...style }} />;
}

// MobileDpad imported from engine

// ─────────────────────────────────────────────────────────────
// PORTAL VISUAL
// ─────────────────────────────────────────────────────────────
function PortalVisual({ portal, tile }) {
  const TILE = tile || TILE_DEFAULT;
  const [frame, setFrame] = useState(0);
  useEffect(() => { const t = setInterval(() => setFrame(f => (f+1)%60), 50); return () => clearInterval(t); }, []);
  const pulse = 0.7 + 0.3 * Math.sin(frame * 0.2);
  const img = PORTAL_SPRITES[portal.spriteKey || `portal_${portal.id}`];
  return (
    <div style={{ position: "absolute", left: (portal.x-1)*TILE, top: portal.y*TILE, width: TILE*3, height: TILE*2, zIndex: 5 }}>
      {img ? (
        <img src={img} alt={portal.label} onError={e => e.target.style.display="none"} style={{ width: "100%", height: "100%", imageRendering: "pixelated" }} />
      ) : (
        <div style={{
          width: "100%", height: "100%",
          background: `radial-gradient(ellipse at 50% 100%, ${portal.color}${Math.round(pulse*80).toString(16).padStart(2,"0")} 0%, transparent 70%)`,
          border: `2px solid ${portal.color}${Math.round(pulse*160).toString(16).padStart(2,"0")}`,
          borderRadius: 12,
          boxShadow: `0 0 ${Math.round(pulse*30)}px ${portal.color}66, inset 0 0 20px ${portal.color}22`,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", paddingBottom: 4,
        }}>
          <div style={{ fontSize: 8, color: GM.vellum, letterSpacing: 2, fontFamily: GM.font,
            fontWeight: 700, textShadow: `0 0 6px ${portal.color}, 0 1px 3px rgba(0,0,0,1)`, opacity: 0.9 + 0.1 * Math.sin(pulse) }}>
            {portal.label.toUpperCase()}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GENERATED OLD FOLD — procedural village visuals (July 15, 2026)
// caP'n: "generate if you have to, we don't need assets right now."
// Everything below draws the village in pure CSS so the Fold is
// fully visible with ZERO files on the CDN. The moment real art
// (p_center.webp) loads successfully, these layers step aside —
// the bgLoaded gate in the render handles the swap automatically.
// House law: imperative keyframes, id-guarded (no JSX <style>).
// ─────────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("of-proc-kf")) {
  const el = document.createElement("style");
  el.id = "of-proc-kf";
  el.textContent = `
@keyframes ofFlame { 0%,100%{transform:scaleY(1) scaleX(1);opacity:0.95} 50%{transform:scaleY(1.18) scaleX(0.92);opacity:1} }
@keyframes ofEmber { 0%{transform:translateY(0);opacity:0.9} 100%{transform:translateY(-26px);opacity:0} }
@keyframes ofMote  { 0%{transform:translateY(0);opacity:0} 25%{opacity:0.9} 100%{transform:translateY(-34px);opacity:0} }
@keyframes ofBob   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2px)} }
@keyframes ofGlow  { 0%,100%{opacity:0.55} 50%{opacity:0.9} }
`;
  document.head.appendChild(el);
}

// ── Ground — village earth, stone square, connecting paths ────
// Derived from the ACTIVE geometry so landscape and portrait both
// get a correct floor. Paths: one vertical spine from the portal
// row down through the square, one horizontal band along the
// building rows. Simple, readable, walkable-looking.
function OldFoldGround({ cols, rows, tile, portalsArr, portrait }) {
  const W = cols * tile, H = rows * tile;
  const midX = W / 2;
  const squareY = portrait ? 7 * tile : 10 * tile;   // bonfire row
  const stoneR = tile * (portrait ? 2.6 : 3.4);
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }}>
      {/* earth base + faint tile grid */}
      <div style={{ position: "absolute", inset: 0, background: `
        repeating-linear-gradient(0deg, transparent, transparent ${tile - 1}px, rgba(190,170,120,0.045) ${tile}px),
        repeating-linear-gradient(90deg, transparent, transparent ${tile - 1}px, rgba(190,170,120,0.045) ${tile}px),
        radial-gradient(ellipse at 50% 42%, #17201a 0%, #121a16 38%, #0d1210 70%, #0a0d0f 100%)` }} />
      {/* scattered grass tufts — deterministic, cheap */}
      {Array.from({ length: 34 }, (_, i) => {
        const gx = ((i * 73) % (cols - 2) + 1) * tile + (i % 3) * 4;
        const gy = ((i * 41) % (rows - 2) + 1) * tile + (i % 4) * 3;
        return <div key={i} style={{ position: "absolute", left: gx, top: gy,
          width: 5, height: 3, borderRadius: "50% 50% 0 0",
          background: i % 2 ? "#24352a" : "#1d2c22", opacity: 0.8 }} />;
      })}
      {/* dirt paths — vertical spines from each portal + horizontal band */}
      {portalsArr.map(p => (
        <div key={p.id} style={{ position: "absolute",
          left: p.x * tile - tile * 0.4, top: (p.y + 2) * tile,
          width: tile * 1.8, height: squareY - (p.y + 2) * tile + stoneR * 0.4,
          background: "linear-gradient(90deg, transparent, #2a231a 18%, #332b1f 50%, #2a231a 82%, transparent)",
          opacity: 0.85 }} />
      ))}
      <div style={{ position: "absolute", left: tile * 1.5, right: tile * 1.5, top: squareY - tile * 0.5,
        height: tile * 1.4,
        background: "linear-gradient(0deg, transparent, #2a231a 20%, #332b1f 50%, #2a231a 80%, transparent)",
        opacity: 0.8 }} />
      {/* the village square — worn cobblestone circle around the Bonfire */}
      <div style={{ position: "absolute", left: midX - stoneR, top: squareY + tile * 0.6 - stoneR * 0.62,
        width: stoneR * 2, height: stoneR * 1.24, borderRadius: "50%",
        background: "radial-gradient(ellipse at 50% 45%, #3a3630 0%, #322e28 45%, #282520 78%, transparent 100%)",
        boxShadow: "inset 0 0 24px rgba(0,0,0,0.5)" }} />
      {/* cobble specks on the square */}
      {Array.from({ length: 16 }, (_, i) => {
        const a = (i / 16) * Math.PI * 2;
        const rr = stoneR * (0.3 + ((i * 37) % 50) / 100);
        return <div key={`c${i}`} style={{ position: "absolute",
          left: midX + Math.cos(a) * rr - 3,
          top: squareY + tile * 0.6 + Math.sin(a) * rr * 0.55 - 2,
          width: 7, height: 4, borderRadius: 2, background: "#403b33", opacity: 0.7 }} />;
      })}
      {/* perimeter vignette so the walls read as the world's edge */}
      <div style={{ position: "absolute", inset: 0,
        boxShadow: "inset 0 0 90px 30px rgba(0,0,0,0.55)" }} />
    </div>
  );
}

// ── Building — a generated structure per walk-in destination ──
// One component, per-id flourishes. Footprint derives from the
// same rx/ry geometry the interaction zone uses, so what you see
// is what you can walk up to.
function BuildingVisual({ shop, tile }) {
  const w = Math.max(2, (shop.rx ?? 2)) * tile * 1.6;
  const h = tile * 2.2;
  const left = shop.x * tile + tile - w / 2;
  const top = shop.y * tile + tile - h;
  const c = shop.color || "#c9a84c";

  // The Bonfire is not a house — it's a stone fire ring.
  if (shop.id === "bonfire") {
    const s = tile * 2.4;
    return (
      <div style={{ position: "absolute", left: shop.x * tile + tile / 2 - s / 2, top: shop.y * tile + tile - s * 0.7,
        width: s, height: s * 0.8, zIndex: 3, pointerEvents: "none" }}>
        <div style={{ position: "absolute", bottom: 0, left: "8%", width: "84%", height: "38%",
          borderRadius: "50%", background: "radial-gradient(ellipse, #3d3830 0%, #2b2822 60%, transparent 100%)" }} />
        {[0,1,2,3,4,5].map(i => (
          <div key={i} style={{ position: "absolute", bottom: `${10 + Math.sin(i) * 6}%`,
            left: `${12 + i * 14}%`, width: tile * 0.32, height: tile * 0.26,
            borderRadius: 4, background: "#4a453c", boxShadow: "inset 0 -2px 3px rgba(0,0,0,0.6)" }} />
        ))}
        <div style={{ position: "absolute", bottom: "22%", left: "50%", transform: "translateX(-50%)",
          width: tile * 0.9, height: tile * 1.1, transformOrigin: "50% 100%",
          background: "radial-gradient(ellipse at 50% 90%, #ffd86b 0%, #f0a33d 35%, #d05a1e 65%, transparent 90%)",
          clipPath: "polygon(50% 0%, 78% 38%, 92% 74%, 50% 100%, 8% 74%, 22% 38%)",
          animation: "ofFlame 0.9s ease-in-out infinite", filter: "blur(0.4px)" }} />
        <div style={{ position: "absolute", bottom: "16%", left: "50%", transform: "translateX(-50%)",
          width: tile * 2.6, height: tile * 1.6, borderRadius: "50%", pointerEvents: "none",
          background: "radial-gradient(ellipse, #e8a33d33 0%, transparent 70%)",
          animation: "ofGlow 1.8s ease-in-out infinite" }} />
        {[0,1,2].map(i => (
          <div key={`e${i}`} style={{ position: "absolute", bottom: "60%", left: `${40 + i * 12}%`,
            width: 3, height: 3, borderRadius: "50%", background: "#ffcc66",
            animation: `ofEmber ${1.6 + i * 0.5}s linear infinite`, animationDelay: `${i * 0.6}s` }} />
        ))}
        <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
          marginTop: 3, whiteSpace: "nowrap", fontSize: 8, letterSpacing: 1.5, fontFamily: GM.font,
          color: "#f0cc60", textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>{shop.label?.toUpperCase()}</div>
      </div>
    );
  }

  // The Chao Garden — crystal flowers + drifting motes, no roof.
  if (shop.id === "garden") {
    return (
      <div style={{ position: "absolute", left, top: top + h * 0.35, width: w, height: h * 0.65,
        zIndex: 3, pointerEvents: "none" }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "40% 40% 30% 30%",
          background: "radial-gradient(ellipse at 50% 70%, #10281f 0%, #0c1e18 70%, transparent 100%)",
          border: `1px solid ${c}33` }} />
        {[0,1,2,3,4].map(i => (
          <div key={i} style={{ position: "absolute", bottom: "22%", left: `${12 + i * 18}%`,
            width: 8, height: 12, background: `linear-gradient(180deg, ${c}, #2a6e58)`,
            clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
            filter: `drop-shadow(0 0 4px ${c}88)`, animation: "ofBob 2.4s ease-in-out infinite",
            animationDelay: `${i * 0.3}s` }} />
        ))}
        {[0,1,2].map(i => (
          <div key={`m${i}`} style={{ position: "absolute", bottom: "40%", left: `${25 + i * 22}%`,
            width: 3, height: 3, borderRadius: "50%", background: c,
            boxShadow: `0 0 5px ${c}`, animation: `ofMote ${2.2 + i * 0.7}s linear infinite`,
            animationDelay: `${i * 0.8}s` }} />
        ))}
        <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
          marginTop: 2, whiteSpace: "nowrap", fontSize: 8, letterSpacing: 1.5, fontFamily: GM.font,
          color: c, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>{shop.label?.toUpperCase()}</div>
      </div>
    );
  }

  // The Bulletin — a board on posts, thick with parchments.
  if (shop.id === "bulletin") {
    return (
      <div style={{ position: "absolute", left, top: top + h * 0.15, width: w * 0.8, height: h * 0.85,
        marginLeft: w * 0.1, zIndex: 3, pointerEvents: "none" }}>
        {[0.12, 0.84].map((p, i) => (
          <div key={i} style={{ position: "absolute", bottom: 0, left: `${p * 100}%`, width: 5,
            height: "55%", background: "#3a2e1e", borderRadius: 2 }} />
        ))}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "62%",
          background: "linear-gradient(145deg, #4a3a24, #3a2d1c)", borderRadius: 4,
          border: `1px solid ${c}55`, boxShadow: "0 3px 8px rgba(0,0,0,0.5)" }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ position: "absolute", top: `${14 + (i % 2) * 38}%`, left: `${8 + i * 23}%`,
              width: "16%", height: "34%", background: i % 2 ? "#d8c9a0" : "#c9b98c",
              transform: `rotate(${(i - 1.5) * 4}deg)`, borderRadius: 1 }} />
          ))}
        </div>
        <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
          marginTop: 2, whiteSpace: "nowrap", fontSize: 8, letterSpacing: 1.5, fontFamily: GM.font,
          color: c, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>{shop.label?.toUpperCase()}</div>
      </div>
    );
  }

  // Standard house — roof, stone body, door, glowing window,
  // per-id flourish (forge chimney embers, marketplace awning, etc.)
  return (
    <div style={{ position: "absolute", left, top, width: w, height: h, zIndex: 3, pointerEvents: "none" }}>
      {/* roof */}
      <div style={{ position: "absolute", top: 0, left: "-4%", width: "108%", height: "42%",
        background: `linear-gradient(180deg, ${c}cc 0%, ${c}77 100%)`,
        clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5)) saturate(0.55) brightness(0.75)" }} />
      {/* body */}
      <div style={{ position: "absolute", top: "40%", left: 0, right: 0, bottom: 0,
        background: "linear-gradient(180deg, #2e2a24 0%, #26221c 100%)",
        border: `1px solid ${c}44`, borderTop: "none", borderRadius: "0 0 4px 4px",
        boxShadow: "inset 0 4px 10px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.45)" }} />
      {/* door */}
      <div style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "20%", height: "38%", background: "linear-gradient(180deg, #1a140c, #120e08)",
        borderRadius: "4px 4px 0 0", border: `1px solid ${c}33`, borderBottom: "none" }}>
        <div style={{ position: "absolute", top: "50%", right: "16%", width: 3, height: 3,
          borderRadius: "50%", background: "#c9a84c" }} />
      </div>
      {/* glowing window */}
      <div style={{ position: "absolute", bottom: "22%", left: "16%", width: "16%", height: "20%",
        background: "radial-gradient(circle, #f0cc6088 0%, #b8862e44 100%)",
        border: "1px solid #00000066", borderRadius: 2, animation: "ofGlow 3s ease-in-out infinite" }} />
      {/* forge: chimney + embers */}
      {shop.id === "forge" && (
        <>
          <div style={{ position: "absolute", top: "-8%", right: "18%", width: "10%", height: "30%",
            background: "#3a342c", borderRadius: 2 }} />
          {[0,1].map(i => (
            <div key={i} style={{ position: "absolute", top: "-12%", right: `${20 + i * 4}%`,
              width: 3, height: 3, borderRadius: "50%", background: "#f0946a",
              animation: `ofEmber ${1.4 + i * 0.6}s linear infinite`, animationDelay: `${i * 0.5}s` }} />
          ))}
        </>
      )}
      {/* marketplace: striped awning */}
      {shop.id === "marketplace" && (
        <div style={{ position: "absolute", top: "36%", left: "-6%", width: "112%", height: "14%",
          background: `repeating-linear-gradient(90deg, ${c}aa 0 10px, #3a2d1caa 10px 20px)`,
          borderRadius: "0 0 6px 6px", filter: "saturate(0.7)" }} />
      )}
      {/* trainer: training dummy beside the hall */}
      {shop.id === "trainer" && (
        <div style={{ position: "absolute", bottom: 0, right: "-14%", width: "12%", height: "42%" }}>
          <div style={{ position: "absolute", bottom: 0, left: "40%", width: 3, height: "60%", background: "#4a3a24" }} />
          <div style={{ position: "absolute", top: 0, left: "8%", width: "84%", height: "46%",
            borderRadius: "50%", background: "#5a4830", border: "1px solid #00000055" }} />
        </div>
      )}
      {/* registrar: hanging scroll sign */}
      {shop.id === "registrar" && (
        <div style={{ position: "absolute", top: "44%", right: "8%", width: "14%", height: "24%",
          background: "linear-gradient(180deg, #d8c9a0, #c0ad82)", borderRadius: 2,
          boxShadow: "0 2px 3px rgba(0,0,0,0.5)" }} />
      )}
      {/* name plaque */}
      <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
        marginTop: 3, whiteSpace: "nowrap", fontSize: 8, letterSpacing: 1.5, fontFamily: GM.font,
        color: c, textShadow: "0 1px 3px rgba(0,0,0,0.9)", filter: "brightness(1.2)" }}>
        {shop.label?.toUpperCase()}
      </div>
    </div>
  );
}

// ── Villager — small robed figure with a name tag ─────────────
const VILLAGER_ROBES = { maerie: "#c9a84c", sigfer: "#8fb8d8", vel: "#e8a33d" };
function VillagerVisual({ npc, tile }) {
  const s = tile * 1.2;
  const robe = VILLAGER_ROBES[npc.id] || "#b8a0e8";
  return (
    <div style={{ position: "absolute", left: npc.x * tile + tile / 2 - s / 2,
      top: npc.y * tile + tile - s, width: s, height: s, zIndex: 6,
      pointerEvents: "none", animation: "ofBob 2.8s ease-in-out infinite" }}>
      <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
        whiteSpace: "nowrap", fontSize: 7, color: GM.vellumDim, fontFamily: GM.font,
        background: "rgba(0,0,0,0.7)", padding: "1px 4px", borderRadius: 3,
        border: `1px solid ${robe}55` }}>{npc.label}</div>
      {/* robe */}
      <div style={{ position: "absolute", bottom: 0, left: "22%", width: "56%", height: "62%",
        background: `linear-gradient(180deg, ${robe}bb, ${robe}66)`,
        clipPath: "polygon(50% 0%, 88% 30%, 100% 100%, 0% 100%, 12% 30%)",
        filter: "saturate(0.65) brightness(0.85)" }} />
      {/* head */}
      <div style={{ position: "absolute", bottom: "54%", left: "50%", transform: "translateX(-50%)",
        width: "34%", height: "34%", borderRadius: "50%",
        background: "linear-gradient(145deg, #d8b890, #b89468)",
        boxShadow: "inset -2px -2px 3px rgba(0,0,0,0.3)" }}>
        <div style={{ position: "absolute", top: "42%", left: "24%", width: 2, height: 2, borderRadius: "50%", background: "#241a10" }} />
        <div style={{ position: "absolute", top: "42%", right: "24%", width: 2, height: 2, borderRadius: "50%", background: "#241a10" }} />
      </div>
      {/* shadow */}
      <div style={{ position: "absolute", bottom: -2, left: "18%", width: "64%", height: 5,
        borderRadius: "50%", background: "rgba(0,0,0,0.45)", filter: "blur(1px)" }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PLAYER ENTITY — gendered directional sprites + walk animation
// ─────────────────────────────────────────────────────────────
// getPlayerSprite, getIdleSprite imported from engine

function PlayerEntity({ player, isLocal, colorIndex, companionId, equipped, tile, localSpriteRef, localCompanionRef, compDir, radiantTier, beingId, beingTier }) {
  const TILE = tile || TILE_DEFAULT;
  const color = PLAYER_COLORS[colorIndex % 4];
  const cx = player.x - (player.facingX || 0);
  const cy = player.y - (player.facingY || 0);

  // Position model:
  //   - Remote players: tile position with CSS transition for network smoothing.
  //   - Local player: tile position with NO transition — the rAF loop in Plaza
  //     applies a sub-tile translate3d via localSpriteRef every frame for smooth
  //     fractional movement. Doing both at once causes flicker (CSS + rAF fight).
  const vx = player.x;
  const vy = player.y;

  const walkFrameRef   = useRef(false);
  const lastPosRef     = useRef({ x: player.x, y: player.y });
  const [walkFrame, setWalkFrame] = useState(false);
  const [isIdle, setIsIdle]       = useState(false);
  const [idleFrame, setIdleFrame] = useState(false); // false=a, true=b
  const idleTimerRef  = useRef(null);
  const idleAnimRef   = useRef(null);

  // Reset idle timer on movement
  useEffect(() => {
    if (player.x !== lastPosRef.current.x || player.y !== lastPosRef.current.y) {
      lastPosRef.current = { x: player.x, y: player.y };
      walkFrameRef.current = !walkFrameRef.current;
      setWalkFrame(walkFrameRef.current);
      // Cancel idle on move
      setIsIdle(false);
      setIdleFrame(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (idleAnimRef.current) clearInterval(idleAnimRef.current);
      // Restart 7s idle countdown
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
        // Whistle/pipe animation — toggle a↔b every 600ms
        idleAnimRef.current = setInterval(() => setIdleFrame(f => !f), 600);
      }, 7000);
    }
  }, [player.x, player.y]);

  // Start initial idle countdown on mount
  useEffect(() => {
    idleTimerRef.current = setTimeout(() => {
      setIsIdle(true);
      idleAnimRef.current = setInterval(() => setIdleFrame(f => !f), 600);
    }, 7000);
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (idleAnimRef.current) clearInterval(idleAnimRef.current);
    };
  }, []);

  const playerSprite = isIdle
    ? getIdleSprite(player.gender, idleFrame)
    : getPlayerSprite(player.gender, player.facingX ?? 0, player.facingY ?? 1, walkFrame);

  const playerSize   = Math.floor(TILE * 1.5);
  const companionSize = Math.floor(TILE * 1.5);
  const gap = TILE * 1.5;

  const fx = player.facingX || 0;
  const fy = player.facingY || 1;
  // Companion offset: where the companion sits relative to the player.
  // Defaults to "opposite facing" (trails behind), but the LOCAL player
  // passes an explicit compDir that only updates on an actual step — so a
  // pure turn (turn-then-step) leaves the companion in place, letting you
  // pivot to FACE your companion to pet it.
  const cdx = (compDir && typeof compDir.x === "number") ? compDir.x : -fx;
  const cdy = (compDir && typeof compDir.y === "number") ? compDir.y : -fy;
  const compLeft = vx*TILE + cdx*gap*2 - companionSize/2 + TILE/2;
  const compTop  = vy*TILE + (cdy === 1 ? gap*2 : cdy*gap) - companionSize + TILE;

  // CompanionSprite handles placeholder → real-art fallthrough automatically.
  // `companionId` flows from Firebase presence (profile.equippedCompanion).
  // THE REVERSAL (July 16): a Transcended player (beingId present) IS the
  // beast — so the companion slot shows their Terror character instead
  // (placeholder for the cross-game import, configured later). Players
  // without a being keep the original companion chain untouched.
  const hasCompanion = beingId ? true : !!companionId;

  // Smoothing source:
  //   Remote players: CSS transition smooths network throttling (~80ms cadence).
  //   Local player:   NO transition — rAF transforms the div directly via ref.
  const smoothTransition = isLocal ? "none" : "left 0.15s linear, top 0.15s linear";

  return (
    <>
      {hasCompanion ? (
        <div ref={isLocal ? localCompanionRef : null}
          style={{ position: "absolute",
          left: compLeft, top: compTop,
          width: companionSize, height: companionSize,
          zIndex: 8, transition: smoothTransition,
          willChange: isLocal ? "transform" : undefined,
          display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.9,
          pointerEvents: "none" }}>
          {beingId ? (
            <div style={{ width: companionSize, height: companionSize }}>
              <TerrorTravelerVisual
                gender={player.gender}
                facing={{ x: player.facingX || 0, y: player.facingY || 1 }}
                walkFrame={walkFrame}
                isIdle={isIdle}
              />
            </div>
          ) : (
            <CompanionSprite companionId={companionId} size={companionSize} radiantTier={radiantTier} />
          )}
        </div>
      ) : null}
      <div ref={isLocal ? localSpriteRef : null}
        style={{ position: "absolute",
        left: vx*TILE - playerSize/2 + TILE/2,
        top:  vy*TILE - playerSize + TILE,
        width: playerSize, height: playerSize,
        zIndex: 10, transition: smoothTransition,
        willChange: isLocal ? "transform" : undefined,
        display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
          whiteSpace: "nowrap", fontSize: 8, color: isLocal ? GM.inkGoldBright : GM.vellumDim,
          background: "rgba(0,0,0,0.7)", padding: "1px 5px", borderRadius: 3,
          border: `1px solid ${isLocal ? GM.inkGold+"66" : GM.pageBorder}`, fontFamily: GM.font }}>
          {player.name}
          {(() => {
            const title = getEquippedTitle(equipped);
            return title ? (
              <span style={{ color: GM.inkGoldDim, fontStyle: "italic", marginLeft: 4 }}>
                · {title}
              </span>
            ) : null;
          })()}
        </div>
        <div style={{ position: "relative", width: playerSize, height: playerSize }}>
          {beingId ? (
            <BeingSprite
              beingId={beingId}
              facing={{ x: player.facingX || 0, y: player.facingY || 1 }}
              walkFrame={walkFrame}
              isIdle={isIdle}
              tier={beingTier || null}
            />
          ) : (
            <Sprite key={playerSprite} src={playerSprite} fallbackColor={isLocal ? color : color+"aa"} size={playerSize}
              style={{ borderRadius: 4 }} />
          )}
          {/* Cosmetic overlay — no-op when equipped is null */}
          <CosmeticOverlay equipped={equipped} size={playerSize} gender={player.gender} facing={{ x: player.facingX || 0, y: player.facingY || 1 }} />
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// PROXIMITY BUBBLE
// ─────────────────────────────────────────────────────────────
// RETIRED (July 8) — prompts flow through RoomConsole now. Kept for reference.
// eslint-disable-next-line no-unused-vars
function ProximityBubble({ color, tileX, tileY, tile, onActivate, onDismiss, verb = "Talk" }) {
  const TILE = tile || TILE_DEFAULT;
  const readyRef = useRef(false);
  const _portrait = isPortrait();

  useEffect(() => {
    const t = setTimeout(() => { readyRef.current = true; }, 400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!readyRef.current) return;
      if (e.key === "q" || e.key === "Q" || e.key === "Enter") {
        e.preventDefault(); onActivate();
      }
      if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault(); onDismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onActivate, onDismiss]);

  const left = tileX * TILE + TILE / 2;
  const top  = tileY * TILE - 18;

  const bubble = (
    <div
      onClick={onActivate}
      onContextMenu={e => { e.preventDefault(); onActivate(); }}
      style={{
        position: _portrait ? "relative" : "absolute",
        left: _portrait ? undefined : left,
        top: _portrait ? undefined : top,
        transform: _portrait ? "none" : "translateX(-50%)",
        zIndex: 160, cursor: "pointer", userSelect: "none",
        animation: _portrait
          ? "bubbleFloatDock 1.8s ease-in-out infinite alternate"
          : "bubbleFloat 1.8s ease-in-out infinite alternate",
      }}>
      <div style={{
        backgroundImage: `url(${UI.interact})`,
        backgroundSize: "100% 100%",
        borderRadius: 4,
        padding: _portrait ? "22px 44px 18px" : "18px 32px 14px",
        textAlign: "center",
        minWidth: _portrait ? 150 : 110,
      }}>
        <div style={{
          fontSize: _portrait ? 16 : 13, fontWeight: 900, color: "#ffffff",
          fontFamily: GM.font, letterSpacing: 2,
          textShadow: `0 0 8px ${color}, 0 1px 4px rgba(0,0,0,1)`,
        }}>
          ◆ {verb}
        </div>
      </div>
      <style>{`
        @keyframes bubbleFloat {
          from { transform: translateX(-50%) translateY(0px); }
          to   { transform: translateX(-50%) translateY(-5px); }
        }
        @keyframes bubbleFloatDock {
          from { transform: translateY(0px); }
          to   { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );

  // Portrait → land in the reserved dock; desktop → float over the world.
  if (_portrait) return <DockPortal align="top">{bubble}</DockPortal>;
  return bubble;
}

// ─────────────────────────────────────────────────────────────
// INTERACT MODAL
// ─────────────────────────────────────────────────────────────
function InteractModal({ label, color, onEnter, onClose }) {
  const [highlighted, setHighlighted] = useState(1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        setHighlighted(h => h === 0 ? 1 : 0);
      }
      if (e.key === "Enter" || e.key === "q" || e.key === "Q") {
        e.preventDefault();
        if (highlighted === 1) onEnter(); else onClose();
      }
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [highlighted, onEnter, onClose]);

  return (
    <div style={{ position: "fixed", bottom: 60, left: "50%", transform: "translateX(-50%)",
      zIndex: 150, backgroundImage: `url(${UI.interact})`, backgroundSize: "100% 100%",
      borderRadius: 4, padding: "28px 40px 22px", textAlign: "center" }}>
      <div style={{ fontSize: 12, color: GM.vellum, fontFamily: GM.font, marginBottom: 10,
        fontWeight: 700, textShadow: `0 0 8px ${color}, 0 1px 4px rgba(0,0,0,1)` }}>{label}</div>
      <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
        {[{ label: "Ignore", action: onClose }, { label: "Enter ◆", action: onEnter }].map((opt, i) => (
          <button key={opt.label} onClick={opt.action} onMouseEnter={() => setHighlighted(i)}
            style={{ padding: "7px 4px", borderRadius: 6, cursor: "pointer",
              border: "none", background: "transparent",
              color: highlighted === i ? (i === 1 ? color : GM.vellum) : GM.vellumDim,
              fontSize: 12, fontWeight: highlighted === i ? 900 : 600,
              fontFamily: GM.font, transition: "all 0.12s",
              letterSpacing: highlighted === i ? 1 : 0,
              textShadow: "0 1px 4px rgba(0,0,0,1)" }}>
            {highlighted === i ? `◆ ${opt.label} ◆` : opt.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 9, color: GM.vellumDim, marginTop: 6, letterSpacing: 2,
        textShadow: "0 1px 3px rgba(0,0,0,1)" }}>Q / ENTER · BACKSPACE</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PORTAL BUBBLE
// ─────────────────────────────────────────────────────────────
// RETIRED (July 8) — prompts flow through RoomConsole now. Kept for reference.
// eslint-disable-next-line no-unused-vars
function PortalBubble({ portal, tile, onActivate, onDismiss }) {
  const TILE = tile || TILE_DEFAULT;
  const readyRef = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => { readyRef.current = true; }, 400);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (!readyRef.current) return;
      if (e.key === "Enter" || e.key === "q" || e.key === "Q") { e.preventDefault(); onActivate(); }
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onDismiss(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onActivate, onDismiss]);

  const left = portal.x * TILE + TILE / 2;
  const top  = portal.y * TILE - 18;
  const _portrait = isPortrait();

  const bubble = (
    <div onClick={onActivate} onContextMenu={e => { e.preventDefault(); onActivate(); }}
      style={{ position: _portrait ? "relative" : "absolute",
        left: _portrait ? undefined : left, top: _portrait ? undefined : top,
        transform: _portrait ? "none" : "translateX(-50%)",
        zIndex: 160, cursor: "pointer", userSelect: "none",
        animation: _portrait
          ? "bubbleFloatDock 1.8s ease-in-out infinite alternate"
          : "bubbleFloat 1.8s ease-in-out infinite alternate",
        textAlign: "center" }}>
      <div style={{ backgroundImage: `url(${UI.interact})`, backgroundSize: "100% 100%",
        borderRadius: 4, padding: _portrait ? "22px 44px 18px" : "18px 32px 14px",
        minWidth: _portrait ? 180 : 140 }}>
        <div style={{ fontSize: _portrait ? 11 : 9, color: portal.color, letterSpacing: 3,
          fontWeight: 900, marginBottom: 2, fontFamily: GM.font,
          textShadow: `0 0 8px ${portal.color}, 0 1px 3px rgba(0,0,0,1)` }}>
          {portal.subtitle?.toUpperCase()}
        </div>
        <div style={{ fontSize: _portrait ? 16 : 13, fontWeight: 900, color: "#ffffff",
          fontFamily: GM.font, letterSpacing: 1,
          textShadow: `0 0 8px ${portal.color}, 0 1px 4px rgba(0,0,0,1)` }}>
          ◆ Enter
        </div>
      </div>
      <style>{`
        @keyframes bubbleFloatDock {
          from { transform: translateY(0px); }
          to   { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );

  if (_portrait) return <DockPortal align="top">{bubble}</DockPortal>;
  return bubble;
}

// ─────────────────────────────────────────────────────────────
// PORTAL READY-UP OVERLAY
// ─────────────────────────────────────────────────────────────
function PortalReadyUp({ portal, players, localPlayerName, localUid, onReady, onCancel, readySet }) {
  const allPlayers = [{ name: localPlayerName }, ...Object.values(players).map(p => ({ name: p.name }))];
  const myReady = readySet[localUid || localPlayerName];
  const allReady = myReady && Object.keys(players).every(sid => readySet[sid]);
  const dungeon = DUNGEONS.find(d => d.id === portal.dungeonId);
  const isLocked = portal.id === "jynxi" ? true : (dungeon && !dungeon.unlocked);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter")     { e.preventDefault(); if (!isLocked && !myReady) onReady(); }
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onCancel(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isLocked, myReady]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(5,3,0,0.82)",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundImage: `url(${UI.boss})`, backgroundSize: "100% 100%",
        borderRadius: 8, padding: "36px 40px 28px", width: "min(92vw,480px)", textAlign: "center", minHeight: 280,
        boxSizing: "border-box" }}>
        <div style={{ fontSize: 10, color: GM.vellum, letterSpacing: 4, marginBottom: 6,
          fontWeight: 700, textShadow: `0 0 8px ${portal.color}, 0 1px 4px rgba(0,0,0,1)` }}>◆ THE VILLAGE GATE</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: GM.vellum, fontFamily: GM.font, marginBottom: 4,
          textShadow: "0 1px 6px rgba(0,0,0,1)" }}>
          {portal.label}
        </div>
        <div style={{ fontSize: 11, color: GM.vellumDim, fontStyle: "italic", marginBottom: 20,
          textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{portal.subtitle}</div>

        {isLocked ? (
          <div style={{ color: GM.vellumDim, fontSize: 13, fontStyle: "italic", marginBottom: 20,
            textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
            ◈ This entrance is sealed.<br />
            <span style={{ fontSize: 11, color: GM.vellumFaint }}>Clear Emperium I to unlock.</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
            {allPlayers.map((p, i) => (
              <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: PLAYER_COLORS[i%4], flexShrink: 0 }} />
                <span style={{ flex: 1, color: GM.vellum, fontSize: 13, textAlign: "left",
                  textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{p.name}</span>
                <span style={{ fontSize: 12, color: readySet[p.name] ? "#22c55e" : GM.vellumDim,
                  textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                  {readySet[p.name] ? "✓ READY" : "waiting..."}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 24, justifyContent: "center" }}>
          <button onClick={onCancel} style={{ padding: "8px 0", border: "none", background: "transparent",
            color: GM.vellumDim, cursor: "pointer", fontSize: 12, fontFamily: GM.font,
            textShadow: "0 1px 3px rgba(0,0,0,1)" }}>Cancel</button>
          {!isLocked && (
            <button onClick={onReady} style={{ padding: "8px 0", border: "none", background: "transparent",
              color: myReady ? "#22c55e" : GM.vellum, cursor: "pointer",
              fontSize: 13, fontWeight: 900, fontFamily: GM.font, transition: "all 0.2s",
              textShadow: myReady ? "0 0 8px #22c55e, 0 1px 3px rgba(0,0,0,1)" : `0 0 8px ${portal.color}, 0 1px 3px rgba(0,0,0,1)` }}>
              {myReady ? "✓ Ready!" : "Ready Up"}
            </button>
          )}
        </div>
        {allReady && !isLocked && (
          <div style={{ marginTop: 14, color: "#22c55e", fontSize: 12, fontStyle: "italic",
            textShadow: "0 0 8px #22c55e, 0 1px 3px rgba(0,0,0,1)" }}>
            All ready — the Old Fold opens its gate...
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LOGIN OVERLAY
// ─────────────────────────────────────────────────────────────
function LoginOverlay({ onLogin }) {
  const [authMode, setAuthMode]               = useState("login");
  const [email, setEmail]                     = useState("");
  const [displayName, setDisplayName]         = useState("");
  const [password, setPassword]               = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [authError, setAuthError]             = useState("");
  const [loading, setLoading]                 = useState(false);
  const [rememberMe, setRememberMe]           = useState(true);

  const inputStyle = {
    width: "100%", padding: "13px 16px",
    background: `${GM.inkGold}08`, border: `1px solid ${GM.inkBrown}`,
    borderRadius: 2, color: GM.vellum, fontFamily: GM.font,
    fontSize: 15, outline: "none", boxSizing: "border-box", caretColor: GM.inkGold,
  };

  const handleLogin = async () => {
    setAuthError(""); setLoading(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      const uid = cred.user.uid;
      const name = cred.user.displayName || email.split("@")[0];
      let profile = await getProfileByUid(uid);
      if (!profile) profile = await createProfile(uid, name);
      onLogin(uid, name, profile);
    } catch (e) {
      setAuthError(e.code === "auth/invalid-credential" ? "Incorrect email or password."
        : e.code === "auth/user-not-found" ? "No account found. Register below."
        : e.code === "auth/too-many-requests" ? "Too many attempts. Try again later."
        : "Login failed. Check your details.");
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    setAuthError("");
    if (!displayName.trim()) { setAuthError("Enter your Aenarchist name."); return; }
    if (displayName.trim().length < 3) { setAuthError("Name must be at least 3 characters."); return; }
    if (!email.trim()) { setAuthError("Enter your email."); return; }
    if (!password) { setAuthError("Enter a password."); return; }
    if (password.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    if (password !== passwordConfirm) { setAuthError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await updateProfile(cred.user, { displayName: displayName.trim() });
      const uid = cred.user.uid;
      const profile = await createProfile(uid, displayName.trim());
      onLogin(uid, displayName.trim(), profile);
    } catch (e) {
      setAuthError(e.code === "auth/email-already-in-use" ? "Email already registered. Log in instead."
        : e.code === "auth/invalid-email" ? "Enter a valid email address."
        : e.code === "auth/weak-password" ? "Password must be at least 6 characters."
        : "Registration failed. Try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(5,3,0,0.96)",
      display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}>
      <div style={{ width: "min(92vw, 420px)" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 6, marginBottom: 8, fontFamily: GM.font }}>◆ THE OLD FOLD ◆</div>
          <div style={{ fontSize: 28, fontWeight: 900, fontStyle: "italic",
            background: `linear-gradient(135deg, ${GM.inkGoldBright}, ${GM.inkGold})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            fontFamily: GM.font, letterSpacing: 2 }}>Identify Yourself</div>
          <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 8, fontStyle: "italic" }}>
            "The Emperium remembers all who enter."
          </div>
        </div>

        <div style={{ background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`,
          border: `1px solid ${GM.inkBrown}`, borderRadius: 4, padding: "28px 24px",
          boxShadow: "0 0 40px rgba(0,0,0,0.8)" }}>

          <div style={{ display: "flex", marginBottom: 24, background: GM.page, borderRadius: 4, padding: 3, gap: 3 }}>
            {[["login","Returning"],["register","New Aenarchist"]].map(([mode, label]) => (
              <button key={mode} onClick={() => { setAuthMode(mode); setAuthError(""); }}
                style={{ flex: 1, padding: "10px", borderRadius: 2, border: "none", cursor: "pointer",
                  fontFamily: GM.font, fontSize: 11, fontWeight: 900, letterSpacing: 1, transition: "all 0.2s",
                  background: authMode === mode ? `linear-gradient(135deg, ${GM.inkGold}22, ${GM.pageMid})` : "transparent",
                  color: authMode === mode ? GM.inkGoldBright : GM.vellumFaint,
                  boxShadow: "none" }}>
                {authMode === mode ? `◆ ${label} ◆` : label}
              </button>
            ))}
          </div>

          {authMode === "register" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: GM.inkBrown, letterSpacing: 3, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>AENARCHIST NAME</div>
              <input placeholder="Your name in the Plaza..." value={displayName}
                onChange={e => { setDisplayName(e.target.value); setAuthError(""); }}
                style={inputStyle} autoFocus />
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: GM.inkBrown, letterSpacing: 3, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>EMAIL</div>
            <input placeholder="your@email.com" value={email} type="email"
              onChange={e => { setEmail(e.target.value); setAuthError(""); }}
              onKeyDown={e => e.key === "Enter" && (authMode === "login" ? handleLogin() : handleRegister())}
              style={inputStyle} autoFocus={authMode === "login"} />
          </div>

          <div style={{ marginBottom: authMode === "register" ? 14 : 20 }}>
            <div style={{ fontSize: 9, color: GM.inkBrown, letterSpacing: 3, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>PASSWORD</div>
            <input type="password" placeholder={authMode === "register" ? "Min 6 characters..." : "Your password..."}
              value={password}
              onChange={e => { setPassword(e.target.value); setAuthError(""); }}
              onKeyDown={e => e.key === "Enter" && (authMode === "login" ? handleLogin() : handleRegister())}
              style={inputStyle} />
          </div>

          {authMode === "register" && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 9, color: GM.inkBrown, letterSpacing: 3, marginBottom: 6, fontWeight: 900, fontFamily: GM.font }}>CONFIRM PASSWORD</div>
              <input type="password" placeholder="Confirm..." value={passwordConfirm}
                onChange={e => { setPasswordConfirm(e.target.value); setAuthError(""); }}
                style={inputStyle} />
            </div>
          )}

          {authError && (
            <div style={{ fontSize: 12, color: SD.red, textAlign: "center", marginBottom: 12,
              padding: "8px 12px", background: `${GM.inkRed}18`, border: `1px solid ${GM.inkRed}`, borderRadius: 2 }}>
              {authError}
            </div>
          )}

          <div onClick={() => setRememberMe(r => !r)}
            style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer" }}>
            <div style={{ width: 18, height: 18, borderRadius: 2, flexShrink: 0,
              border: `2px solid ${rememberMe ? GM.inkGold : GM.pageBorder}`,
              background: rememberMe ? `${GM.inkGold}33` : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: GM.inkGoldBright, transition: "all 0.15s" }}>
              {rememberMe ? "✓" : ""}
            </div>
            <span style={{ fontSize: 11, color: rememberMe ? GM.vellumDim : GM.vellumFaint, fontFamily: GM.font }}>
              Remember me on this device
            </span>
          </div>

          <button onClick={authMode === "login" ? handleLogin : handleRegister}
            disabled={loading || !email.trim() || !password}
            style={{ width: "100%", padding: "16px",
              background: `linear-gradient(135deg, ${GM.inkGold}22, ${GM.pageMid})`,
              border: `1px solid ${GM.inkGold}`, borderRadius: 2, color: GM.inkGoldBright,
              fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic",
              cursor: "pointer", boxShadow: `0 0 16px ${GM.inkGold}33`, transition: "all 0.2s",
              opacity: (loading || !email.trim() || !password) ? 0.35 : 1 }}>
            {loading ? "◆ Verifying..." : authMode === "login" ? "◆ Enter the Plaza" : "◆ Forge Your Identity"}
          </button>

          <div style={{ fontSize: 10, color: GM.vellumFaint, textAlign: "center", marginTop: 12, fontStyle: "italic", fontFamily: GM.font }}>
            Forgot your password? Use Firebase's reset link — coming soon.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STARTER DECK PICKER — gender step first
// ─────────────────────────────────────────────────────────────
function StarterPickerOverlay({ playerName, uid, onPick }) {
  const [step, setStep]         = useState("gender");
  const [gender, setGender]     = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);

  const handlePick = async () => {
    if (!selected || !gender) return;
    setLoading(true);
    const { update: fbUpdate, ref: fbRef } = await import("firebase/database");
    await fbUpdate(fbRef(db, `profiles/${uid}`), { starterDeckId: selected, gender });
    const { addCardToCollection } = await import("./progression");
    const deckData = getStarterDeck(selected);
    if (deckData) {
      for (const cardId of Object.keys(deckData.grimoire || {}))
        await addCardToCollection(uid, cardId, false, null, "starter");
      for (const curioId of (deckData.curios || []))
        if (curioId) await addCardToCollection(uid, curioId, false, null, "starter");
    }
    setLoading(false);
    onPick(selected, gender);
  };

  const GENDER_OPTS = [
    { id: "m", label: "Male",   icon: "♂", color: GM.inkGoldBright },
    { id: "f", label: "Female", icon: "♀", color: GM.inkGold },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(5,3,0,0.96)",
      display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}>
      <div style={{ width: "min(92vw, 480px)", maxHeight: "90vh", overflowY: "auto" }}>

        {step === "gender" && (<>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 6, marginBottom: 8 }}>◆ WELCOME, {playerName.toUpperCase()} ◆</div>
            <div style={{ fontSize: 24, fontWeight: 900, fontStyle: "italic", color: GM.inkGold, fontFamily: GM.font }}>
              Who Are You?
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 8, fontStyle: "italic" }}>
              Choose your form. You can change this later.
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            {GENDER_OPTS.map(opt => (
              <div key={opt.id} onClick={() => setGender(opt.id)} style={{
                flex: 1, padding: "28px 16px", textAlign: "center", cursor: "pointer",
                background: gender === opt.id ? `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageWarm})` : `rgba(124,58,237,0.06)`,
                border: `1px solid ${gender === opt.id ? opt.color : GM.pageMid}`,
                borderRadius: 12, transition: "all 0.2s",
                boxShadow: gender === opt.id ? shimmerBorder(opt.color) : "none" }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>{opt.icon}</div>
                <div style={{ fontSize: 16, fontWeight: 900,
                  color: gender === opt.id ? opt.color : GM.vellumDim,
                  fontFamily: GM.font }}>{opt.label}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setStep("deck")} disabled={!gender}
            style={{ width: "100%", padding: "16px",
              background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`,
              border: `1px solid ${GM.inkGold}`, borderRadius: 2, color: GM.inkGoldBright,
              fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic",
              cursor: gender ? "pointer" : "not-allowed", opacity: gender ? 1 : 0.35,
              boxShadow: `0 0 14px ${GM.inkGold}44` }}>
            ◆ Continue
          </button>
        </>)}

        {step === "deck" && (<>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 6, marginBottom: 8 }}>◆ WELCOME ◆</div>
            <div style={{ fontSize: 24, fontWeight: 900, fontStyle: "italic", color: GM.inkGold, fontFamily: GM.font }}>
              Choose Your Starter Deck
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 8, fontStyle: "italic" }}>
              This is your foundation. Choose wisely.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {STARTER_DECKS.map(deck => (
              <div key={deck.id} onClick={() => setSelected(deck.id)} style={{
                background: selected === deck.id ? `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageWarm})` : `rgba(124,58,237,0.06)`,
                border: `1px solid ${selected === deck.id ? GM.inkGold : GM.inkBrown}55`,
                borderRadius: 12, padding: "14px 18px", cursor: "pointer", transition: "all 0.2s",
                boxShadow: selected === deck.id ? `0 0 14px ${GM.inkGold}44` : "none" }}>
                <div style={{ fontSize: 14, fontWeight: 900, fontStyle: "italic",
                  color: selected === deck.id ? GM.inkGoldBright : GM.vellumDim, fontFamily: GM.font }}>
                  {deck.name}
                </div>
                {deck.description && <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 4 }}>{deck.description}</div>}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep("gender")}
              style={{ padding: "16px 20px", background: "transparent",
                border: `1px solid ${GM.inkBrown}66`, borderRadius: 10, color: GM.vellumFaint,
                fontFamily: GM.font, fontSize: 13, cursor: "pointer" }}>← Back</button>
            <button onClick={handlePick} disabled={!selected || loading}
              style={{ flex: 1, padding: "16px",
                background: `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageMid})`,
                border: `1px solid ${GM.inkGold}`, borderRadius: 2, color: GM.inkGoldBright,
                fontFamily: GM.font, fontSize: 14, fontWeight: 900, fontStyle: "italic",
                cursor: "pointer", opacity: (!selected || loading) ? 0.35 : 1,
                boxShadow: `0 0 14px ${GM.inkGold}44` }}>
              {loading ? "◆ Preparing..." : "◆ Enter the Plaza"}
            </button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GENDER SETTINGS
// ─────────────────────────────────────────────────────────────
function GenderSettings({ uid, currentGender, onClose, onChanged }) {
  const [saving, setSaving] = useState(false);

  const pick = async (g) => {
    if (g === currentGender || saving) return;
    setSaving(true);
    const { update: fbUpdate, ref: fbRef } = await import("firebase/database");
    await fbUpdate(fbRef(db, `profiles/${uid}`), { gender: g });
    setSaving(false);
    onChanged(g);
    onClose();
  };

  const OPTS = [
    { id: "m", label: "Male",   icon: "♂", color: GM.inkGoldBright },
    { id: "f", label: "Female", icon: "♀", color: GM.inkGold },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 250, background: "rgba(5,3,0,0.90)",
      display: "flex", alignItems: "center", justifyContent: "center", fontFamily: GM.font }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(88vw, 340px)",
        background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`,
        border: `1px solid ${GM.inkGold}44`, borderRadius: 16, padding: "28px 24px",
        boxShadow: `0 0 14px ${GM.inkGold}33` }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 4, marginBottom: 6 }}>◆ APPEARANCE ◆</div>
          <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: GM.vellum }}>Change Form</div>
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          {OPTS.map(opt => (
            <div key={opt.id} onClick={() => pick(opt.id)} style={{
              flex: 1, padding: "20px 12px", textAlign: "center",
              cursor: saving ? "not-allowed" : "pointer",
              background: currentGender === opt.id ? `linear-gradient(135deg, ${GM.inkGold}18, ${GM.pageWarm})` : `rgba(124,58,237,0.06)`,
              border: `1px solid ${currentGender === opt.id ? opt.color : GM.pageMid}`,
              borderRadius: 12, transition: "all 0.2s",
              boxShadow: currentGender === opt.id ? shimmerBorder(opt.color) : "none",
              opacity: saving ? 0.5 : 1 }}>
              <div style={{ fontSize: 30, marginBottom: 6 }}>{opt.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 900,
                color: currentGender === opt.id ? opt.color : GM.vellumDim }}>
                {opt.label}
              </div>
              {currentGender === opt.id && (
                <div style={{ fontSize: 9, color: opt.color, marginTop: 4, letterSpacing: 1 }}>◆ ACTIVE</div>
              )}
            </div>
          ))}
        </div>
        <button onClick={onClose} style={{ width: "100%", padding: "10px", background: "transparent",
          border: `1px solid ${GM.inkBrown}66`, borderRadius: 8, color: GM.vellumFaint,
          cursor: "pointer", fontSize: 12, fontFamily: GM.font }}>Cancel</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREST BADGE
// ─────────────────────────────────────────────────────────────
function CrestBadge({ amount }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6,
      background: OBSIDIAN_RECESS,
      border: `1px solid ${GM.inkGoldBright}88`, borderRadius: 20, padding: "4px 13px",
      boxShadow: `inset 0 2px 7px rgba(0,0,0,0.7), 0 0 12px ${GM.inkGold}30` }}>
      <span style={{ fontSize: 13, color: GM.inkGoldBright, textShadow: `0 0 7px ${GM.inkGoldBright}` }}>◆</span>
      <span style={{ fontSize: 14, fontWeight: 800, color: "#ffe9a8", fontFamily: GM.fontUI, textShadow: TEXT_SET, letterSpacing: 0.4 }}>
        {amount?.toLocaleString() || 0}
      </span>
      <span style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2, fontFamily: GM.fontUI, fontWeight: 600 }}>CRESTS</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// WILLY DIALOGUE ENGINE
// ─────────────────────────────────────────────────────────────
// getWillyDialogue — returns { lines: [...], sweetTreat?: boolean, grantLineIdx?: number }
// - sweetTreat: true means the sweet-treat grant flow runs alongside this dialogue.
// - grantLineIdx: the line index AFTER which the +5 ◆ pour animation fires
//   and the atomic write happens. Defaults to undefined for non-grant branches.
function getWillyDialogue(profile) {
  if (!profile) return { lines: ["...","You shouldn't be here without a profile.","The Plaza doesn't accept ghosts."] };

  const wins       = profile.totalWins || 0;
  const stats      = profile.stats || {};
  const ilkWins    = stats.ilkWins || {};
  const streak     = stats.winStreak || 0;
  const collection = profile.collection || {};
  const collectionSize = Object.keys(collection).length;
  const defeated   = profile.storyProgress?.defeatedOpponents || [];
  const dungeons   = profile.storyProgress?.completedDungeons || [];
  const beatLord   = (stats.difficultyWins?.Lord || 0) > 0;
  const ownsDragon = collection["chaos_dragon_lord"] || collection["chaos_dragon_champion"] || collection["chaos_dragon_beb"];
  const losses     = (stats.totalGames || 0) - wins;

  // ── FIRST-VISIT SWEET TREAT ────────────────────────────────
  // Top of the cascade. Fires for any player who finished onboarding
  // but has not yet collected their five Crests at the front desk.
  // The grant fires at grantLineIdx (line "Sweet treat. As promised. Hold out your hand.").
  if (profile.hasCompletedOnboarding && !profile.hasReceivedSweetTreat) {
    return {
      sweetTreat: true,
      grantLineIdx: 2, // 0:"Ah!...", 1:"You came...", 2:"Sweet treat..." → grant after this line
      lines: [
        "Ah! There you are. There you are there you are.",
        "You came. Like I said. Good good.",
        "Sweet treat. As promised. Hold out your hand.",
        "There. Five Crests. Sweet treat. In this world.",
        "Mmm. Sweet.",
        "...And the real tutorial. The Solrend, proper. Bones and all.",
        "Want to learn now? Or come back later — I'll be right here.",
      ],
    };
  }

  // ── EASTER EGG BRANCHES ────────────────────────────────────
  // Tucked above the standard cascade. Fire on quirky thresholds the
  // win-count branches don't catch. Each is a small reward for noticing.
  if (collectionSize >= 100) return { lines: ["A hundred cards.","The Void doesn't make jokes.","But if it did. That'd be one."] };
  if (streak === 0 && wins >= 50)  return { lines: ["You lost.","After all that.","...That hurts more than the first one. Doesn't it."] };
  if ((stats.difficultyWins?.Beb || 0) >= 20 && (stats.difficultyWins?.Lord || 0) === 0) return { lines: ["Twenty Bebis. Beat into dust.","You're warm.","Try a Champion. Then a Lord.","Or don't. Bebis can't hurt you back."] };
  if (losses >= 30 && wins < 5)    return { lines: ["...","You keep coming back.","I respect that.","Don't tell anyone I said it."] };
  if (defeated.length === 1 && (stats.totalGames || 0) >= 15) return { lines: ["One opponent. Fifteen tries.","The Shatterfield doesn't grade for effort.","But I do. A little."] };
  if (collectionSize === 1)        return { lines: ["One card.","That's a start.","...That's a small start."] };

  // ── STANDARD CASCADE ───────────────────────────────────────
  if (streak >= 5) return { lines: [`${streak} wins in a row.`,"Don't get comfortable. The Void has a long memory.","...I'm watching you."] };
  if (dungeons.length >= 4) return { lines: ["Four Emperiums. Four.","I've been here a long time.","I have never seen anyone do that.","...Don't tell J'ynxi."] };
  if (dungeons.includes(1) && dungeons.length === 1) return { lines: ["You walked out of the Space Emperium.","I didn't think you would.","The stars noticed."] };
  if ((ilkWins["Space"] || 0) >= 3) return { lines: ["You keep running those star-born things.","H-01... Xulan... the void-walkers.","The Space Ilk is starting to remember your name."] };
  if ((ilkWins["Phantom"] || 0) >= 3) return { lines: ["Shadow beasts.","You really like things that hunt in the dark.","...So do I."] };
  if ((ilkWins["Time"] || 0) >= 3) return { lines: ["The machines. Always the machines.","Z4Z, DG-0... they're tools, you know.","Make sure you're the one using them."] };
  if ((ilkWins["Dragon"] || 0) >= 3) return { lines: ["Dragons.","Every single time.","Fine. But don't come to me when Chaos Dragon decides it doesn't like you anymore."] };
  if (beatLord) return { lines: ["That opponent you dismantled.","They weren't weak.","You're becoming something the Shatterfield hasn't seen in a while."] };
  if (ownsDragon) return { lines: ["I see it in your Grimoire.","The Dragon.","...Be careful with that one.","It has beaten players who thought they controlled it."] };
  if (collectionSize >= 60) return { lines: ["You've been collecting.","That many cards... the Void is paying attention to you.","Good."] };
  if (collectionSize >= 30) return { lines: ["Your collection grows.","Every card remembers who holds it.","Don't forget that."] };
  if (defeated.length >= 5) return { lines: [`${defeated.length} story opponents.`,"They all thought they were ready for you.","They were not."] };
  if (wins >= 20) return { lines: [`${wins} wins.`,"The Plaza has seen many Aenarchists.","Most of them stopped showing up.","You didn't."] };
  if (wins >= 10) return { lines: ["Ten wins.","Some people never get there.","Don't tell them I said that."] };
  if (wins >= 5) return { lines: ["Five wins.","You're starting to understand something.","I'm not sure what yet. Neither are you.","That's fine."] };
  if (wins === 1) return { lines: ["One win.","I heard what happened in the Shatterfield.","Beginner's luck, probably.","...Probably."] };
  if (wins === 0 && stats.totalGames > 0) return { lines: ["You keep losing.","The Shatterfield doesn't care about your feelings.","Neither do I.","Go again."] };
  return { lines: ["Ah.","Another soul finds the Plaza.","Don't touch anything.","...Welcome."] };
}

// ─────────────────────────────────────────────────────────────
// WILLY DIALOGUE MODAL
// ─────────────────────────────────────────────────────────────
function WillyDialogue({ profile, uid, onClose, onTutorial1, onRulebook, showToast, logLine }) {
  // Capture dialogue once on mount so re-renders during the grant flow
  // (which mutate profile.hasReceivedSweetTreat) don't yank the script
  // out from under us mid-conversation.
  const dialogueRef = useRef(null);
  if (dialogueRef.current === null) dialogueRef.current = getWillyDialogue(profile);
  const dialogue = dialogueRef.current;
  const lines = dialogue.lines;
  const isSweetTreat = !!dialogue.sweetTreat;
  const grantLineIdx = dialogue.grantLineIdx;

  const [lineIdx, setLineIdx] = useState(0);
  const [pourPulse, setPourPulse] = useState(false);  // +5 ◆ animation flag
  const grantFiredRef = useRef(false);                // double-fire guard inside session
  const finishedRef  = useRef(false);                 // prevent double-finish

  // Session record — every Willy line lands in the plaza console log
  // as it appears, and stays there for the whole browser session.
  const logLineRef = useRef(logLine);
  useEffect(() => { logLineRef.current = logLine; }, [logLine]);
  useEffect(() => {
    const l = lines[lineIdx];
    if (l && logLineRef.current) logLineRef.current(typeof l === "string" ? l : (l.text || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIdx]);

  // ── Stable refs for callbacks ──────────────────────────────
  // Plaza re-renders constantly (multiplayer presence + position rAF),
  // and onClose/onRulebook are inline arrows that get a fresh ref every
  // Plaza render. Without these refs, the keydown effect tears down and
  // re-attaches every frame, which under React 18 batching can cascade
  // into infinite update warnings during the close → navigate transition.
  const onCloseRef    = useRef(onClose);
  const onRulebookRef = useRef(onRulebook);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { onRulebookRef.current = onRulebook; }, [onRulebook]);

  // ── Finish handler — close + route to the How To Play hub ──
  // Single source of truth for the post-dialogue tutorial picker.
  // Idempotent — guards against double-fire from rapid Enter presses.
  const handleFinish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onCloseRef.current?.();
    // onRulebook is wired to goTo("howtoplay") in App.js — that's the hub.
    onRulebookRef.current?.();
  }, []);

  // ── SWEET TREAT GRANT ──────────────────────────────────────
  // Fires exactly once when we step PAST the grant line.
  // Atomic write happens inside awardSweetTreat; we just trigger it.
  useEffect(() => {
    if (!isSweetTreat) return;
    if (grantFiredRef.current) return;
    if (lineIdx <= grantLineIdx) return;
    if (!uid) return;
    grantFiredRef.current = true;
    setPourPulse(true);
    setTimeout(() => setPourPulse(false), 1800);
    awardSweetTreat(uid).then((res) => {
      if (res && !res.alreadyClaimed) {
        showToast?.("✦ +5 Divine Crests · Sweet Treat", GM.inkGold);
      }
    }).catch((err) => {
      console.error("[Willy] sweet treat grant failed:", err);
    });
    // showToast intentionally excluded — it's a fresh ref every Plaza
    // render and we only need it once at grant time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIdx, isSweetTreat, grantLineIdx, uid]);

  // Keydown — bound once per dialogue lifetime, not once per Plaza render.
  // We read live state via refs so the handler always sees current lineIdx.
  const lineIdxRef    = useRef(lineIdx);
  const linesLenRef   = useRef(lines.length);
  useEffect(() => { lineIdxRef.current  = lineIdx;  }, [lineIdx]);
  useEffect(() => { linesLenRef.current = lines.length; }, [lines.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (lineIdxRef.current < linesLenRef.current - 1) setLineIdx(i => i + 1);
        else handleFinish();
      }
      if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleFinish]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(5,3,0,0.82)",
      display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={() => {
        if (lineIdx < lines.length - 1) setLineIdx(i => i + 1);
        else handleFinish();
      }}>
      <div onClick={e => e.stopPropagation()}
        style={{ position: "relative", background: `linear-gradient(135deg, ${GM.pageMid}44, ${GM.page})`,
          border: `2px solid ${GM.inkGold}55`, borderRadius: 20, padding: "28px 32px",
          width: "min(92vw, 400px)", fontFamily: GM.font,
          boxShadow: `0 0 50px ${GM.inkGold}22` }}>

        {/* +5 ◆ POUR PULSE — sweet treat grant moment */}
        {pourPulse && (
          <div style={{
            position: "absolute", top: "30%", left: "50%",
            transform: "translateX(-50%)",
            fontSize: 36, fontWeight: 900, color: GM.inkGold,
            fontFamily: GM.font, letterSpacing: 2,
            textShadow: `0 0 20px ${GM.inkGold}, 0 0 40px ${GM.inkGold}88`,
            pointerEvents: "none", zIndex: 5,
            animation: "willyCrestPour 1.8s ease-out forwards",
          }}>
            +5 ◆
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%",
            background: `${GM.inkGold}22`, border: `1px solid ${GM.inkGold}44`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, flexShrink: 0 }}>🧹</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 900, color: GM.inkGold, fontFamily: GM.font }}>Willy</div>
            <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2 }}>GROUNDS KEEPER</div>
          </div>
        </div>

        <div style={{ fontSize: 15, color: GM.vellum, lineHeight: 1.9,
          fontStyle: "italic", minHeight: 60, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>
          "{lines[lineIdx]}"
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20 }}>
          <div style={{ display: "flex", gap: 5 }}>
            {lines.map((_, i) => (
              <div key={i} style={{ width: 5, height: 5, borderRadius: "50%",
                background: i === lineIdx ? GM.inkGold : GM.pageBorder, transition: "background 0.2s" }} />
            ))}
          </div>
          <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic" }}>
            {lineIdx < lines.length - 1
              ? "tap to continue ▶"
              : (isSweetTreat ? "tap to learn ▶" : "tap to continue ▶")}
          </div>
        </div>

        <button onClick={onClose}
          style={{ width: "100%", padding: "8px", borderRadius: 8, marginTop: 14,
            border: `1px solid ${GM.inkBrown}66`, background: "transparent",
            color: GM.vellumFaint, fontSize: 12, cursor: "pointer" }}>
          ← Leave
        </button>
      </div>
      <style>{`
        @keyframes willyCrestPour {
          0%   { opacity: 0; transform: translate(-50%, 10px) scale(0.7); }
          20%  { opacity: 1; transform: translate(-50%, 0) scale(1.15); }
          60%  { opacity: 1; transform: translate(-50%, -28px) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -56px) scale(0.95); }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BULLETIN BOARD
// ─────────────────────────────────────────────────────────────
const ILK_META = {
  space:   { label: "Space Emperium",   color: "#6d28d9", icon: "✦" },
  phantom: { label: "Phantom Emperium", color: "#c9a84c", icon: "◈" },
  time:    { label: "Time Emperium",    color: "#22d3ee", icon: "⏳" },
  dragon:  { label: "Dragon Emperium",  color: "#dc2626", icon: "🐉" },
};

const RANK_TITLE = (wins) =>
  wins === 0      ? "Unproven"
  : wins < 5      ? "◈ Escling"
  : wins < 20     ? "◆ Beb"
  : wins < 50     ? "⚔ Champion"
  : wins < 100    ? "👑 Lord"
  : "🌑 Escendant";

function BulletinBoard({ profile, onClose, onCollection }) {
  const [tab, setTab]               = useState("emperium");
  const [unstable, setUnstable]     = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    setLoading(true);
    let done = 0;
    const check = () => { done++; if (done >= 2) setLoading(false); };
    onValue(ref(db, "world/unstableEmperium"), snap => {
      setUnstable(snap.exists() ? snap.val() : null); check();
    }, { onlyOnce: true });
    onValue(ref(db, "leaderboard"), snap => {
      if (!snap.exists()) { setLeaderboard([]); check(); return; }
      const all = [];
      snap.forEach(child => {
        const p = child.val();
        if (p.name && (p.wins || 0) > 0) all.push({ name: p.name, wins: p.wins || 0, uid: child.key });
      });
      all.sort((a, b) => b.wins - a.wins);
      setLeaderboard(all.slice(0, 10));
      check();
    }, { onlyOnce: true });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const ilk = unstable?.ilk ? ILK_META[unstable.ilk] : null;
  const myRank = leaderboard.findIndex(p => p.uid === profile?.uid) + 1;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(5,3,0,0.88)",
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundImage: `url(${UI.bulletin})`, backgroundSize: "100% 100%",
        borderRadius: 8, width: "min(92vw, 500px)", minHeight: 480,
        display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
        <div style={{ padding: "130px 32px 48px", display: "flex", flexDirection: "column", flex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 4, marginBottom: 4, textShadow: "0 1px 4px rgba(0,0,0,1)" }}>◆ THE BULLETIN BOARD ◆</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: GM.vellum, fontFamily: GM.font, textShadow: "0 1px 6px rgba(0,0,0,1)" }}>Plaza Notices</div>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
            {[["emperium","🌑 Unstable Emperium"],["void_record","◆ Void Record"]].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} style={{
                flex: 1, padding: "10px 6px", borderRadius: 8, cursor: "pointer",
                border: `1px solid ${tab === id ? GM.inkGold : GM.inkBrown}55`,
                background: tab === id ? `rgba(0,0,0,0.6)` : "rgba(0,0,0,0.3)",
                color: tab === id ? GM.inkGoldBright : GM.vellumDim,
                fontFamily: GM.font, fontSize: 10, fontWeight: 900,
                textShadow: "0 1px 3px rgba(0,0,0,1)", transition: "all 0.2s" }}>{label}</button>
            ))}
          </div>
          {loading && <div style={{ textAlign: "center", padding: "40px 0", color: GM.vellumFaint, fontSize: 12, fontStyle: "italic", textShadow: "0 1px 3px rgba(0,0,0,1)" }}>◆ Reading the board...</div>}
          {!loading && tab === "emperium" && (
            <div style={{ flex: 1 }}>
              {unstable && ilk ? (
                <>
                  <div style={{ padding: "16px", borderRadius: 12, marginBottom: 14, background: `rgba(0,0,0,0.65)`, border: `1px solid ${ilk.color}66` }}>
                    <div style={{ fontSize: 9, color: ilk.color, letterSpacing: 3, marginBottom: 6, textShadow: `0 0 8px ${ilk.color}, 0 1px 3px rgba(0,0,0,1)` }}>⚠ CURRENTLY UNSTABLE</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: GM.vellum, fontFamily: GM.font, marginBottom: 4, textShadow: "0 1px 6px rgba(0,0,0,1)" }}>{ilk.icon} {ilk.label}</div>
                    {unstable.bonus && <div style={{ fontSize: 12, color: ilk.color, marginBottom: 8, fontWeight: 700, textShadow: `0 0 6px ${ilk.color}, 0 1px 3px rgba(0,0,0,1)` }}>◆ {unstable.bonus}</div>}
                    {unstable.flavor && <div style={{ fontSize: 11, color: GM.vellumDim, fontStyle: "italic", lineHeight: 1.7, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>"{unstable.flavor}"</div>}
                  </div>
                  <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(0,0,0,0.5)", border: `1px solid ${GM.inkBrown}66` }}>
                    <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2, marginBottom: 6, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>WHAT THIS MEANS</div>
                    <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.8, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                      The rift surrounding the {ilk.label} is tearing wider this cycle. Decks built around the {unstable.ilk ? unstable.ilk.charAt(0).toUpperCase() + unstable.ilk.slice(1) : ""} Ilk will feel the difference.{unstable.bonus ? ` ${unstable.bonus}.` : ""}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ padding: "20px", borderRadius: 12, textAlign: "center", background: "rgba(0,0,0,0.5)", border: `1px solid ${GM.inkBrown}66` }}>
                  <div style={{ fontSize: 28, marginBottom: 12 }}>◇</div>
                  <div style={{ fontSize: 13, color: GM.vellumDim, fontStyle: "italic", lineHeight: 1.8, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>The Shatterfield is calm.<br/>No Emperium is currently unstable.</div>
                  <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 10, fontStyle: "italic", textShadow: "0 1px 3px rgba(0,0,0,1)" }}>Check back next cycle.</div>
                </div>
              )}
            </div>
          )}
          {!loading && tab === "void_record" && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 3, marginBottom: 10, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>
                TOP AENARCHISTS BY VICTORIES
                {myRank > 0 && <span style={{ color: GM.inkGold, marginLeft: 8 }}>— YOU: #{myRank}</span>}
              </div>
              {leaderboard.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: GM.vellumFaint, fontStyle: "italic", textShadow: "0 1px 3px rgba(0,0,0,1)" }}>◇ The Void Record is empty.<br/>Be the first to claim it.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {leaderboard.map((p, i) => {
                    const isMe = p.uid === profile?.uid;
                    const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
                    return (
                      <div key={p.uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: isMe ? "rgba(212,168,75,0.15)" : "rgba(0,0,0,0.5)", border: `1px solid ${isMe ? GM.inkGold + "66" : GM.pageBorder}` }}>
                        <div style={{ width: 22, textAlign: "center", flexShrink: 0 }}>
                          {medal ? <span style={{ fontSize: 14 }}>{medal}</span> : <span style={{ fontSize: 11, color: GM.vellumFaint }}>#{i+1}</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 900, color: isMe ? GM.inkGoldBright : GM.vellum, fontFamily: GM.font, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{p.name}{isMe ? " ◀ you" : ""}</div>
                          <div style={{ fontSize: 9, color: GM.vellumFaint, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{RANK_TITLE(p.wins)}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 900, color: isMe ? GM.inkGoldBright : GM.vellumDim, fontFamily: GM.font, textShadow: "0 1px 3px rgba(0,0,0,1)" }}>{p.wins}</div>
                          <div style={{ fontSize: 8, color: GM.vellumFaint }}>WINS</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <button onClick={onClose} style={{ width: "100%", marginTop: 16, padding: "9px", borderRadius: 2, border: `1px solid ${GM.inkBrown}66`, background: `${GM.page}88`, color: GM.vellumDim, fontSize: 12, cursor: "pointer", fontFamily: GM.font }}>← Leave</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// VYREN KIOSK sub-components
// ─────────────────────────────────────────────────────────────
function VyrenModeSelect({ onSelect, onClose }) {
  const MODES = [
    { id:"free",   label:"Free Play", sub:"J'ynxi's Cheat Chest — any card, no limits",         color:GM.inkGold },
    { id:"ranked", label:"Ranked",    sub:"Your real profile deck — Divine Crests on the line",  color:GM.inkGold, note:"◈ Crest betting coming soon" },
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key==="ArrowUp"||e.key==="ArrowLeft")   { e.preventDefault(); setIdx(i=>(i-1+MODES.length)%MODES.length); }
      if (e.key==="ArrowDown"||e.key==="ArrowRight") { e.preventDefault(); setIdx(i=>(i+1)%MODES.length); }
      if (e.key==="Enter")                           { e.preventDefault(); onSelect(MODES[idx].id); }
      if (e.key==="Escape"||e.key==="Backspace")     { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {MODES.map((m,i) => (
        <div key={m.id} onClick={() => onSelect(m.id)} onMouseEnter={() => setIdx(i)}
          style={{ padding:"14px 18px", borderRadius:12, cursor:"pointer", border:`1px solid ${idx===i ? m.color+"88" : m.color+"22"}`, background: idx===i ? `${m.color}18` : "transparent", transition:"all 0.12s" }}>
          <div style={{ fontSize:13, fontWeight:900, color:idx===i?m.color:GM.vellumFaint, fontFamily:GM.font, letterSpacing:idx===i?1:0 }}>
            {idx===i ? `◆ ${m.label} ◆` : m.label}
          </div>
          {idx===i && <div style={{ fontSize:10, color:GM.vellumFaint, marginTop:4 }}>{m.sub}</div>}
          {idx===i && m.note && <div style={{ fontSize:9, color:m.color, marginTop:3, fontStyle:"italic" }}>{m.note}</div>}
        </div>
      ))}
      <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginTop:4 }}>▲ ▼  ·  ENTER  ·  BACKSPACE</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HOLO HELPERS
// ─────────────────────────────────────────────────────────────

// Build synthetic collection merging Firebase collection with DeckBuilder holo selections
function buildHoloCollectionFromDeck(profileCollection, deck) {
  const col = { ...(profileCollection || {}) };
  const selections = deck?.holoSelections || {};
  Object.entries(selections).forEach(([cardId, tier]) => {
    if (tier) col[cardId] = { ...(col[cardId] || { count: 1 }), holoTier: tier, holoCount: 1 };
  });
  return col;
}

// Assign random holo tier to every card in a deck — mixed silver/gold/mythic
function buildPresetHoloDeck(deck) {
  const rollTier = () => {
    const r = Math.random();
    if (r < 0.10) return "holo_mythic";
    if (r < 0.35) return "holo_gold";
    return "holo_silver";
  };
  const col = {};
  const allCards = [
    ...Object.keys(deck.grimoire || {}),
    ...Object.keys(deck.luxmoire || {}),
  ];
  allCards.forEach(id => {
    col[id] = { count: 1, holoCount: 1, holoTier: rollTier() };
  });
  return col;
}

// ─────────────────────────────────────────────────────────────
// STARTER DECK CHOOSER — shown before NPC battle in Free Play
// Pick a preset starter deck; every card gets a random holo tier
// ─────────────────────────────────────────────────────────────
function StarterDeckChoose({ onChooseOwn, onChoosePreset, onBack }) {
  const [view, setView] = useState("choice"); // "choice" | "decklist"
  const [deckIdx, setDeckIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (view === "choice") {
        if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onBack(); }
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); onChooseOwn(); }
        if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); setView("decklist"); }
      } else {
        if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); setView("choice"); }
        if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); setDeckIdx(i => (i - 1 + STARTER_DECKS.length) % STARTER_DECKS.length); }
        if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); setDeckIdx(i => (i + 1) % STARTER_DECKS.length); }
        if (e.key === "Enter") { e.preventDefault(); onChoosePreset(STARTER_DECKS[deckIdx]); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, deckIdx, onChooseOwn, onChoosePreset, onBack]);

  if (view === "choice") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 3, textAlign: "center", marginBottom: 4 }}>◆ CHOOSE YOUR ARSENAL ◆</div>
        <button onClick={onChooseOwn} style={{
          padding: "14px 16px", borderRadius: 10, cursor: "pointer",
          border: `1px solid ${GM.inkGold}66`, background: `${GM.inkGold}12`,
          color: GM.inkGold, fontFamily: GM.font, fontSize: 13, fontWeight: 900,
          textAlign: "left", transition: "all 0.15s",
        }}>
          <div>◆ Use My Deck</div>
          <div style={{ fontSize: 10, color: GM.vellumFaint, fontWeight: 400, marginTop: 4 }}>Your equipped loadout, your rules.</div>
        </button>
        <button onClick={() => setView("decklist")} style={{
          padding: "14px 16px", borderRadius: 10, cursor: "pointer",
          border: `1px solid ${GM.inkGold}66`, background: `${GM.inkGold}10`,
          color: GM.inkGold, fontFamily: GM.font, fontSize: 13, fontWeight: 900,
          textAlign: "left", transition: "all 0.15s",
        }}>
          <div>✦ Preset Holo Deck</div>
          <div style={{ fontSize: 10, color: GM.vellumFaint, fontWeight: 400, marginTop: 4 }}>Pick a starter deck — every card comes holographic.</div>
        </button>
        <div style={{ fontSize: 9, color: GM.vellumFaint, textAlign: "center", letterSpacing: 2, marginTop: 4 }}>◈ BACKSPACE = BACK</div>
      </div>
    );
  }

  // Deck list view
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 3, textAlign: "center", marginBottom: 2 }}>✦ SELECT STARTER DECK ✦</div>
      <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
        {STARTER_DECKS.map((deck, i) => (
          <button key={deck.id} onClick={() => onChoosePreset(deck)} onMouseEnter={() => setDeckIdx(i)}
            style={{
              padding: "10px 14px", borderRadius: 8, cursor: "pointer", textAlign: "left",
              border: `1px solid ${deckIdx === i ? deck.color : deck.color + "33"}`,
              background: deckIdx === i ? `${deck.color}18` : "transparent",
              transition: "all 0.12s",
            }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: deck.color, flexShrink: 0, boxShadow: deckIdx === i ? `0 0 8px ${deck.color}` : "none" }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: deckIdx === i ? deck.color : GM.vellumDim, fontFamily: GM.font }}>
                  {deck.name}
                </div>
                {deckIdx === i && (
                  <div style={{ fontSize: 9, color: GM.vellumFaint, marginTop: 2, lineHeight: 1.4 }}>{deck.ilk} · {deck.description?.slice(0, 60)}...</div>
                )}
              </div>
              {deckIdx === i && <div style={{ marginLeft: "auto", fontSize: 9, color: GM.inkGold, letterSpacing: 1, flexShrink: 0 }}>✦ ALL HOLO ✦</div>}
            </div>
          </button>
        ))}
      </div>
      <div style={{ fontSize: 9, color: GM.vellumFaint, textAlign: "center", letterSpacing: 2, marginTop: 4 }}>▲ ▼ · ENTER · BACKSPACE</div>
    </div>
  );
}

function VyrenMatchSelect({ mode, onSelect, onBack }) {
  const accent = GM.inkGold;
  const OPTS = [
    { label:"Create Match",     icon:"🌐", id:"create" },
    { label:"Join Match",       icon:"◆",  id:"join"   },
    { label:"vs NPC Adversary", icon:"⚔",  id:"npc"    },
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key==="ArrowUp"||e.key==="ArrowLeft")   { e.preventDefault(); setIdx(i=>(i-1+OPTS.length)%OPTS.length); }
      if (e.key==="ArrowDown"||e.key==="ArrowRight") { e.preventDefault(); setIdx(i=>(i+1)%OPTS.length); }
      if (e.key==="Enter")                           { e.preventDefault(); onSelect(OPTS[idx].id); }
      if (e.key==="Escape"||e.key==="Backspace")     { e.preventDefault(); onBack(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
      <div style={{ height:2, borderRadius:2, background:accent, marginBottom:4 }} />
      {OPTS.map((opt,i) => (
        <button key={opt.id} onClick={() => onSelect(opt.id)} onMouseEnter={() => setIdx(i)}
          style={{ padding:"12px 16px", borderRadius:10, cursor:"pointer", border:`1px solid ${idx===i ? accent+"88" : accent+"22"}`, background: idx===i ? `${accent}18` : "transparent", color: idx===i ? accent : GM.vellumFaint, fontSize:13, fontWeight:idx===i?900:400, fontFamily:GM.font, transition:"all 0.12s", letterSpacing: idx===i ? 1 : 0 }}>
          {idx===i ? `◆ ${opt.icon} ${opt.label} ◆` : `${opt.icon} ${opt.label}`}
        </button>
      ))}
      <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginTop:4 }}>▲ ▼  ·  ENTER  ·  BACKSPACE</div>
    </div>
  );
}

function VyrenNPCSelect({ difficulties, onConfirm, onBack }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key==="ArrowUp"||e.key==="ArrowLeft")   { e.preventDefault(); setIdx(i=>(i-1+difficulties.length)%difficulties.length); }
      if (e.key==="ArrowDown"||e.key==="ArrowRight") { e.preventDefault(); setIdx(i=>(i+1)%difficulties.length); }
      if (e.key==="Enter")                           { e.preventDefault(); onConfirm(difficulties[idx].id); }
      if (e.key==="Escape"||e.key==="Backspace")     { e.preventDefault(); onBack(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:4 }}>
      {difficulties.map((d,i) => (
        <div key={d.id} onClick={() => { setIdx(i); onConfirm(d.id); }} onMouseEnter={() => setIdx(i)}
          style={{ padding:"12px 16px", borderRadius:10, cursor:"pointer", border:`1px solid ${idx===i ? d.color : GM.pageMid}`, background: idx===i ? `${d.color}18` : "rgba(124,58,237,0.06)", transition:"all 0.12s" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>{d.icon}</span>
            <div>
              <div style={{ fontSize:13, fontWeight:900, color:idx===i?d.color:GM.vellumFaint, fontFamily:GM.font, letterSpacing:idx===i?1:0 }}>
                {idx===i ? `◆ ${d.id} ◆` : d.id}
              </div>
              {idx===i && <div style={{ fontSize:10, color:GM.vellumFaint, marginTop:2 }}>{d.desc}</div>}
            </div>
          </div>
        </div>
      ))}
      <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginTop:4 }}>▲ ▼  ·  ENTER  ·  BACKSPACE</div>
    </div>
  );
}

function VyrenKiosk({ playerName, profile, onClose, onEnterGame, onEnterNPC }) {
  const [mode, setMode]               = useState(null);
  const [matchMode, setMatchMode]     = useState(null);
  const [pendingDiff, setPendingDiff] = useState(null);   // held while choosing deck
  const [deckChoice, setDeckChoice]   = useState(false);  // show StarterDeckChoose
  const [joinCode, setJoinCode]       = useState("");
  const [difficulty, setDiff]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [createdCode, setCreatedCode] = useState(null);

  const STORAGE_KEY = "terror_tcg_grimoires";
  const savedDecks = (() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; } })();

  const getDeck = () => {
    // Both ranked and free use the same deck — your equipped loadout for your starterDeckId.
    // NEVER grab Object.values()[0] — that returns a random deck from localStorage.
    return resolveDeck(profile);
  };

  const DIFFICULTIES = [
    { id:"Beb",      icon:"🌱", color:"#a78bfa", desc:"Random chaos. Good for learning."        },
    { id:"Champion", icon:"⚔",  color:"#d4a84b", desc:"Follows the rules. A real test."         },
    { id:"Lord",     icon:"👑", color:"#dc2626", desc:"Calculates and punishes. Brace yourself." },
  ];

  const accentColor = GM.inkGold;

  const handleCreate = async () => {
    setLoading(true); setError("");
    try {
      const deck = getDeck();
      if (!deck) { setError("No deck found. Visit The Forge or Cheat Chest first."); setLoading(false); return; }
      const { createMatch, drawAndDepleteCards } = await import("./gameManager");
      const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deck.grimoire || {}, [], 5);
      const code = await createMatch(playerName, depletedGrimoire, deck.luxmoire||{}, deck.prophecy||null, startingHand, deck.curios||[null,null]);
      setCreatedCode(code);
    } catch(e) { setError("Something went wrong."); }
    setLoading(false);
  };

  const handleJoin = async () => {
    if (joinCode.length !== 4) return;
    setLoading(true); setError("");
    try {
      const deck = getDeck();
      if (!deck) { setError("No deck found."); setLoading(false); return; }
      const { joinMatch, drawAndDepleteCards } = await import("./gameManager");
      const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deck.grimoire || {}, [], 5);
      const joined = await joinMatch(joinCode, playerName, depletedGrimoire, deck.luxmoire||{}, deck.prophecy||null, startingHand, deck.curios||[null,null]);
      // joinMatch returns { error } on failure or { success } on success —
      // ALWAYS a truthy object, so `if (!joined)` never caught anything and a
      // full/nonexistent code dropped the joiner onto a dead board. Inspect
      // the shape instead.
      if (joined?.error) { setError(joined.error); setLoading(false); return; }
      onEnterGame(joinCode, 1, playerName);
      onClose();
    } catch(e) { setError("Something went wrong."); }
    setLoading(false);
  };

  const handleNPC = (diff) => {
    const d = diff || difficulty;
    if (!d) return;
    setPendingDiff(d);
    setDeckChoice(true); // show deck choice before launching
  };

  const launchNPC = (deck, collection, extraMeta = {}) => {
    onEnterNPC(playerName, { grimoire:deck.grimoire||{}, luxmoire:deck.luxmoire||{}, curios:deck.curios||[null,null] }, deck.prophecy||null, null, pendingDiff, { collection, ...extraMeta });
    onClose();
  };

  const handleChooseOwn = () => {
    const deck = getDeck();
    if (!deck) { setError("No deck found. Visit The Forge or Cheat Chest first."); setDeckChoice(false); return; }
    launchNPC(deck, buildHoloCollectionFromDeck(profile?.collection, deck));
  };

  const handleChoosePreset = (starterDeck) => {
    // starterDeckId rides the meta so a WIN with this preset stamps
    // starterWins/{deckId} on the profile (terror:all_starters tracker).
    // Only PRESET starter picks carry it — a forged personal deck is not
    // "winning with the starter deck," even if it grew from one.
    launchNPC(starterDeck, buildPresetHoloDeck(starterDeck), { starterDeckId: starterDeck.id });
  };

  const handleMatchSelect = (id) => {
    if (id === "create") { setMatchMode("create"); handleCreate(); }
    else setMatchMode(id);
  };

  // ── EASTER EGG: AENERGY ARTS LORE ──────────────────────────
  // V'yen is named after the Vyren — original Serpentine
  // Aenergy practitioners. Their opposites: Aeternavis (Aegle
  // Phoenix), masters of O'mnia and Solaea. Rare drops on the
  // opening line, gated by win count. Lore reward for the deep.
  // ──────────────────────────────────────────────────────────
  const vyrenLine = () => {
    const wins = profile?.wins || 0;
    const eggRoll = Math.random();

    if (!mode) {
      if (wins >= 100 && eggRoll < 0.08) {
        return '"My mother\'s mother coiled with the first Vyren. Do not mistake the name for the bloodline, Aenarchist."';
      }
      if (wins >= 50 && eggRoll < 0.12) {
        return '"You think the name is mine? The Vyren wove Aenergy before your kind had words for it."';
      }
      if (wins >= 25 && eggRoll < 0.10) {
        return '"An Aeternavis would not last three turns at this table. Soft feathers. Softer wills."';
      }
      return '"Looking for a fight, Aenarchist?"';
    }

    if (mode==="free" && !matchMode) return '"Cheat Chest rules — anything goes."';
    if (mode==="ranked" && !matchMode) return '"Ranked battle. Your real deck. No excuses."';
    if (matchMode==="create" && createdCode) return '"Share the code. Your opponent is waiting."';
    if (matchMode==="join") return '"Enter the code your opponent gave you."';
    if (matchMode==="npc" && !deckChoice) return '"Choose your adversary."';
    if (deckChoice) return '"Which arsenal will you bring?"';
    return '"Ready when you are."';
  };

  const Btn = ({ label, color, onClick, disabled }) => (
    <button onClick={onClick} disabled={disabled} style={{ width:"100%", padding:"14px",
      background:`linear-gradient(135deg,${GM.pageMid},${GM.pageMid})`,
      border:`1px solid ${color||accentColor}`, borderRadius:10, color:color||accentColor,
      fontFamily:GM.font, fontSize:13, fontWeight:900, fontStyle:"italic",
      cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.35:1, transition:"all 0.2s" }}>{label}</button>
  );

  return (
    <div style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(5,3,0,0.85)", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ backgroundImage:`url(${UI.npc})`, backgroundSize:"100% 100%", borderRadius:8, padding:"48px 44px 32px", width:"min(92vw,480px)", minHeight:420 }}>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ fontSize:9, color:SD.red, letterSpacing:4, marginBottom:4 }}>◆ MATCHMAKING KIOSK</div>
          <div style={{ fontSize:22, fontWeight:900, color:GM.inkGoldBright, fontFamily:GM.font, marginBottom:6 }}>V'yen</div>
          <div style={{ fontSize:11, color:GM.vellumFaint, fontStyle:"italic", lineHeight:1.6 }}>{vyrenLine()}</div>
        </div>
        {!mode && <VyrenModeSelect onSelect={setMode} onClose={onClose} />}
        {mode && !matchMode && !createdCode && <VyrenMatchSelect mode={mode} onSelect={handleMatchSelect} onBack={() => setMode(null)} />}
        {matchMode==="create" && (
          <div style={{ textAlign:"center" }}>
            {loading && <div style={{ color:GM.vellumFaint, fontSize:13, marginBottom:16 }}>◆ Creating match...</div>}
            {createdCode && (
              <>
                <div style={{ fontSize:11, color:GM.vellumFaint, marginBottom:8, letterSpacing:2 }}>SHARE THIS CODE</div>
                <div style={{ fontSize:52, fontWeight:900, color:GM.inkGoldBright, letterSpacing:16, fontFamily:GM.font, textShadow:`0 0 24px ${GM.inkGold}`, marginBottom:20 }}>{createdCode}</div>
                <Btn label="◆ Enter Shatterfield" color={GM.inkGoldBright} onClick={() => { onEnterGame(createdCode, 0, playerName); onClose(); }} />
              </>
            )}
          </div>
        )}
        {matchMode==="join" && (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <input placeholder="CODE" value={joinCode} onChange={e => { setJoinCode(e.target.value.toUpperCase()); setError(""); }} maxLength={4} autoFocus
              style={{ width:"100%", padding:"13px 16px", background:"rgba(124,58,237,0.08)", border:`1px solid ${GM.pageBorder}`, borderRadius:10, color:GM.vellumDim, fontFamily:GM.font, fontSize:22, outline:"none", boxSizing:"border-box", textAlign:"center", letterSpacing:10, fontWeight:900 }} />
            <Btn label="◆ Join Match" color={GM.inkGoldBright} onClick={handleJoin} disabled={joinCode.length!==4||loading} />
          </div>
        )}
        {matchMode==="npc" && !deckChoice && <VyrenNPCSelect difficulties={DIFFICULTIES} onConfirm={handleNPC} onBack={() => setMatchMode(null)} />}
        {deckChoice && (
          <StarterDeckChoose
            onChooseOwn={handleChooseOwn}
            onChoosePreset={handleChoosePreset}
            onBack={() => setDeckChoice(false)}
          />
        )}
        {error && <div style={{ fontSize:12, color:SD.red, textAlign:"center", marginTop:10, padding:"8px", background:`${SD.red}10`, borderRadius:8 }}>{error}</div>}
        <button onClick={() => {
          if (deckChoice) { setDeckChoice(false); }
          else if (matchMode && matchMode !== "create") { setMatchMode(null); setError(""); }
          else if (mode && !matchMode) { setMode(null); setError(""); }
          else onClose();
        }} style={{ width:"100%", marginTop:12, padding:"8px", borderRadius:8, border:`1px solid ${GM.pageBorder}`, background:"transparent", color:GM.vellumFaint, fontSize:12, cursor:"pointer" }}>
          {(matchMode && matchMode !== "create") || (mode && !matchMode) ? "← Back" : "← Leave"}
        </button>
        <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginTop:4 }}>BACKSPACE = BACK</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PORTAL CINEMATIC
// ─────────────────────────────────────────────────────────────
function PortalCinematic({ portal, dungeon, onComplete }) {
  const [phase, setPhase]   = useState("blackout");
  const [wordIdx, setWordIdx] = useState(0);
  const color    = portal?.color || "#6d28d9";
  const flavor   = dungeon?.flavor || "◆ The gate opens. ◆";
  const words    = flavor.split(" ");

  useEffect(() => {
    if (phase === "dialogue") return;
    const timings = { blackout: 500, bleed: 900, name: 1000, title: 700 };
    const t = setTimeout(() => {
      const next = { blackout:"bleed", bleed:"name", name:"title", title:"dialogue" };
      setPhase(p => next[p] || p);
    }, timings[phase] || 600);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "dialogue") return;
    if (wordIdx >= words.length) return;
    const t = setTimeout(() => setWordIdx(i => i + 1), 110);
    return () => clearTimeout(t);
  }, [phase, wordIdx, words.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === "q" || e.key === "Q" || e.key === " ") {
        e.preventDefault();
        if (wordIdx >= words.length) onComplete();
        else setWordIdx(words.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [wordIdx, words.length, onComplete]);

  const showName     = ["name","title","dialogue"].includes(phase);
  const showTitle    = ["title","dialogue"].includes(phase);
  const showDialogue = phase === "dialogue";
  const done         = wordIdx >= words.length;

  return (
    <div onClick={() => { if (done) onComplete(); else setWordIdx(words.length); }}
      style={{ position:"fixed", inset:0, zIndex:400, background:"#000", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, pointerEvents:"none", background:`radial-gradient(ellipse at center, ${color}22 0%, transparent 70%)`, opacity: phase === "blackout" ? 0 : 1, transition:"opacity 1.4s ease" }} />
      <div style={{ position:"absolute", top:0, left:0, right:0, height:64, background:"#000", zIndex:1 }} />
      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:64, background:"#000", zIndex:1 }} />
      <div style={{ fontSize:11, color, letterSpacing:6, fontFamily:GM.font, textShadow:`0 0 16px ${color}`, zIndex:2, marginBottom:12, opacity: showName ? 1 : 0, transition:"opacity 0.8s ease" }}>{portal?.subtitle?.toUpperCase()}</div>
      <div style={{ fontSize:30, fontWeight:900, color:"#fff", fontFamily:GM.font, letterSpacing:4, textShadow:`0 0 30px ${color}, 0 0 60px ${color}55`, zIndex:2, marginBottom:10, opacity: showName ? 1 : 0, transform: showName ? "translateY(0)" : "translateY(24px)", transition:"all 0.9s ease" }}>{dungeon?.name?.toUpperCase()}</div>
      <div style={{ fontSize:9, color:"#666", letterSpacing:4, fontFamily:GM.font, zIndex:2, marginBottom:44, opacity: showTitle ? 1 : 0, transition:"opacity 0.7s ease 0.3s" }}>{dungeon?.subtitle?.toUpperCase()}</div>
      <div style={{ maxWidth:380, textAlign:"center", zIndex:2, minHeight:50, padding:"0 32px", opacity: showDialogue ? 1 : 0, transition:"opacity 0.4s ease" }}>
        <div style={{ fontSize:13, color:"#aaa", fontStyle:"italic", lineHeight:2, textShadow:"0 1px 4px rgba(0,0,0,1)" }}>
          {words.slice(0, wordIdx).join(" ")}
          {!done && <span style={{ opacity:0.4 }}>▌</span>}
        </div>
      </div>
      {done && (
        <div style={{ marginTop:44, zIndex:2, animation:"fightPulse 1.2s ease-in-out infinite alternate" }}>
          <div style={{ fontSize:13, fontWeight:900, color, letterSpacing:4, fontFamily:GM.font, textShadow:`0 0 20px ${color}, 0 0 40px ${color}88`, border:`1px solid ${color}`, padding:"12px 40px", borderRadius:4, background:`${color}11` }}>◆ ENTER ◆</div>
        </div>
      )}
      <div style={{ position:"absolute", bottom:72, zIndex:2, fontSize:9, color:"#333", letterSpacing:3, fontFamily:GM.font }}>{done ? "CLICK OR PRESS ENTER" : "PRESS ENTER TO SKIP"}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BOSS CINEMATIC
// ─────────────────────────────────────────────────────────────
function BossCinematic({ villain, onComplete }) {
  const [phase, setPhase]     = useState("blackout");
  const [wordIdx, setWordIdx] = useState(0);
  const dialogue = villain?.dialogue?.approach?.[0] || "";
  const words    = dialogue.split(" ");
  const color    = villain?.accentColor || "#ff2266";

  useEffect(() => {
    const timings = { blackout:600, bleed:900, name:1000, title:800, dialogue:0 };
    if (phase === "dialogue") return;
    const t = setTimeout(() => {
      const next = { blackout:"bleed", bleed:"name", name:"title", title:"dialogue" };
      setPhase(p => next[p] || p);
    }, timings[phase] || 600);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "dialogue") return;
    if (wordIdx >= words.length) return;
    const t = setTimeout(() => setWordIdx(i => i + 1), 120);
    return () => clearTimeout(t);
  }, [phase, wordIdx, words.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === "q" || e.key === "Q" || e.key === " ") {
        e.preventDefault();
        if (wordIdx >= words.length || phase === "fight") onComplete();
        else setWordIdx(words.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, wordIdx, words.length, onComplete]);

  const showName     = ["name","title","dialogue"].includes(phase);
  const showTitle    = ["title","dialogue"].includes(phase);
  const showDialogue = phase === "dialogue";
  const dialogueDone = wordIdx >= words.length;

  return (
    <div onClick={() => { if (dialogueDone) onComplete(); else setWordIdx(words.length); }}
      style={{ position:"fixed", inset:0, zIndex:400, background:"#000", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, background:`radial-gradient(ellipse at center, ${color}18 0%, transparent 70%)`, opacity: phase === "blackout" ? 0 : 1, transition:"opacity 1.2s ease", pointerEvents:"none" }} />
      <div style={{ position:"absolute", top:0, left:0, right:0, height:60, background:"#000", zIndex:1 }} />
      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:60, background:"#000", zIndex:1 }} />
      <div style={{ fontSize:48, marginBottom:20, zIndex:2, opacity: showName ? 1 : 0, transform: showName ? "translateY(0)" : "translateY(20px)", transition:"all 0.8s ease", textShadow:`0 0 40px ${color}` }}>{villain?.avatarIcon}</div>
      <div style={{ fontSize:32, fontWeight:900, color:"#fff", fontFamily:GM.font, letterSpacing:6, textShadow:`0 0 30px ${color}, 0 0 60px ${color}66`, zIndex:2, marginBottom:8, opacity: showName ? 1 : 0, transform: showName ? "translateY(0)" : "translateY(30px)", transition:"all 0.9s ease" }}>{villain?.name?.toUpperCase()}</div>
      <div style={{ fontSize:10, color, letterSpacing:5, fontFamily:GM.font, textShadow:`0 0 12px ${color}`, zIndex:2, marginBottom:40, opacity: showTitle ? 1 : 0, transition:"opacity 0.8s ease 0.2s" }}>{villain?.title?.toUpperCase()}</div>
      <div style={{ maxWidth:400, textAlign:"center", zIndex:2, minHeight:60, opacity: showDialogue ? 1 : 0, transition:"opacity 0.4s ease", padding:"0 32px" }}>
        <div style={{ fontSize:13, color:"#ccc", fontStyle:"italic", lineHeight:2, textShadow:"0 1px 4px rgba(0,0,0,1)" }}>
          {words.slice(0, wordIdx).join(" ")}
          {wordIdx < words.length && <span style={{ opacity:0.4 }}>▌</span>}
        </div>
      </div>
      {dialogueDone && (
        <div style={{ marginTop:40, zIndex:2, animation:"fightPulse 1.2s ease-in-out infinite alternate" }}>
          <div style={{ fontSize:14, fontWeight:900, color, letterSpacing:4, fontFamily:GM.font, textShadow:`0 0 20px ${color}, 0 0 40px ${color}88`, border:`1px solid ${color}`, padding:"12px 40px", borderRadius:4, background:`${color}11` }}>◆ FIGHT ◆</div>
        </div>
      )}
      <div style={{ position:"absolute", bottom:72, zIndex:2, fontSize:9, color:"#444", letterSpacing:3, fontFamily:GM.font }}>{dialogueDone ? "CLICK OR PRESS ENTER" : "PRESS ENTER TO SKIP"}</div>
      <style>{`@keyframes fightPulse { from { opacity:0.7; transform:scale(1); } to { opacity:1; transform:scale(1.04); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// INNER EMPERIUM SELECT
// ─────────────────────────────────────────────────────────────
function InnerEmperiumSelect({ playerName, profile, onClose, onEnterNPC, onDepths }) {
  const [cinematic, setCinematic]     = useState(false);
  const [pendingVillain, setPendingVillain] = useState(null);
  const innerWins = profile?.innerWins || 0;
  const accentColor = "#ff2266";

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleCinematicComplete = () => {
    setCinematic(false);
    if (pendingVillain) onEnterNPC(pendingVillain.difficulty, pendingVillain);
  };

  if (cinematic && pendingVillain) return <BossCinematic villain={pendingVillain} onComplete={handleCinematicComplete} />;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(5,3,0,0.94)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:GM.font }}>
      <div style={{ width:"min(92vw,440px)", maxHeight:"90vh", overflowY:"auto", background:`linear-gradient(145deg,#1a000a,#0d0005)`, border:`1px solid ${accentColor}44`, borderRadius:20, padding:"28px 24px", boxShadow:`0 0 60px ${accentColor}22` }}>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ fontSize:9, color:accentColor, letterSpacing:4, marginBottom:6, textShadow:`0 0 12px ${accentColor}` }}>◆ THE INNER EMPERIUM ◆</div>
          <div style={{ fontSize:20, fontWeight:900, color:GM.vellum, fontFamily:GM.font, textShadow:"0 1px 6px rgba(0,0,0,1)" }}>Endless Depths</div>
          <div style={{ fontSize:10, color:accentColor, marginTop:6 }}>{innerWins} {innerWins===1?"victory":"victories"} recorded within</div>
        </div>
        <button onClick={onDepths} style={{ width:"100%", padding:"14px", borderRadius:10, cursor:"pointer", background:`${accentColor}15`, border:`1px solid ${accentColor}`, color:accentColor, fontFamily:GM.font, fontSize:14, fontWeight:900, marginBottom:16, textShadow:`0 0 8px ${accentColor}` }}>
          ◆ Enter the Depths — ELO Ranked
        </button>
        <div style={{ fontSize:9, color:GM.vellumFaint, textAlign:"center", letterSpacing:2, marginBottom:14 }}>BACKSPACE = LEAVE</div>
        <button onClick={onClose} style={{ width:"100%", padding:"10px", borderRadius:2, border:`1px solid ${GM.inkBrown}66`, background:"transparent", color:GM.vellumFaint, fontSize:12, cursor:"pointer", fontFamily:GM.font }}>← Turn Back</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DECK LOOKUP — safe on mobile (localStorage can be sandboxed)
// Falls back through: loadout → starterDeck in storage → any saved → starterDeck definition
// getStarterDeck is imported at top of file from starterDecks.js
// ─────────────────────────────────────────────────────────────
function resolveDeck(profile) {
  // 1. Use Firebase loadout if set (highest priority — user explicitly saved this)
  if (profile?.loadout?.grimoire) {
    const deck = profile.loadout;
    const starterDeck = profile?.starterDeckId ? getStarterDeck(profile.starterDeckId) : null;
    return {
      ...deck,
      curios:   (deck.curios?.some(c => c !== null)) ? deck.curios   : (starterDeck?.curios   || [null, null]),
      prophecy: deck.prophecy || starterDeck?.prophecy || null,
    };
  }
  // 2. Try localStorage with exact starterDeckId key match
  if (profile?.starterDeckId) {
    const STORAGE_KEY = "terror_tcg_grimoires";
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (saved[profile.starterDeckId]) {
        const deck = saved[profile.starterDeckId];
        const starterDeck = getStarterDeck(profile.starterDeckId);
        return {
          ...deck,
          curios:   (deck.curios?.some(c => c !== null)) ? deck.curios   : (starterDeck?.curios   || [null, null]),
          prophecy: deck.prophecy || starterDeck?.prophecy || null,
        };
      }
    } catch (e) {}
    // 3. Always fall back to the correct starter deck definition — never a random saved deck
    return getStarterDeck(profile.starterDeckId) || null;
  }
  return null;
}

// Build synthetic collection merging Firebase collection with DeckBuilder holo selections

// ─────────────────────────────────────────────────────────────
// MAIN PLAZA COMPONENT
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// PLAZA HUB — The Sign Board
// Visual novel style — same system as dungeon interactables
// Houses: The Forge + The Codex + future sections
// ─────────────────────────────────────────────────────────────

function ForgeSection({ profile, onClose }) {
  // Story Variants, Grimoire Pages, Companions earned from dungeons
  const storyVariants  = profile?.storyVariants  || [];
  const grimPages      = profile?.grimoirePages   || [];
  const companions     = profile?.companions      || [];
  const easterEggs     = profile?.easterEggVariants || [];

  const [tab, setTab] = useState("variants"); // variants | pages | companions

  const tabStyle = (id) => ({
    padding: "8px 16px", borderRadius: 6, cursor: "pointer",
    fontFamily: GM.font, fontSize: 10, letterSpacing: 2,
    background: tab === id ? `${GM.inkGold}22` : "transparent",
    border: `1px solid ${tab === id ? GM.inkGold : GM.pageBorder}`,
    color: tab === id ? GM.inkGold : GM.vellumFaint,
    transition: "all 0.2s",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 16 }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: 8 }}>
        <button style={tabStyle("variants")} onClick={() => setTab("variants")}>
          ◈ Story Variants ({storyVariants.length + easterEggs.length})
        </button>
        <button style={tabStyle("pages")} onClick={() => setTab("pages")}>
          ◇ Grimoire Pages ({grimPages.length}/???)
        </button>
        <button style={tabStyle("companions")} onClick={() => setTab("companions")}>
          ✦ Companions ({companions.length})
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {tab === "variants" && (
          <div>
            {storyVariants.length === 0 && easterEggs.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 20px",
                color: GM.vellumFaint, fontSize: 12, fontStyle: "italic",
                lineHeight: 2,
              }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.3 }}>◈</div>
                No Story Variants yet.<br />
                Defeat dungeon bosses to earn variants<br />
                that remember where they came from.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {[...storyVariants, ...easterEggs].map((v, i) => (
                  <div key={i} style={{
                    width: 80, padding: 8, borderRadius: 8,
                    background: `${GM.pageMid}44`,
                    border: `1px solid ${v.isEasterEgg ? GM.inkGoldBright : GM.inkGold}44`,
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                    boxShadow: v.isEasterEgg ? `0 0 12px ${GM.inkGold}44` : "none",
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 6,
                      background: `${GM.pageWarm}`,
                      border: `1px solid ${v.isEasterEgg ? GM.inkGoldBright : GM.inkGold}33`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20,
                    }}>
                      {v.isEasterEgg ? "◈" : "◆"}
                    </div>
                    <div style={{
                      fontSize: 8, color: v.isEasterEgg ? GM.inkGold : GM.inkGold,
                      textAlign: "center", fontFamily: GM.font,
                      letterSpacing: 0.5, lineHeight: 1.4,
                    }}>
                      {v.name || v.cardId}
                    </div>
                    {v.isEasterEgg && (
                      <div style={{ fontSize: 7, color: GM.inkGold, opacity: 0.7 }}>◈ EGG</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "pages" && (
          <div>
            {grimPages.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 20px",
                color: GM.vellumFaint, fontSize: 12, fontStyle: "italic", lineHeight: 2,
              }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.3 }}>◇</div>
                No Grimoire Pages collected.<br />
                They hide in places you weren't supposed to go.<br />
                <span style={{ fontSize: 10, opacity: 0.5 }}>??? pages total. Or maybe more.</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {grimPages.map((page, i) => (
                  <div key={i} style={{
                    width: 100, padding: 12, borderRadius: 8,
                    background: `${GM.inkGold}11`,
                    border: `1px solid ${GM.inkGold}33`,
                    display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    <div style={{
                      fontSize: 10, color: GM.inkGold,
                      fontFamily: GM.font, letterSpacing: 1,
                    }}>
                      Page {page.pageId}
                    </div>
                    <div style={{
                      fontSize: 8, color: GM.vellumFaint,
                      fontStyle: "italic", lineHeight: 1.6,
                    }}>
                      {page.fragment || "The text fades when you try to read it."}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "companions" && (
          <div>
            {companions.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "40px 20px",
                color: GM.vellumFaint, fontSize: 12, fontStyle: "italic", lineHeight: 2,
              }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.3 }}>✦</div>
                No companions yet.<br />
                Clear dungeons to unlock companions<br />
                that follow you through the Plaza.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {companions.map((c, i) => (
                  <div key={i} style={{
                    width: 80, padding: 8, borderRadius: 8,
                    background: `${GM.inkGoldBright}11`,
                    border: `1px solid ${GM.inkGoldBright}33`,
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: "50%",
                      background: GM.pageWarm,
                      border: `1px solid ${GM.inkGoldBright}44`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20,
                    }}>✦</div>
                    <div style={{
                      fontSize: 8, color: GM.inkGoldBright,
                      textAlign: "center", fontFamily: GM.font,
                    }}>
                      {c.name || c.id}
                    </div>
                    {c.active && (
                      <div style={{
                        fontSize: 7, color: GM.inkGoldBright,
                        background: `${GM.inkGoldBright}22`,
                        border: `1px solid ${GM.inkGoldBright}44`,
                        padding: "2px 6px", borderRadius: 4,
                      }}>ACTIVE</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── PLAZA HUB MODAL ──────────────────────────────────────────
// Visual novel style — p_forge.webp fills top panel
// Willie the Forger lives here (Willy in disguise, new apron)
// Four buttons positioned over scene objects:
//   Cauldron (far left) → The Cauldron — card/item creation
//   Fireplace (center)  → The Forge    — earned story items
//   Grimoire (right)    → The Codex    — lore & cards
//   Crystal (far right) → The Crystal  — re-roll/fate (TBA)
function PlazaHub({ profile, section: initialSection, onClose, onOpenCodex, onForge, logLine }) {
  const [phase, setPhase]         = useState(initialSection ? "section" : "sign");
  const [activeSection, setActiveSection] = useState(initialSection || null);
  const [typed, setTyped]         = useState("");
  const [typing, setTyping]       = useState(true);
  const [lineIdx, setLineIdx]     = useState(0);
  const [willieClicked, setWillieClicked] = useState(false);

  // Willie the Forger dialogue — he is DEFINITELY not Willy
  const WILLIE_LINES = [
    "◆ Ah, a visitor! Welcome to my establishment.",
    "I am Willie. Willie the Forger. Not to be confused with anyone else.",
    "The apron is new. The name is new. Everything is new.",
    "...The broom is also new. Very different broom.",
  ];

  // Scene description lines — shown before Willie speaks
  const SCENE_LINES = [
    "The air smells of coal and something older.",
    "A hunched figure tends the fireplace without looking up.",
    "He is wearing an apron. It says 'WILLIE' on it in fresh ink.",
  ];

  const LINES = willieClicked ? WILLIE_LINES : SCENE_LINES;

  // Session record — scene + Willie-the-Forger lines land in the plaza log
  const logLineRef = useRef(logLine);
  useEffect(() => { logLineRef.current = logLine; }, [logLine]);
  useEffect(() => {
    if (phase !== "sign") return;
    const l = LINES[lineIdx];
    if (l && logLineRef.current) {
      logLineRef.current({
        label: willieClicked ? "Willie the Forger" : "The Forge",
        color: willieClicked ? "#c9a84c" : GM.inkGold,
        text: l,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIdx, willieClicked, phase]);

  useEffect(() => {
    if (phase !== "sign") return;
    setTyped(""); setTyping(true);
    let i = 0;
    const line = LINES[lineIdx] || "";
    const t = setInterval(() => {
      i++;
      setTyped(line.slice(0, i));
      if (i >= line.length) { clearInterval(t); setTyping(false); }
    }, 22);
    return () => clearInterval(t);
  }, [lineIdx, phase, willieClicked]);

  const advance = () => {
    if (typing) { setTyped(LINES[lineIdx]); setTyping(false); return; }
    if (lineIdx < LINES.length - 1) setLineIdx(i => i + 1);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      if ((e.key === "Enter" || e.key === " ") && phase === "sign") { e.preventDefault(); advance(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, phase, lineIdx, typing]);

  // Four scene objects — positioned as % of image width/height
  // Adjust these values to match where objects sit in p_forge.webp
  const SECTIONS = [
    {
      id: "cauldron",
      label: "The Cauldron",
      symbol: "🫧",
      color: "#22d3ee",      // cyan — brew/create
      pos: { left: "17%", top: "55%" }, // far left — over the cauldron
      comingSoon: true,
      desc: "Brew something new. Card and item creation. Coming soon.",
    },
    {
      id: "forge",
      label: "The Forge",
      symbol: "◈",
      color: GM.inkGoldBright,
      pos: { left: "42%", top: "55%" }, // center — over the fireplace
      comingSoon: false,
      desc: "Your story. Variants earned, pages found, companions unlocked.",
    },
    {
      id: "codex",
      label: "The Codex",
      symbol: "◆",
      color: GM.inkGold,
      pos: { left: "68%", top: "55%" }, // right — over the grimoire
      comingSoon: false,
      desc: "Lore, cards, records. Everything the Emperium left behind.",
    },
    {
      id: "crystal",
      label: "The Crystal",
      symbol: "✦",
      color: "#9333ea",      // purple — fate/re-roll
      pos: { left: "88%", top: "55%" }, // far right — over the crystal
      comingSoon: true,
      desc: "Fate manipulation. Re-rolling the Unstable World. Coming soon.",
    },
  ];

  if (phase === "section" && activeSection === "forge") {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,5,0.97)",
        display: "flex", flexDirection: "column",
        fontFamily: GM.font,
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 24px",
          borderBottom: `1px solid ${GM.inkGoldBright}33`,
          display: "flex", alignItems: "center", gap: 16,
        }}>
          <button onClick={() => { setPhase("sign"); setActiveSection(null); }} style={{
            background: "transparent", border: `1px solid ${GM.inkBrown}66`,
            color: GM.vellumFaint, padding: "4px 10px", borderRadius: 6,
            fontSize: 10, cursor: "pointer", fontFamily: GM.font,
          }}>← Back</button>
          <div style={{
            fontSize: 11, color: GM.inkGoldBright,
            letterSpacing: 4, fontWeight: 900,
            textShadow: `0 0 12px ${GM.inkGoldBright}`,
          }}>◈ THE FORGE</div>
          <button onClick={onClose} style={{
            marginLeft: "auto", background: "transparent",
            border: `1px solid ${GM.inkBrown}66`, color: GM.vellumFaint,
            padding: "4px 10px", borderRadius: 6, fontSize: 10,
            cursor: "pointer", fontFamily: GM.font,
          }}>ESC</button>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          <ForgeSection profile={profile} onClose={onClose} />
        </div>
      </div>
    );
  }

  // Sign close-up view
  const atEnd = lineIdx >= LINES.length - 1 && !typing;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      fontFamily: GM.font,
      overflow: "hidden",
    }}>
      {/* ── Full screen image ── */}
      <div style={{
        position: "absolute", inset: 0,
        cursor: "pointer",
      }} onClick={advance}>
        <img
          src="https://terror-assets.pages.dev/p_forge.webp"
          alt="The Forge"
          onError={e => { e.target.style.display="none"; e.target.parentNode.style.background="linear-gradient(160deg,#0a0414,#130a2a)"; }}
          style={{
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "center top",
            display: "block",
          }}
        />
        {/* Subtle vignette so text is readable */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
        }} />

        {/* Willie the Forger — center-bottom of image */}
        {/* swap: willie_forger.webp — Willy, same pose, apron that says WILLIE, different hat */}
        <div
          onClick={(e) => { e.stopPropagation(); setWillieClicked(true); setLineIdx(0); setTyped(""); setTyping(true); }}
          style={{
            position: "absolute", bottom: "8%", left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3, cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center",
          }}>
          {/* Willie CSS placeholder — same as Willy but with apron */}
          <div style={{
            width: 40, height: 48,
            background: "linear-gradient(180deg, #3a2a1a, #2a1a0a)",
            border: "2px solid #c9a84c66",
            borderRadius: "40% 40% 30% 30%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, position: "relative",
            boxShadow: "0 0 12px rgba(201,168,76,0.3)",
          }}>
            🧑‍🔧
            {/* Apron badge */}
            <div style={{
              position: "absolute", bottom: -6, left: "50%",
              transform: "translateX(-50%)",
              background: "#c9a84c", color: "#000",
              fontSize: 5, fontWeight: 900, letterSpacing: 1,
              padding: "1px 4px", borderRadius: 2, whiteSpace: "nowrap",
            }}>WILLIE</div>
          </div>
          {/* Speak bubble */}
          <div style={{
            marginTop: 6,
            background: "rgba(0,0,0,0.85)",
            border: `1px solid #c9a84c66`,
            borderRadius: 8, padding: "3px 10px",
            fontSize: 9, color: "#c9a84c",
            fontFamily: GM.font, letterSpacing: 1,
            backdropFilter: "blur(4px)",
            whiteSpace: "nowrap",
          }}>◆ Speak</div>
        </div>

        {/* 4 Section buttons — absolutely positioned over scene objects */}
        {SECTIONS.map(s => (
          <div key={s.id}
            onClick={(e) => {
              e.stopPropagation();
              if (s.comingSoon) return;
              if (s.id === "codex") { onOpenCodex && onOpenCodex(); onClose(); return; }
              if (s.id === "forge") { onForge && onForge(); onClose(); return; }
              setActiveSection(s.id); setPhase("section");
            }}
            style={{
              position: "absolute",
              left: s.pos.left, top: s.pos.top,
              transform: "translate(-50%, -50%)",
              zIndex: 4,
              padding: "8px 14px",
              background: s.comingSoon ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.82)",
              border: `2px solid ${s.comingSoon ? s.color + "44" : s.color + "99"}`,
              borderRadius: 8,
              display: "flex", flexDirection: "column",
              alignItems: "center", gap: 3,
              cursor: s.comingSoon ? "default" : "pointer",
              transition: "all 0.2s",
              boxShadow: s.comingSoon ? "none" : `0 0 16px ${s.color}55, 0 4px 12px rgba(0,0,0,0.9)`,
              backdropFilter: "blur(6px)",
              opacity: s.comingSoon ? 0.5 : 1,
            }}>
            <div style={{ fontSize: 16, color: s.color, textShadow: s.comingSoon ? "none" : `0 0 10px ${s.color}` }}>
              {s.symbol}
            </div>
            <div style={{ fontSize: 7, color: s.color, fontWeight: 900, letterSpacing: 2, whiteSpace: "nowrap" }}>
              {s.label.toUpperCase()}
            </div>
            {s.comingSoon && (
              <div style={{ fontSize: 6, color: s.color, opacity: 0.6, letterSpacing: 1 }}>SOON</div>
            )}
          </div>
        ))}

        {/* Title */}
        <div style={{
          position: "absolute", top: 12, left: "50%",
          transform: "translateX(-50%)", zIndex: 5,
          fontSize: 9, color: "rgba(255,255,255,0.85)",
          letterSpacing: 5, fontFamily: GM.font,
          background: "rgba(0,0,0,0.55)",
          padding: "3px 14px", borderRadius: 4,
          backdropFilter: "blur(4px)",
          textShadow: "0 1px 4px rgba(0,0,0,1)",
          pointerEvents: "none",
        }}>◆ THE OLD FOLD ◆</div>

        {/* ESC */}
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{
          position: "absolute", top: 10, right: 10, zIndex: 6,
          background: "rgba(0,0,0,0.7)", border: `1px solid ${GM.inkBrown}66`,
          color: GM.vellumFaint, padding: "4px 10px",
          borderRadius: 6, fontSize: 10, cursor: "pointer",
        }}>ESC</button>
      </div>

      {/* ── Dialogue bubble — absolute, bottom center ── */}
      <div style={{
        position: "absolute",
        bottom: 32, left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        width: "min(680px, 90vw)",
      }}>
        {/* Dialogue bubble — wraps tightly around text */}
        <div style={{
          background: willieClicked
            ? "linear-gradient(135deg, rgba(201,168,76,0.08), rgba(4,2,14,0.95))"
            : "linear-gradient(135deg, rgba(109,40,217,0.08), rgba(4,2,14,0.95))",
          border: `1px solid ${willieClicked ? "#c9a84c55" : "#4a208866"}`,
          borderRadius: 12,
          padding: "12px 20px",
          maxWidth: "min(640px, 90vw)",
          width: "fit-content",
          boxShadow: willieClicked
            ? "0 0 20px rgba(201,168,76,0.12)"
            : "0 0 20px rgba(109,40,217,0.12)",
          transition: "all 0.3s",
        }}>
          {/* Speaker label */}
          <div style={{
            fontSize: 9, letterSpacing: 3, fontWeight: 900,
            color: willieClicked ? "#c9a84c99" : `${GM.inkGold}88`,
            marginBottom: 6, fontFamily: GM.font,
          }}>
            {willieClicked ? "◆ WILLIE THE FORGER" : "◆ THE FORGE"}
          </div>

          {/* Text — wraps naturally */}
          <div style={{
            fontSize: 13, lineHeight: 1.75, fontStyle: "italic",
            color: willieClicked ? "#c9a84c" : GM.vellum,
            textShadow: willieClicked ? "0 0 8px #c9a84c22" : "none",
            transition: "color 0.3s",
            whiteSpace: "pre-wrap",
          }}>
            {typed}
            {typing && <span style={{ opacity: 0.35, animation: "pulse 1s infinite" }}>▌</span>}
          </div>
        </div>

        {/* Progress dots + buttons — below the bubble */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          {/* Dots */}
          <div style={{ display: "flex", gap: 5 }}>
            {LINES.map((_, i) => (
              <div key={i} style={{
                width: i === lineIdx ? 16 : 6,
                height: 4, borderRadius: 2,
                background: i <= lineIdx
                  ? (willieClicked ? "#c9a84c" : GM.inkGold)
                  : GM.pageBorder,
                transition: "all 0.25s",
              }} />
            ))}
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            {!atEnd ? (
              <button onClick={advance} style={{
                padding: "7px 18px", borderRadius: 20, cursor: "pointer",
                border: `1px solid ${willieClicked ? "#c9a84c55" : GM.inkGold + "55"}`,
                background: "transparent",
                color: willieClicked ? "#c9a84caa" : `${GM.inkGold}aa`,
                fontSize: 10, fontFamily: GM.font, letterSpacing: 1,
              }}>◆ Continue</button>
            ) : (
              <button onClick={onClose} style={{
                padding: "7px 20px", borderRadius: 20, cursor: "pointer",
                border: `1px solid ${GM.pageBorder}44`, background: "transparent",
                color: GM.vellumFaint, fontSize: 10, fontFamily: GM.font,
              }}>Leave</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Plaza({
  onEnterDungeon, onEnterShop, onEnterBuilding, onRulebook, onTutorial1, onDeckBuilder,
  onForge, onCollection, onVersus, onEnterGame, onEnterNPC, onBack,
  onFactionHall, onPierre, depthResult, onClearDepthResult,
}) {
  // ── Portrait lock — try OS-level hard lock; RotateGate is the
  //    fallback curtain when a plain browser tab won't grant it. ──
  const { needsRotate } = useOrientationLock();

  // ── Dynamic tile + portrait camera ────────────────────────
  const [tile, setTile] = useState(calcTile);
  // Generated-village gate: procedural Old Fold renders until the
  // real p_center.webp actually LOADS (not merely exists as a URL —
  // the July 15 white-void bug was a truthy PLAZA_BG whose 404 left
  // nothing painted). onLoad flips this; onError keeps the village.
  const [bgLoaded, setBgLoaded] = useState(false);
  const [portrait, setPortrait] = useState(isPortrait);
  const [camOffset, setCamOffset] = useState(null);
  useEffect(() => {
    const update = () => {
      setTile(calcTile());
      setPortrait(isPortrait());
    };
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  const TILE = tile;
  const mobile = isMobile();
  const activeCOLS    = portrait ? P_COLS    : COLS;
  const activeROWS    = portrait ? P_ROWS    : ROWS;
  const activePORTALS = portrait ? P_PORTALS : PORTALS;
  const activeSHOPS   = portrait ? P_SHOPS   : SHOPS;
  const activeNPCS    = portrait ? P_NPCS    : NPCS;
  const activeMAP     = portrait ? P_MAP     : MAP;

  // ── Auth & profile ─────────────────────────────────────────
  const [uid, setUid]                               = useState(null);
  const [playerName, setPlayerName]                 = useState("");
  const [profile, setProfile]                       = useState(null);
  const [authReady, setAuthReady]                   = useState(false);
  const [needsStarterPick, setNeedsStarterPick]     = useState(false);
  const [needsOnboarding, setNeedsOnboarding]       = useState(false);
  const [gender, setGender]                         = useState("m");
  const [showGenderSettings, setShowGenderSettings] = useState(false);
  const profileUnsubRef                             = useRef(null);

  // ── Local companion — SINGLE SOURCE OF TRUTH ────────────────
  // Resolve the equipped companion the SAME way useEquippedCompanion
  // (the failsafe hook every other room uses) does, so the Plaza shows
  // the exact same companion as the Affinity hall, dungeons, workshop,
  // etc. Priority: equipped (if it still exists) → the player's starter
  // companion → null. This is why the companion no longer "shapeshifts"
  // room-to-room: every surface now resolves from this same rule, and
  // renders from the same CDN art source.
  const myCompanionId = useMemo(() => {
    if (!profile) return null;
    const eq = profile.equippedCompanion;
    if (eq && COMPANIONS[eq]) return eq;
    const starter = getStarterCompanionId
      ? getStarterCompanionId(profile.starterDeckId)
      : null;
    return (starter && COMPANIONS[starter]) ? starter : null;
  }, [profile]);
  const myCompanionRadiant = profile?.equippedCompanionRadiant || null;

  // ── My being — the annihilation lane on the shared profile ──
  // profiles/{uid}/annihilation___character (written by CharacterCreate
  // via saveCharacter). Present → I walk the Fold as my being, and my
  // Terror self walks beside me. Absent (Escling, pre-Transcendence) →
  // the human sprite path below stays exactly as it was.
  const myBeingId = profile?.annihilation___character?.beingId || null;
  const myBeingTier = useMemo(() => {
    if (!myBeingId) return null;
    const { level } = levelForXp(profile?.annihilation___character?.xp || 0);
    return tierForLevel(level);
  }, [myBeingId, profile?.annihilation___character?.xp]);

  // ── Movement ───────────────────────────────────────────────
  const [pos, setPos]       = useState(() => isPortrait() ? P_SPAWN : SPAWN);
  const [facing, setFacing] = useState({ x: 0, y: 1 });
  const posRef              = useRef(isPortrait() ? P_SPAWN : SPAWN);

  // ── Visual (sub-tile) position — smooth walking ────────────
  // pos is the authoritative tile position (collision, proximity, NPC zones).
  // visualPosRef is the rendered fractional position that lerps toward pos
  // each frame. We do NOT keep this in React state — the rAF loop drives
  // the camera DOM transform directly via worldRef.current.style. This is
  // the only sane way to do per-frame animation under React 18, which
  // throws "max update depth" if it can't flush per-frame setStates fast
  // enough on slower devices.
  const spawn = isPortrait() ? P_SPAWN : SPAWN;
  const visualPosRef              = useRef({ x: spawn.x, y: spawn.y });
  const rafRef                    = useRef(null);
  const worldRef                  = useRef(null);     // the translate3d world layer
  const localSpriteRef            = useRef(null);     // local PlayerEntity's outer sprite div
  const localCompanionRef         = useRef(null);     // local PlayerEntity's companion div (if any)
  const camOffsetInternalRef      = useRef(null);     // rAF-internal cam offset; separate from camOffset state

  // ── Multiplayer ────────────────────────────────────────────
  const [players, setPlayers] = useState({});

  // ── Chat ───────────────────────────────────────────────────
  // One server-wide chat: Plaza joins the same "lobby" channel every
  // RoomOverlay-wrapped room uses, so conversation follows players across
  // rooms. Plaza keeps its own in-world floaties either way.
  const chat = useChatSystem({ zoneId: "lobby", uid, playerName, pos });

  // ── UI ─────────────────────────────────────────────────────
  const [showWilly, setShowWilly]           = useState(false);
  const [showBoard, setShowBoard]           = useState(false);
  const [showHub, setShowHub]               = useState(false);
  const [hubSection, setHubSection]         = useState(null); // null | "forge" | "codex"
  const [showVyren, setShowVyren]           = useState(false);
  const [portalPrompt, setPortalPrompt]     = useState(null);
  const [portalActive, setPortalActive]     = useState(false);
  const [portalCinematicData, setPortalCinematicData] = useState(null);
  const [showDepths, setShowDepths]         = useState(false);
  const [showMenu, setShowMenu]             = useState(false);  // Start menu (Pokémon-style)
  const [showGallery, setShowGallery]       = useState(false);  // Deeds & Renown wall
  const [showCodex, setShowCodex]           = useState(false);  // The Codex — the four Ilk, worn open
  const [showPet, setShowPet]               = useState(false);  // companion petting close-up

  // ── Achievement toast feed ─────────────────────────────────
  // The visible payoff for the whole achievement engine. Grants are
  // written to profiles/{uid}/achievements/{id} from all over the app
  // (story wins, pack opens, petting, the Forge). Rather than thread a
  // setter through every one of those call sites, we watch the live
  // achievement map here — the single place that always holds the uid —
  // and toast any id that appears AFTER the first snapshot. This catches
  // grants no matter where they fired, including a cascade (a single win
  // can also trip an empire meta achievement) and even cross-tab earns.
  const { map: achMap } = useAchievements(uid);
  const [toastIds, setToastIds] = useState([]);
  const achSeenRef = useRef(null); // null until the first snapshot is banked
  useEffect(() => {
    const ids = Object.keys(achMap || {});
    // First snapshot just banks what's already owned — never toasts history.
    if (achSeenRef.current === null) {
      achSeenRef.current = new Set(ids);
      return;
    }
    const fresh = ids.filter((id) => !achSeenRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => achSeenRef.current.add(id));
    setToastIds((q) => [...q, ...fresh]);
  }, [achMap]);
  // Reset the bank when the player changes (logout / handoff).
  useEffect(() => { achSeenRef.current = null; setToastIds([]); }, [uid]);
  const [petPrompt, setPetPrompt]           = useState(null);   // {x,y} tile when facing companion
  const [compOffset, setCompOffset]         = useState({ x: 0, y: -1 }); // companion offset; freezes on a pure turn
  const [readySet, setReadySet]             = useState({});
  const [shopPrompt, setShopPrompt]         = useState(null);
  const [npcPrompt, setNpcPrompt]           = useState(null);

  // ── ROOM CONSOLE (the treatment — July 8) ─────────────────
  // Session-persistent dialogue log for the "plaza" zone + the
  // ◀ ▶ cycling selection across every prompt in range at once.
  const [plazaLog, setPlazaLog] = useState(() => getSessionLog("plaza").slice());
  const addPlazaLog = useCallback((entry) => {
    setPlazaLog(prev => {
      const next = [...prev, entry];
      setSessionLog("plaza", next);
      return next;
    });
  }, []);
  const [consoleIdx, setConsoleIdx] = useState(0);
  const consoleIdsRef = useRef("");
  const [toast, setToast]                   = useState(null);
  const [challengePrompt, setChallengePrompt] = useState(null); // { targetUid, targetName } — outgoing
  const [incomingChallenge, setIncomingChallenge] = useState(null); // { fromUid, fromName, code }

  const keysRef     = useRef({});
  const facingRef    = useRef({ x: 0, y: 1 });   // current facing, readable synchronously in the step loop
  const compOffsetRef = useRef({ x: 0, y: -1 }); // mirrors compOffset, updated only on a real step
  const menuOpenRef = useRef(false);
  const sessionId   = useRef(`${Date.now()}_${Math.random().toString(36).slice(2)}`);

  // ── Camera follow — runs on BOTH desktop and portrait ────
  // Deadzone follow on desktop, snap-center on portrait.
  // Camera offset state — used by ChatSystem (chat bubbles need a React
  // state value to position themselves). Updated only when `pos` changes
  // (tile-cross), so this fires ~1-2 times per second of walking, not 60.
  // The world's actual visual translate3d is driven directly via worldRef
  // in the rAF loop — that's what gives smooth sub-tile scrolling. The
  // camOffset state just tracks a coarse "where is the camera roughly?"
  // for non-animation consumers.
  useEffect(() => {
    setCamOffset(prev => {
      const next = calcCamOffset(pos.x, pos.y, TILE, prev, {
        cols: activeCOLS, rows: activeROWS,
      });
      if (prev && next.x === prev.x && next.y === prev.y) return prev;
      return next;
    });
  }, [pos, portrait, TILE, activeCOLS, activeROWS]);

  // ── Teleport guard — snap visualPosRef to pos on large jumps ──
  // Covers: portrait↔desktop swap, portal respawn, edge cases.
  // Normal tile walking (delta 1) does not trigger this. The rAF loop
  // picks up the new ref value on its next tick — no setState needed.
  useEffect(() => {
    const cur = visualPosRef.current;
    if (Math.abs(pos.x - cur.x) > 2 || Math.abs(pos.y - cur.y) > 2) {
      visualPosRef.current = { x: pos.x, y: pos.y };
    }
  }, [pos]);

  // ── Smooth movement — lerp visualPosRef toward pos every frame ──
  // pos is tile-indexed (authoritative). visualPosRef is fractional,
  // catches up via exponential lerp. Tuned so a single-tile step
  // takes ~130ms to visually complete — snappy but smooth.
  //
  // CRITICAL: this loop does NOT call setState. It writes the world's
  // translate3d directly to worldRef.current.style.transform and the
  // local player's sub-tile offset to localSpriteRef/localCompanionRef.
  // Driving 60fps animation through React state under React 18 is
  // unsafe — when the scheduler can't flush per-frame setStates fast
  // enough (cold starts, busy CPU), it raises "max update depth".
  // DOM-direct animation sidesteps that entirely.
  useEffect(() => {
    let last = performance.now();
    const LERP_SPEED = 14; // higher = snappier; ~14 feels EarthBound-ish
    const SNAP_EPSILON = 0.01; // snap when within 1% of a tile

    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); // cap dt at 50ms to avoid jumps on tab-return
      last = now;
      const target = posRef.current;
      const cur    = visualPosRef.current;
      const dx = target.x - cur.x;
      const dy = target.y - cur.y;

      let nx, ny;
      if (Math.abs(dx) < SNAP_EPSILON && Math.abs(dy) < SNAP_EPSILON) {
        // Close enough — snap
        nx = target.x; ny = target.y;
        visualPosRef.current = { x: nx, y: ny };
      } else {
        // Exponential lerp: frame-rate independent via dt
        const t = 1 - Math.exp(-LERP_SPEED * dt);
        nx = cur.x + dx * t;
        ny = cur.y + dy * t;
        visualPosRef.current = { x: nx, y: ny };
      }

      // Camera transform — direct DOM write, no React.
      const camNow = calcCamOffset(nx, ny, TILE, camOffsetInternalRef.current, {
        cols: activeCOLS, rows: activeROWS,
      });
      camOffsetInternalRef.current = camNow;
      if (worldRef.current) {
        worldRef.current.style.transform = `translate3d(${-camNow.x}px, ${-camNow.y}px, 0)`;
      }

      // Local player sub-tile transform — applied to the sprite div directly.
      // Sprite's `left/top` is at the integer tile position (player.x * TILE),
      // so the rAF transform supplies the fractional remainder. Net visual
      // position is at (visualPosRef.x, visualPosRef.y) in tile coords.
      // CSS transition is `none` for local sprite so this doesn't fight CSS.
      const subX = (nx - target.x) * TILE;
      const subY = (ny - target.y) * TILE;
      const subTransform = `translate3d(${subX}px, ${subY}px, 0)`;
      if (localSpriteRef.current) {
        localSpriteRef.current.style.transform = subTransform;
      }
      if (localCompanionRef.current) {
        localCompanionRef.current.style.transform = subTransform;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [TILE, activeCOLS, activeROWS]);

  // Mirror facing/compOffset into refs so the step loop can read them
  // synchronously without re-subscribing every turn.
  useEffect(() => { facingRef.current = facing; }, [facing]);
  useEffect(() => { compOffsetRef.current = compOffset; }, [compOffset]);

  // Open/close the Start menu with M. Ignored while typing in an input or
  // when another modal owns the screen (M isn't a movement or bubble key).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "m" && e.key !== "M") return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (showVyren || showBoard || showHub || showWilly || showGenderSettings || showDepths || portalActive || showPet) return;
      e.preventDefault();
      setShowMenu(v => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showVyren, showBoard, showHub, showWilly, showGenderSettings, showDepths, portalActive, showPet]);

  // ── Firebase Auth ──────────────────────────────────────────
  useEffect(() => { setPersistence(auth, browserLocalPersistence).catch(() => {}); }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUid(user.uid);
        const name = user.displayName || user.email?.split("@")[0] || "Aenarchist";
        setPlayerName(name);
        const p = await getProfileByUid(user.uid);
        if (!p) {
          // Brand-new profile — the Empire's uniform welcome shape.
          // (Same createProfile shape as every IE door — no drift.)
          const newP = await createProfile(user.uid, name);
          setProfile({ ...newP, uid: user.uid });
          profileUnsubRef.current = listenToProfileByUid(user.uid, (updated) => {
            setProfile(updated);
            if (updated?.gender) setGender(updated.gender);
          });
        } else {
          setProfile({ ...p, uid: user.uid });
          if (p.gender) setGender(p.gender);
          // ANNIHILATION: no Terror starter-deck / onboarding gate.
          // Class selection (Escling → Transcendence) ships as its
          // own flow in a later batch. No Terror-lane grants fire
          // from this app — write-lane law. Everyone walks the
          // Old Fold today.
          profileUnsubRef.current = listenToProfileByUid(user.uid, (updated) => {
            setProfile(updated);
            if (updated?.gender) setGender(updated.gender);
          });
        }
      } else {
        setUid(null); setPlayerName(""); setProfile(null);
        setNeedsStarterPick(false); setNeedsOnboarding(false); setGender("m");
      }
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  const handleLogin = (newUid, name, p) => {
    setUid(newUid); setPlayerName(name);
    setProfile(p || null);
    if (p?.gender) setGender(p.gender);
    profileUnsubRef.current = listenToProfileByUid(newUid, (updated) => {
      setProfile(updated);
      if (updated?.gender) setGender(updated.gender);
    });
  };

  const handleStarterPicked = async (deckId, pickedGender) => {
    setNeedsStarterPick(false);
    if (pickedGender) setGender(pickedGender);
    if (uid) {
      // Grant starter Beb immediately on starter pick
      try { await ensureStarterCompanion(uid); } catch (e) { console.warn("[starter companion]", e); }
      const p = await getProfileByUid(uid);
      if (p) {
        setProfile(p);
        if (p.gender) setGender(p.gender);
        profileUnsubRef.current = listenToProfileByUid(uid, (updated) => {
          setProfile(updated);
          if (updated?.gender) setGender(updated.gender);
        });
      }
    }
  };

  // ── Onboarding completion ────────────────────────────────────
  // Fires when the new player finishes (or dev-skips) the Onboarding
  // overlay. The Onboarding component itself already wrote the profile
  // fields (gender, starterDeckId, hasCompletedOnboarding) and granted
  // the starter deck's cards to the collection. We just need to:
  //   1. Flip the onboarding flag off (reveals the Plaza)
  //   2. Grant the starter companion (same as starter-pick flow)
  //   3. Reload the profile + subscribe to live updates
  const handleOnboardingComplete = async ({ deckId, gender: pickedGender } = {}) => {
    setNeedsOnboarding(false);
    if (pickedGender) setGender(pickedGender);
    if (uid) {
      try { await ensureStarterCompanion(uid); } catch (e) { console.warn("[starter companion]", e); }
      const p = await getProfileByUid(uid);
      if (p) {
        setProfile(p);
        if (p.gender) setGender(p.gender);
        profileUnsubRef.current = listenToProfileByUid(uid, (updated) => {
          setProfile(updated);
          if (updated?.gender) setGender(updated.gender);
        });
      }
    }
  };

  const handleLogout = async () => {
    profileUnsubRef.current?.();
    await signOut(auth);
    setUid(null); setPlayerName(""); setProfile(null);
    setNeedsStarterPick(false); setNeedsOnboarding(false); setGender("m");
  };

  // ── Firebase presence ──────────────────────────────────────
  useEffect(() => {
    if (!uid || !playerName || !authReady || needsStarterPick || needsOnboarding) return;
    const playerRef = ref(db, `${NET}/players/${uid}`);
    set(playerRef, { name: playerName, x: (isPortrait() ? P_SPAWN : SPAWN).x, y: (isPortrait() ? P_SPAWN : SPAWN).y, facingX: 0, facingY: 1, gender, companionId: myCompanionId || null, equipped: profile?.equipped || null, beingId: myBeingId || null, beingTier: myBeingTier || null, ts: Date.now() });
    const unsub = onValue(ref(db, `${NET}/players`), snap => {
      const data = snap.val() || {};
      const cutoff = Date.now() - 15000;
      const others = {};
      Object.entries(data).forEach(([id, p]) => {
        if (id === uid) return;
        if (!p.ts || p.ts < cutoff) { remove(ref(db, `${NET}/players/${id}`)); return; }
        others[id] = p;
      });
      const capped = {};
      Object.entries(others).slice(0, 3).forEach(([id, p]) => { capped[id] = p; });
      setPlayers(capped);
    });
    const heartbeat = setInterval(() => { set(ref(db, `${NET}/players/${uid}/ts`), Date.now()); }, 5000);
    return () => { unsub(); clearInterval(heartbeat); remove(playerRef); };
  }, [uid, playerName, authReady, needsStarterPick, needsOnboarding]);

  // ── Companion swap push — lightweight update so other players see
  // the new companion immediately after a Sol Convert, without tearing
  // down the presence listener. Only writes the companionId field.
  useEffect(() => {
    if (!uid || !playerName || !authReady || needsStarterPick || needsOnboarding) return;
    set(ref(db, `${NET}/players/${uid}/companionId`), myCompanionId || null);
  }, [uid, playerName, authReady, needsStarterPick, needsOnboarding, myCompanionId]);

  // ── Being push — same pattern as companion. The annihilation lane
  // often arrives AFTER the initial presence write (profile listener
  // timing), and a Transcendence mid-session changes it live. Only
  // writes the two being fields; the presence listener stays up.
  useEffect(() => {
    if (!uid || !playerName || !authReady || needsStarterPick || needsOnboarding) return;
    set(ref(db, `${NET}/players/${uid}/beingId`), myBeingId || null);
    set(ref(db, `${NET}/players/${uid}/beingTier`), myBeingTier || null);
  }, [uid, playerName, authReady, needsStarterPick, needsOnboarding, myBeingId, myBeingTier]);

  // ── Cosmetics swap push — same pattern as companion. When the player
  // equips/unequips from any IE app, push the updated `equipped` map to
  // the Plaza presence node so other players see cosmetics change live.
  useEffect(() => {
    if (!uid || !playerName || !authReady || needsStarterPick || needsOnboarding) return;
    set(ref(db, `${NET}/players/${uid}/equipped`), profile?.equipped || null);
  }, [uid, playerName, authReady, needsStarterPick, needsOnboarding, profile?.equipped]);

  const lastSync = useRef(0);
  const syncPos = useCallback((x, y, fx, fy) => {
    if (!uid) return;
    const now = Date.now();
    if (now - lastSync.current < 80) return;
    lastSync.current = now;
    set(ref(db, `${NET}/players/${uid}`), { name: playerName, x, y, facingX: fx, facingY: fy, gender, companionId: myCompanionId || null, equipped: profile?.equipped || null, beingId: myBeingId || null, beingTier: myBeingTier || null, ts: now });
  }, [uid, playerName, gender, myCompanionId, profile?.equipped, myBeingId, myBeingTier]);

  // ── Incoming challenge listener ────────────────────────────
  useEffect(() => {
    if (!uid) return;
    const challengeRef = ref(db, `${NET}/challenges/${uid}`);
    const unsub = onValue(challengeRef, snap => {
      const data = snap.val();
      if (!data) { setIncomingChallenge(null); return; }
      // Auto-expire after 15s
      if (Date.now() - data.ts > 15000) { remove(challengeRef); return; }
      setIncomingChallenge({ fromUid: data.fromUid, fromName: data.fromName, code: data.code });
    });
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!portalPrompt) return;
    if (portalPrompt.id === "pierre") {
      // Pierre skips ready-up entirely — launch hub immediately
      setPortalPrompt(null);
      setPortalActive(false);
      onPierre && onPierre({ ...profile, uid, name: playerName });
      return;
    }
    const unsub = onValue(ref(db, `${NET}/ready/${portalPrompt.id}`), snap => setReadySet(snap.val() || {}));
    return () => unsub();
  }, [portalPrompt]);

  useEffect(() => {
    if (!portalPrompt) return;
    if (portalPrompt.id === "pierre") return;
    // The Gate isn't in Terror's DUNGEONS — it resolves to the
    // Outskirts. Fallback keeps the ready-up + cinematic flow whole.
    const dungeon = DUNGEONS.find(d => d.id === portalPrompt.dungeonId)
      || GATE_DESTINATION;
    if (!dungeon?.unlocked) return;
    const allKeys = [uid, ...Object.keys(players)];
    if (allKeys.length > 0 && allKeys.every(k => readySet[k])) {
      setTimeout(() => {
        remove(ref(db, `${NET}/ready/${portalPrompt.id}`));
        const portal = portalPrompt;
        setPortalPrompt(null); setReadySet({}); setPortalActive(false);
        setPortalCinematicData({ portal, dungeon, profile });
      }, 1200);
    }
  }, [readySet, portalPrompt, players, playerName, profile, onEnterDungeon]);

  useEffect(() => {
    const isOpen = showWilly || showBoard || showVyren || showGenderSettings || portalActive || showHub || showMenu || showPet;
    menuOpenRef.current = isOpen;
    if (isOpen) keysRef.current = {};
  }, [showWilly, showBoard, showVyren, showGenderSettings, portalActive, showHub, showMenu, showPet]);

  useEffect(() => {
    keysRef.current = {};
    const MOVE_KEYS = new Set(["ArrowUp","ArrowDown","ArrowLeft","ArrowRight","w","W","a","A","s","S","d","D"]);
    const down  = e => { if (menuOpenRef.current && MOVE_KEYS.has(e.key)) return; keysRef.current[e.key] = true; };
    const up    = e => { keysRef.current[e.key] = false; };
    const clear = ()  => { keysRef.current = {}; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup",   up);
    window.addEventListener("blur",    clear);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup",   up);
      window.removeEventListener("blur",    clear);
    };
  }, []);

  useEffect(() => {
    if (!playerName || needsStarterPick || needsOnboarding) return;
    let lastMove = 0;
    const loop = setInterval(() => {
      const menuOpen = showWilly || showBoard || showVyren || showGenderSettings || portalActive || showHub || showMenu || showPet;
      if (menuOpen) { keysRef.current = {}; return; }
      const now = Date.now();
      if (now - lastMove < 130) return;
      const k = keysRef.current;
      let dx = 0, dy = 0;
      if (k["ArrowUp"]    || k["w"] || k["W"]) dy = -1;
      if (k["ArrowDown"]  || k["s"] || k["S"]) dy =  1;
      if (k["ArrowLeft"]  || k["a"] || k["A"]) dx = -1;
      if (k["ArrowRight"] || k["d"] || k["D"]) dx =  1;
      if (dx === 0 && dy === 0) return;
      lastMove = now;

      const f = facingRef.current;
      const facingThisWay = (f.x === dx && f.y === dy);

      // ── TURN-THEN-STEP ──────────────────────────────────────
      // Press a direction you are NOT facing → only turn to face it
      // (no step). Press (or hold) the direction you ARE facing → step.
      // A single tap = turn; holding = turn, then walk.
      if (!facingThisWay) {
        const nf = { x: dx, y: dy };
        setFacing(nf);
        facingRef.current = nf;
        // Update presence facing without moving.
        syncPos(posRef.current.x, posRef.current.y, dx, dy);
        // The companion offset is frozen on turns — so if this turn points
        // us AT the companion, surface the Pet prompt. Otherwise clear it.
        const co = compOffsetRef.current;
        const myCompanion = myCompanionId;
        if (myCompanion && co.x === dx && co.y === dy) {
          const px = posRef.current.x, py = posRef.current.y;
          setPetPrompt({ x: px + co.x, y: py + co.y });
        } else {
          setPetPrompt(null);
        }
        return; // turn only — no step this beat
      }

      // ── STEP (already facing this way) ───────────────────────
      setPos(prev => {
        const nx = prev.x + dx, ny = prev.y + dy;
        if (!isWalkable(nx, ny)) return prev;
        posRef.current = { x: nx, y: ny };
        // Companion trails to the tile we just left. This is the ONLY place
        // compOffset changes, so a pure turn never repositions the companion.
        const co = { x: -dx, y: -dy };
        compOffsetRef.current = co;
        setCompOffset(co);
        setPetPrompt(null);               // walking away from the companion
        setFacing({ x: dx, y: dy });
        facingRef.current = { x: dx, y: dy };
        syncPos(nx, ny, dx, dy);
        const portal = getPortalAt(nx, ny);
        const shop   = getShopAt(nx, ny);
        const npc    = getNPCAt(nx, ny);
        if (portal) { setPortalPrompt(p => p || portal); setShopPrompt(null); setNpcPrompt(null); setChallengePrompt(null); }
        else if (shop) { setShopPrompt(s => s || shop); setPortalPrompt(null); setNpcPrompt(null); setChallengePrompt(null); }
        else if (npc)  { setNpcPrompt(n => n || npc); setPortalPrompt(null); setShopPrompt(null); setChallengePrompt(null); }
        else { setPortalPrompt(null); setShopPrompt(null); setNpcPrompt(null); setPortalActive(false); }
        return { x: nx, y: ny };
      });
      // Proximity check OUTSIDE setPos — posRef is always current, players closure is fresh
      const { x: cx, y: cy } = posRef.current;
      const nearby = Object.entries(players).find(([, p]) =>
        Math.abs(p.x - cx) <= 2 && Math.abs(p.y - cy) <= 2
      );
      if (nearby) {
        const [targetUid, targetData] = nearby;
        setChallengePrompt({ targetUid, targetName: targetData.name });
      } else {
        setChallengePrompt(null);
      }
    }, 50);
    return () => clearInterval(loop);
  }, [players, syncPos, playerName, needsStarterPick, needsOnboarding, showWilly, showBoard, showVyren, showGenderSettings, portalActive, showHub, showMenu, showPet]);

  function showToast(msg, color = GM.inkGold) {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2500);
  }

  useEffect(() => {
    if (!depthResult) return;
    const { humanWon, eloChange, newElo, opponentName, isGatekeeper, dcEarned } = depthResult;
    const color = humanWon ? "#22c55e" : "#dc2626";
    const icon  = humanWon ? "✓" : "✗";
    const sign  = eloChange >= 0 ? "+" : "";
    const gkTag = isGatekeeper ? " ⚔ GATEKEEPER" : "";
    const dcTag = dcEarned > 0 ? ` · +${dcEarned} DC` : "";
    showToast(`${icon} ${opponentName}${gkTag} · ${sign}${eloChange} ELO · ${newElo} total${dcTag}`, color);
    onClearDepthResult && onClearDepthResult();
  }, [depthResult]);

  const handleNPCEnter = () => {
    const id = npcPrompt?.id;
    const label = npcPrompt?.label;
    const color = npcPrompt?.color;
    setNpcPrompt(null);
    // Villagers speak from villageData — one source for who they
    // are and what they say. Full DialogueEngine trees come later;
    // today they talk in the console log + a toast.
    const villager = VILLAGERS.find(v => v.id === id);
    if (villager) {
      const lines = (villager.lines && villager.lines.length) ? villager.lines : [villager.persona];
      const line = lines[Math.floor(Math.random() * lines.length)];
      addPlazaLog({ label: villager.name, color: color || GM.inkGold, text: line });
      showToast(`${villager.name}: “${line}”`, color || GM.inkGold);
      return;
    }
    showToast(`${label} speaks...`, color);
  };

  const handleShopEnter = () => {
    const id = shopPrompt?.id;
    setShopPrompt(null);
    const prof = { ...profile, uid, name: playerName };
    // ONE dispatch for all eight buildings — App.js routes by id
    // via villageData. No per-building callback sprawl.
    if (onEnterBuilding) { onEnterBuilding(id, prof); return; }
    // Legacy PRIME-contract fallbacks if the router is older:
    if (id === "forge")            onForge && onForge(prof);
    else if (id === "marketplace") onEnterShop && onEnterShop(prof);
  };

  // ── CONSOLE TARGET LIST — every prompt in range, cycled with ◀ ▶ / Tab ──
  // Replaces the old one-at-a-time floating bubbles (portal > shop > npc >
  // pet precedence). All are now simultaneously reachable via cycling.
  const consoleTargets = useMemo(() => {
    if (showMenu || showPet) return [];
    const list = [];
    if (portalPrompt && !portalActive) list.push({
      id: `portal_${portalPrompt.id}`, kind: "portal",
      verb: "Enter",
      label: portalPrompt.label || portalPrompt.subtitle || "Portal",
      color: portalPrompt.color,
    });
    if (shopPrompt) list.push({
      id: `shop_${shopPrompt.id}`, kind: "shop",
      verb: shopPrompt.id === "bonfire"     ? "Rest"
          : shopPrompt.id === "marketplace" ? "Shop"
          : shopPrompt.id === "forge"       ? "Forge"
          : shopPrompt.id === "trainer"     ? "Train"
          : shopPrompt.id === "registrar"   ? "Register"
          : shopPrompt.id === "bulletin"    ? "Notices"
          : shopPrompt.id === "garden"      ? "Tend"
          : "Enter",
      label: shopPrompt.label || "Building",
      color: shopPrompt.color,
    });
    if (npcPrompt) list.push({
      id: `npc_${npcPrompt.id}`, kind: "npc",
      verb: "Speak",
      label: npcPrompt.label || "Villager",
      color: npcPrompt.color,
    });
    if (petPrompt) list.push({
      id: "pet", kind: "pet", verb: "🩷 Pet", label: "Companion", color: "#ff7fa3",
    });
    return list;
  }, [showMenu, showPet, portalPrompt, portalActive, shopPrompt, npcPrompt, petPrompt]);

  // Reset the ◀ ▶ selection whenever the in-range set changes
  useEffect(() => {
    const ids = consoleTargets.map(t => t.id).join(",");
    if (ids !== consoleIdsRef.current) {
      consoleIdsRef.current = ids;
      setConsoleIdx(0);
    }
  }, [consoleTargets]);

  const cycleConsole = useCallback((dir) => {
    setConsoleIdx(i => {
      const n = consoleTargets.length;
      return n ? (i + dir + n) % n : 0;
    });
  }, [consoleTargets.length]);

  const activateConsoleTarget = useCallback((t) => {
    if (!t) return;
    if (t.kind === "portal") {
      if (portalPrompt?.id === "pierre") {
        setPortalPrompt(null);
        onPierre && onPierre({ ...profile, uid, name: playerName });
      } else {
        setPortalActive(true);
      }
    } else if (t.kind === "shop") {
      handleShopEnter();
    } else if (t.kind === "npc") {
      handleNPCEnter();
    } else if (t.kind === "pet") {
      setPetPrompt(null); setShowPet(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalPrompt, profile, uid, playerName, shopPrompt, npcPrompt]); // handlers close over prompt state

  const dismissConsoleTarget = useCallback((t) => {
    if (!t) return;
    if (t.kind === "portal") {
      setPortalPrompt(null); setPortalActive(false);
      remove(ref(db, `${NET}/ready/${portalPrompt?.id}/${uid}`));
    }
    else if (t.kind === "shop") setShopPrompt(null);
    else if (t.kind === "npc")  setNpcPrompt(null);
    else if (t.kind === "pet")  setPetPrompt(null);
  }, [portalPrompt, uid]);

  // Console keys — Q/Enter activate · Tab cycle · Esc/Backspace dismiss.
  // 400ms arming delay mirrors the old bubbles (the walk-up keypress
  // can't instantly activate). Listener only exists while targets exist.
  useEffect(() => {
    if (consoleTargets.length === 0) return;
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 400);
    const onKey = (e) => {
      if (!armed) return;
      if (e.key === "q" || e.key === "Q" || e.key === "Enter") {
        e.preventDefault();
        activateConsoleTarget(consoleTargets[consoleIdx] || consoleTargets[0]);
      } else if (e.key === "Tab" && consoleTargets.length > 1) {
        e.preventDefault(); cycleConsole(1);
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        dismissConsoleTarget(consoleTargets[consoleIdx] || consoleTargets[0]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(armTimer); window.removeEventListener("keydown", onKey); };
  }, [consoleTargets, consoleIdx, activateConsoleTarget, dismissConsoleTarget, cycleConsole]);

  const handleSendChallenge = async () => {
    if (!challengePrompt || !uid || !profile) return;
    const { targetUid, targetName } = challengePrompt;
    setChallengePrompt(null);
    try {
      const { createMatch, drawAndDepleteCards } = await import("./gameManager");
      const deckKey = profile.activeDeck || Object.keys(profile.decks || {})[0];
      const deck = profile.decks?.[deckKey];
      if (!deck) { showToast("No deck equipped", SD.red); return; }
      // drawAndDepleteCards(grimoire, currentHand, count) → { hand, grimoire }.
      // The old call passed luxmoire as the hand and no count (drew the WHOLE
      // grimoire), then destructured names the function never returns — the
      // match was created with an undefined grimoire and undefined hand.
      const { hand: startingHand, grimoire: depletedGrimoire } = drawAndDepleteCards(deck.grimoire || {}, [], 5);
      const code = await createMatch(playerName, depletedGrimoire, deck.luxmoire || {}, deck.prophecy || null, startingHand, deck.curios || [null, null]);
      // Write challenge to target
      await set(ref(db, `${NET}/challenges/${targetUid}`), {
        fromUid: uid,
        fromName: playerName,
        code,
        ts: Date.now(),
      });
      showToast(`⚔ Challenge sent to ${targetName}...`, GM.inkGold);
      // Auto-clean after 15s if not accepted
      setTimeout(() => remove(ref(db, `${NET}/challenges/${targetUid}`)), 15000);
      // Enter as player 0
      onEnterGame && onEnterGame(code, 0, playerName);
    } catch (e) {
      showToast("Challenge failed", "#dc2626");
    }
  };

  const handleAcceptChallenge = async () => {
    if (!incomingChallenge || !uid || !profile) return;
    const { code, fromName } = incomingChallenge;
    // SEAT PLAYER 1 BEFORE ENTERING. The old accept path jumped straight to
    // onEnterGame without ever calling joinMatch, so players[1] stayed null,
    // status never flipped off "waiting", bothPresent could never be true,
    // and BOTH clients hung forever on "Waiting for opponent...". Proximity
    // duels literally could not start. We now load the accepter's active
    // deck, draw the opening hand, and claim seat 1 (mirrors the Vyren
    // kiosk code-join) — only entering once the seat is committed.
    try {
      const { joinMatch, drawAndDepleteCards } = await import("./gameManager");
      const deckKey = profile.activeDeck || Object.keys(profile.decks || {})[0];
      const deck    = profile.decks?.[deckKey];
      if (!deck) { showToast("No deck equipped — visit The Forge first", SD.red); return; }
      const { hand: startingHand, grimoire: depletedGrimoire } =
        drawAndDepleteCards(deck.grimoire || {}, [], 5);
      const joined = await joinMatch(
        code, playerName, depletedGrimoire,
        deck.luxmoire || {}, deck.prophecy || null,
        startingHand, deck.curios || [null, null]
      );
      // joinMatch returns { error } or { success } — an object is always
      // truthy, so we must inspect the shape, not the value.
      if (joined?.error) { showToast(joined.error, SD.red); return; }
      remove(ref(db, `${NET}/challenges/${uid}`));
      setIncomingChallenge(null);
      showToast(`⚔ Entering Solrend vs ${fromName}...`, GM.inkGold);
      onEnterGame && onEnterGame(code, 1, playerName);
    } catch (e) {
      showToast("Failed to join challenge", SD.red);
    }
  };

  const handleDeclineChallenge = () => {
    remove(ref(db, `${NET}/challenges/${uid}`));
    setIncomingChallenge(null);
  };

  // ── START MENU SECTIONS — every interactable, one front door ──
  // Each leaf fires the same opener the in-world prompt/portal uses.
  const menuSections = [
    {
      id: "village", label: "The Village", icon: "🔥",
      items: VILLAGE_BUILDINGS.filter(b => b.id !== "gate").map(b => ({
        id: b.id, label: b.name,
        run: () => onEnterBuilding && onEnterBuilding(b.id, { ...profile, uid, name: playerName }),
      })),
    },
    {
      id: "world", label: "The World", icon: "🗺",
      items: [
        { id: "outskirts", label: "The Outskirts", run: () => onEnterDungeon && onEnterDungeon({ id: "gate" }, { ...profile, uid, name: playerName }) },
      ],
    },
    {
      id: "travel", label: "Travel", icon: "✦",
      items: [
        { id: "pier", label: "W'ily's Pier", run: () => onPierre && onPierre({ ...profile, uid, name: playerName }) },
      ],
    },
    {
      id: "deeds", label: "Deeds", icon: "🏅",
      items: [
        { id: "deeds", label: "Deeds & Renown", run: () => setShowGallery(true) },
      ],
    },
    {
      id: "codex", label: "The Codex", icon: "◆",
      items: [
        { id: "codex", label: "The Four Ilk", run: () => setShowCodex(true) },
      ],
    },
    {
      id: "settings", label: "Settings", icon: "⚙",
      items: [
        { id: "appearance", label: "Appearance", run: () => setShowGenderSettings(true) },
      ],
    },
    // Log out lives at the BOTTOM of the main menu (caP'n, July 8) — off the
    // HUD, into the front door where session-level actions belong.
    {
      id: "session", label: "Log out", icon: "⊗",
      items: [
        { id: "logout", label: "Confirm Log Out", run: () => handleLogout() },
      ],
    },
  ];

  const allPlayers = [
    { sid: sessionId.current, data: { name: playerName, x: pos.x, y: pos.y, facingX: facing.x, facingY: facing.y, gender, companionId: myCompanionId || null, equipped: profile?.equipped || null }, isLocal: true, colorIndex: 0 },
    ...Object.entries(players).map(([sid, data], i) => ({ sid, data, isLocal: false, colorIndex: i+1 })),
  ];

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, background: "linear-gradient(160deg,#07050f 0%,#050308 60%,#060410 100%)", fontFamily: GM.font, overflow: "hidden" }}>
      {/* LETTERBOX BACKDROP — equipped in the Backdrop Atelier */}
      <RoomBackdrop uid={uid} />
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes bubbleFloat { from{transform:translateX(-50%) translateY(0px)} to{transform:translateX(-50%) translateY(-5px)} }
        @keyframes fightPulse { from{opacity:0.7;transform:scale(1)} to{opacity:1;transform:scale(1.04)} }
      `}</style>

      {/* HUD — desktop: a compact pill cluster top-LEFT (controls) plus
          a title/crest/count pill docked at the TOP of the right letterbox
          column (caP'n, July 8 — chrome lives in the vertical band now, no
          more full-width veil eating the top of the screen). Mobile keeps
          its slim full-width bar. */}
      {!mobile ? (
        <>
          {/* Controls — top-left. ONLY Exit Plaza now: Log out moved to
              the main menu's footer, gender to Settings → Appearance. */}
          <div style={{ position: "absolute", top: 12, left: 14, zIndex: 55,
            display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={onBack} style={{ background: OBSIDIAN_SLAB, border: "none",
              clipPath: facetTB(6),
              boxShadow: "inset 0 1px 0 rgba(226,232,244,0.16), inset 0 -4px 8px rgba(0,0,0,0.4)",
              filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
              color: GM.silver, padding: "5px 12px", textShadow: TEXT_SET,
              fontFamily: GM.font, letterSpacing: 1,
              fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>
              ← Exit Village
            </button>
          </div>

          {/* Title + crest + player-count — a pill in the right column,
              docked BELOW the chrome rail (MENU + chat bubble sit above
              it). Same 24vw band as the console. */}
          <div style={{ position: "absolute", top: 112, right: 12, zIndex: 55,
            width: "min(280px, 24vw)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 8, padding: "6px 14px",
            background: "linear-gradient(175deg, rgba(5,3,12,0.95) 0%, rgba(13,9,24,0.92) 100%)",
            border: `1px solid ${GM.pageBorder}`, borderRadius: 999,
            boxShadow: "0 8px 26px rgba(0,0,0,0.65), inset 0 1px 0 rgba(226,232,244,0.08)" }}>
            <span style={{ fontSize: 12, color: "#f0cc60", fontWeight: 700,
              letterSpacing: 2.5, fontFamily: GM.font, whiteSpace: "nowrap",
              textShadow: textGlow(GM.inkGoldBright) }}>◆ THE OLD FOLD</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {profile && <CrestBadge amount={profile.divineCrests} />}
              <div style={{ display: "flex", alignItems: "center", gap: 4,
                background: OBSIDIAN_RECESS, border: `1px solid ${GM.pageBorder}`,
                borderRadius: 20, padding: "2px 8px",
                boxShadow: "inset 0 2px 6px rgba(0,0,0,0.7)",
                fontSize: 9.5, fontWeight: 700, fontFamily: GM.fontUI,
                color: GM.vellumDim, textShadow: TEXT_SET }}>
                <span style={{ color: "#22c55e", textShadow: "0 0 6px #22c55e" }}>●</span>
                {Object.keys(players).length + 1}/4
              </div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 50,
          padding: "6px 8px",
          display: "flex", alignItems: "center", gap: 6,
          background: "linear-gradient(180deg, rgba(7,4,16,0.94) 0%, rgba(7,4,16,0.5) 60%, transparent 100%)" }}>
          <div style={{ position: "absolute", left: "6%", right: "6%", bottom: 0, height: 1, background: GOLD_METAL_H, opacity: 0.35, pointerEvents: "none" }} />
          <button onClick={onBack} style={{ background: OBSIDIAN_SLAB, border: "none",
            clipPath: facetTB(6),
            boxShadow: "inset 0 1px 0 rgba(226,232,244,0.16), inset 0 -4px 8px rgba(0,0,0,0.4)",
            filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
            color: GM.silver, padding: "4px 9px", textShadow: TEXT_SET,
            fontFamily: GM.font, letterSpacing: 1,
            fontSize: 10, cursor: "pointer", whiteSpace: "nowrap" }}>
            ← Exit
          </button>
          {playerName && (
            <button onClick={handleLogout} style={{ background: OBSIDIAN_SLAB, border: "none",
              clipPath: facetTB(6),
              boxShadow: "inset 0 1px 0 rgba(226,232,244,0.16), inset 0 -4px 8px rgba(0,0,0,0.4)",
              filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
              color: GM.silver, padding: "4px 9px", textShadow: TEXT_SET,
              fontFamily: GM.font, letterSpacing: 1,
              fontSize: 10, cursor: "pointer" }}>
              ⊗
            </button>
          )}
          {uid && playerName && !needsStarterPick && !needsOnboarding && (
            <button onClick={() => setShowGenderSettings(true)}
              style={{ background: OBSIDIAN_SLAB, border: "none",
                clipPath: facetTB(6),
                boxShadow: "inset 0 1px 0 rgba(226,232,244,0.16), inset 0 -4px 8px rgba(0,0,0,0.4)",
                filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
                color: GM.silver, padding: "4px 9px", textShadow: TEXT_SET,
                fontFamily: GM.font,
                fontSize: 10, cursor: "pointer" }}>
              {gender === "f" ? "♀" : "♂"}
            </button>
          )}
          <div style={{ flex: 1, textAlign: "center" }}>
            <span style={{ fontSize: 10, color: "#f0cc60", fontWeight: 700,
              letterSpacing: 2, fontFamily: GM.font,
              textShadow: textGlow(GM.inkGoldBright) }}>◆ THE OLD FOLD</span>
          </div>
          {profile && <CrestBadge amount={profile.divineCrests} />}
          <div style={{ display: "flex", alignItems: "center", gap: 5,
            background: OBSIDIAN_RECESS, border: `1px solid ${GM.pageBorder}`,
            borderRadius: 20, padding: "3px 10px",
            boxShadow: "inset 0 2px 6px rgba(0,0,0,0.7)",
            fontSize: 10, fontWeight: 700, fontFamily: GM.fontUI,
            color: GM.vellumDim, textShadow: TEXT_SET }}>
            <span style={{ color: "#22c55e", textShadow: "0 0 6px #22c55e" }}>●</span>
            {Object.keys(players).length + 1}/4
          </div>
        </div>
      )}

      {/* WORLD — 3D by default (OldFold3D), 2D tile map as the
          flag-gated rollback. The 3D renderer mirrors the world-layer
          contract exactly: every callback is wrapped here so the
          profile rides along, same as the 2D prompt handlers do. */}
      {VILLAGE_3D ? (
        <OldFold3D
          character={profile?.annihilation___character || null}
          gateEntry={GATE_DESTINATION}
          players={Object.entries(players).map(([sid, data]) => ({
            sid,
            name: data.name,
            x: data.x, y: data.y,
            facingX: data.facingX, facingY: data.facingY,
            beingId: data.beingId || null,
            beingTier: data.beingTier || null,
          }))}
          onEnterBuilding={(screen) =>
            onEnterBuilding && onEnterBuilding(screen, { ...profile, uid, name: playerName })}
          onEnterShop={() =>
            onEnterBuilding
              ? onEnterBuilding("marketplace", { ...profile, uid, name: playerName })
              : onEnterShop && onEnterShop({ ...profile, uid, name: playerName })}
          onForge={() => onForge && onForge({ ...profile, uid, name: playerName })}
          onPierre={() => onPierre && onPierre({ ...profile, uid, name: playerName })}
          onEnterNPC={(id) => {
            // Villagers speak from villageData — same one-source rule
            // the 2D prompt handler follows.
            const villager = VILLAGERS.find((v) => v.id === id);
            if (villager) {
              const lines = (villager.lines && villager.lines.length) ? villager.lines : [villager.persona];
              const line = lines[Math.floor(Math.random() * lines.length)];
              addPlazaLog({ label: villager.name, color: GM.inkGold, text: line });
              showToast(`${villager.name}: “${line}”`, GM.inkGold);
            }
          }}
          onEnterDungeon={(entry) =>
            onEnterDungeon && onEnterDungeon(entry || GATE_DESTINATION, { ...profile, uid, name: playerName })}
          onBack={onBack}
        />
      ) : (
      <div style={{ position:"absolute",
        left:0,
        right: portrait ? 0 : RECORD_RESERVE(),
        top:0,
        bottom: portrait ? "auto" : 0,
        height: portrait ? getViewportH() : "auto",
        overflow:"hidden",
        cursor: mobile ? "default" : "none", background:"transparent" }}>
        {portrait && (
          <div style={{ position:"absolute", left:0, right:0, bottom:0, height:20,
            pointerEvents:"none", zIndex:6,
            background:"linear-gradient(180deg, transparent, #050308)" }} />
        )}
        <div ref={worldRef} style={{
          position: "absolute",
          left: 0,
          top: 0,
          width:  activeCOLS * TILE,
          height: activeROWS * TILE,
          transform: `translate3d(${-(camOffset?.x || 0)}px, ${-(camOffset?.y || 0)}px, 0)`,
          willChange: "transform",
          transition: "none",
        }}>

          {/* GENERATED OLD FOLD — ground, then (below) walls, buildings,
              villagers. Renders until real art loads; the img sits at a
              higher z and simply covers it once p_center.webp exists. */}
          {!bgLoaded && (
            <OldFoldGround cols={activeCOLS} rows={activeROWS} tile={TILE}
              portalsArr={activePORTALS} portrait={portrait} />
          )}
          {PLAZA_BG && (
            <img src={PLAZA_BG} alt="plaza"
              onLoad={() => setBgLoaded(true)}
              onError={e => { e.target.style.display = "none"; setBgLoaded(false); }}
              style={{ position:"absolute", left:0, top:0,
                width: activeCOLS * TILE,
                height: activeROWS * TILE,
                imageRendering:"pixelated", objectFit:"fill",
                zIndex:1, pointerEvents:"none" }} />
          )}

          {!bgLoaded && activeMAP.map((row, y) => row.map((type, x) =>
            type === T.WALL ? (
              <div key={`${x}_${y}`} style={{ position:"absolute", left:x*TILE, top:y*TILE,
                width:TILE, height:TILE, zIndex:2, boxSizing:"border-box",
                background:"linear-gradient(180deg, #26332a 0%, #1b241e 55%, #141a16 100%)",
                border:"1px solid #0d120f", borderRadius:3,
                boxShadow:"inset 0 2px 2px rgba(160,200,160,0.10), inset 0 -3px 4px rgba(0,0,0,0.5)" }} />
            ) : null
          ))}

          {/* Generated buildings + villagers — same gate as the ground */}
          {!bgLoaded && activeSHOPS.map(s => (
            <BuildingVisual key={`bv_${s.id}`} shop={s} tile={TILE} />
          ))}
          {!bgLoaded && activeNPCS.map(n => (
            <VillagerVisual key={`nv_${n.id}`} npc={n} tile={TILE} />
          ))}

          {/* Portals — always visible (PortalVisual carries its own
              sprite→glow fallback; it was defined but never rendered
              until July 15). The Gate and Pierre now glow on the map. */}
          {activePORTALS.map(p => (
            <PortalVisual key={`pv_${p.id}`} portal={p} tile={TILE} />
          ))}

          {/* Invisible interaction zones */}
          {activePORTALS.map(p => (
            <div key={p.id} style={{ position:"absolute", left:p.x*TILE, top:p.y*TILE, width:TILE, height:TILE, zIndex:4 }} />
          ))}
          {activeSHOPS.map(s => (
            <div key={s.id} style={{ position:"absolute", left:s.x*TILE, top:s.y*TILE, width:TILE*2, height:TILE*2, zIndex:4 }} />
          ))}
          {activeNPCS.map(n => (
            <div key={n.id} style={{ position:"absolute", left:n.x*TILE, top:n.y*TILE, width:TILE, height:TILE, zIndex:4 }} />
          ))}

          {/* Players */}
          {allPlayers.map(({ sid, data, isLocal, colorIndex }) => (
            <PlayerEntity
              key={sid}
              player={data}
              isLocal={isLocal}
              colorIndex={colorIndex}
              companionId={data.companionId}
              equipped={data.equipped}
              tile={TILE}
              localSpriteRef={isLocal ? localSpriteRef : undefined}
              localCompanionRef={isLocal ? localCompanionRef : undefined}
              compDir={isLocal ? compOffset : undefined}
              radiantTier={isLocal ? (myCompanionRadiant || null) : undefined}
              beingId={isLocal ? myBeingId : (data.beingId || null)}
              beingTier={isLocal ? myBeingTier : (data.beingTier || null)}
            />
          ))}

          {/* Floating proximity bubbles EXCISED (July 8) — every prompt
              now flows through the RoomConsole (rendered outside the map):
              persistent session log + ◀ ▶ / Tab cycling across ALL prompts
              in range simultaneously (old precedence chain retired). */}

          {/* ── SOLREND CHALLENGE PILL ── */}
          {challengePrompt && !portalPrompt && !shopPrompt && !npcPrompt && (
            <DockPortal align="top">
            <div style={{
              position: portrait ? "relative" : "fixed",
              left: portrait ? undefined : "50%",
              bottom: portrait ? undefined : (mobile ? 120 : 80),
              transform: portrait ? "none" : "translateX(-50%)",
              zIndex: 60, display: "flex", gap: 8, alignItems: "center",
            }}>
              <button onClick={handleSendChallenge} style={{
                padding: "10px 24px", borderRadius: 24,
                background: `linear-gradient(135deg, ${GM.pageMid}, #3b0f6e)`,
                border: `1px solid ${GM.inkGold}`,
                color: GM.vellum, fontFamily: GM.font,
                fontSize: 13, fontWeight: 900, letterSpacing: 2,
                cursor: "pointer", animation: "fightPulse 1s ease-in-out infinite alternate",
                boxShadow: `0 0 20px ${GM.inkGold}66`,
              }}>
                ⚔ Solrend · {challengePrompt.targetName}
              </button>
              <button onClick={() => setChallengePrompt(null)} style={{
                padding: "8px 14px", borderRadius: 20,
                background: "transparent", border: `1px solid ${GM.inkBrown}66`,
                color: GM.vellumFaint, fontFamily: GM.font, fontSize: 11,
                cursor: "pointer",
              }}>✕</button>
            </div>
            </DockPortal>
          )}
        </div>
      </div>
      )}

      {/* ── PORTRAIT DOCK ── reserved bottom half on a phone.
          Holds the d-pad and is the landing zone for popups (pet
          close-up, etc.) via portrait-aware placement below. On
          desktop/landscape this renders nothing. */}
      {portrait && (
        <div id="ie-dock" style={{
          position: "absolute", left: 0, right: 0, bottom: 0,
          height: window.innerHeight - getViewportH(),
          zIndex: 40, overflow: "hidden",
          background: "linear-gradient(180deg, #050308 0%, #07050f 100%)",
          borderTop: `1px solid ${GM.inkGold}2e`,
          boxShadow: "inset 0 14px 28px rgba(0,0,0,0.55)",
        }}>
          {/* faint manuscript ruling so empty dock isn't dead space */}
          <div style={{ position:"absolute", inset:0, pointerEvents:"none", opacity:0.5,
            background:"repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(122,109,156,0.07) 28px)" }} />
        </div>
      )}
      {/* ── INCOMING CHALLENGE MODAL ── */}
      {incomingChallenge && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
        }}>
          <div style={{
            background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`,
            border: `1px solid ${GM.inkGold}`,
            borderRadius: 16, padding: "32px 40px",
            textAlign: "center", fontFamily: GM.font,
            boxShadow: `0 0 40px ${GM.inkGold}44`,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 20,
          }}>
            <div style={{ fontSize: 11, color: GM.inkGold, letterSpacing: 3 }}>INCOMING CHALLENGE</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: GM.vellum, fontStyle: "italic" }}>
              ⚔ {incomingChallenge.fromName}
            </div>
            <div style={{ fontSize: 11, color: GM.vellumFaint }}>challenges you to a Solrend</div>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={handleAcceptChallenge} style={{
                padding: "12px 28px", borderRadius: 12,
                background: `linear-gradient(135deg, ${GM.pageMid}, #3b0f6e)`,
                border: `1px solid ${GM.inkGold}`,
                color: GM.vellum, fontFamily: GM.font,
                fontSize: 13, fontWeight: 900, cursor: "pointer",
                boxShadow: `0 0 16px ${GM.inkGold}44`,
              }}>Accept</button>
              <button onClick={handleDeclineChallenge} style={{
                padding: "12px 28px", borderRadius: 12,
                background: "transparent",
                border: `1px solid ${GM.inkBrown}66`,
                color: GM.vellumFaint, fontFamily: GM.font,
                fontSize: 13, cursor: "pointer",
              }}>Decline</button>
            </div>
          </div>
        </div>
      )}

      {/* Controls hint — desktop only */}
      {!mobile && (
        <div style={{ position:"absolute", bottom:12, left:"50%", transform:"translateX(-50%)",
          fontSize:9, color:GM.vellumFaint, letterSpacing:2, zIndex:50, fontFamily: GM.fontUI, fontWeight: 600,
          background:"rgba(7,4,16,0.72)", border:`1px solid ${GM.pageBorder}`,
          boxShadow:"inset 0 2px 6px rgba(0,0,0,0.6)", textShadow: TEXT_SET,
          padding:"4px 14px", borderRadius:12 }}>
          WASD / ARROW KEYS TO MOVE
        </div>
      )}

      {/* Mobile D-pad — suppressed while the pet close-up owns the dock
          in portrait so it doesn't float over the codex card. */}
      {mobile && !(portrait && showPet) && <MobileDpad keysRef={keysRef} />}

      {/* START — opens the unified menu (also bound to the M key).
          Desktop: lives at the TOP of the right-column CHROME RAIL (caP'n,
          July 8 — menu + chat live in the backdrop band now). Mobile:
          stays bottom-left for thumb reach. Suppressed while the pet
          close-up owns the dock in portrait. */}
      {uid && playerName && !needsStarterPick && !needsOnboarding && authReady && !showMenu && !(portrait && showPet) && (
        <button
          onClick={() => setShowMenu(true)}
          aria-label="Open menu"
          style={{
            position: "fixed",
            ...(mobile
              ? { left: 16, bottom: 26 }
              : { right: 12, top: 12, width: "min(280px, 24vw)", justifyContent: "center" }),
            zIndex: 70,
            display: "flex", alignItems: "center", gap: 6,
            padding: mobile ? "11px 18px" : "9px 18px",
            background: OBSIDIAN_SLAB,
            border: "none",
            borderTop: "1px solid #f5d878",
            clipPath: facetTB(9),
            color: "#ffe9a8", fontFamily: GM.font,
            fontSize: 12, fontWeight: 700, letterSpacing: 2,
            cursor: "pointer",
            boxShadow: `inset 0 1px 0 rgba(245,216,120,0.5), inset 0 -6px 14px rgba(0,0,0,0.45), inset 0 0 16px ${GM.amethyst}22`,
            filter: `drop-shadow(0 4px 10px rgba(0,0,0,0.65)) drop-shadow(0 0 9px ${GM.inkGoldBright}44)`,
            textShadow: textGlow(GM.inkGoldBright),
            userSelect: "none", WebkitUserSelect: "none",
          }}
        >
          {mobile && <span style={{ position: "absolute", left: 0, top: "16%", bottom: "16%", width: 3, background: GOLD_METAL, boxShadow: `0 0 7px ${GM.inkGoldBright}cc` }} />}
          ☰ MENU
        </button>
      )}

      {/* START MENU — Pokémon-style nested front door to every interactable */}
      {showMenu && (
        <StartMenu sections={menuSections} onClose={() => setShowMenu(false)} />
      )}

      {/* COMPANION PETTING — hearts + codex close-up.
          In portrait it docks into the reserved bottom zone (DockPortal);
          on desktop/landscape it's a normal centered overlay. */}
      {showPet && (
        <DockPortal>
          <CompanionCloseup
            companionId={myCompanionId}
            radiantTier={myCompanionRadiant || null}
            profile={profile}
            docked={portrait}
            onClose={() => {
              setShowPet(false);
              // If we're still facing the companion, re-offer the Pet prompt.
              const co = compOffsetRef.current, f = facingRef.current;
              if (myCompanionId && co.x === f.x && co.y === f.y) {
                setPetPrompt({ x: posRef.current.x + co.x, y: posRef.current.y + co.y });
              }
            }} />
        </DockPortal>
      )}

      {/* Chat — only when logged in and settled. Desktop: docked into the
          right-column CHROME RAIL, just under MENU. Mobile: bottom-left. */}
      {uid && playerName && !needsStarterPick && !needsOnboarding && authReady && (
        <ChatSystem {...chat} tile={TILE} camOffset={camOffset || { x: 0, y: 0 }}
          togglePos={mobile ? { left: 16, bottom: 80 } : { right: 12, top: 60 }} />
      )}

      {/* LOGIN */}
      {authReady && !playerName && <LoginOverlay onLogin={handleLogin} />}

      {/* ONBOARDING — first-login welcome + gender + Ilk + starter deck */}
      {authReady && uid && playerName && needsOnboarding && (
        <Onboarding uid={uid} playerName={playerName} onComplete={handleOnboardingComplete} />
      )}

      {/* STARTER PICK — fallback for profiles that completed onboarding
          without picking a deck (edge case). New users go through Onboarding. */}
      {authReady && uid && playerName && !needsOnboarding && needsStarterPick && (
        <StarterPickerOverlay playerName={playerName} uid={uid} onPick={handleStarterPicked} />
      )}

      {/* GENDER SETTINGS */}
      {showGenderSettings && (
        <GenderSettings uid={uid} currentGender={gender}
          onClose={() => setShowGenderSettings(false)}
          onChanged={(g) => setGender(g)} />
      )}

      {/* VYREN KIOSK */}
      {showVyren && (
        <VyrenKiosk playerName={playerName} profile={{ ...profile, uid }}
          onClose={() => setShowVyren(false)}
          onEnterGame={(code, idx, name) => { setShowVyren(false); onEnterGame && onEnterGame(code, idx, name); }}
          onEnterNPC={(name, deck, prophecy, hand, diff, meta) => { setShowVyren(false); onEnterNPC && onEnterNPC(name, deck, prophecy, hand, diff, meta); }} />
      )}

      {/* BULLETIN BOARD */}
      {showBoard && (
        <BulletinBoard profile={{ ...profile, uid }} onClose={() => setShowBoard(false)}
          onCollection={(tab) => { setShowBoard(false); onCollection && onCollection({ ...profile, uid, name: playerName, initialTab: tab }); }} />
      )}

      {showHub && (
        <PlazaHub
          profile={{ ...profile, uid, name: playerName }}
          logLine={(entry) => addPlazaLog(entry)}
          section={hubSection}
          onClose={() => { setShowHub(false); setHubSection(null); }}
          onOpenCodex={() => {
            setShowHub(false);
            onCollection && onCollection({ ...profile, uid, name: playerName });
          }}
          onForge={() => {
            setShowHub(false);
            onForge && onForge({ ...profile, uid, name: playerName });
          }}
        />
      )}

      {/* ROOM CONSOLE — prompts + the persistent session record.
          Desktop: vertical panel in the right letterbox bar.
          Portrait: lands in the reserved dock via DockPortal. */}
      {!showMenu && !showPet && (consoleTargets.length > 0 || plazaLog.length > 0) && (
        portrait ? (
          <DockPortal align="top">
            <RoomConsole inline zoneId="plaza"
              log={plazaLog}
              nearbyList={consoleTargets}
              nearbyIndex={consoleIdx}
              onCycle={cycleConsole}
              onActivate={() => activateConsoleTarget(consoleTargets[consoleIdx] || consoleTargets[0])}
            />
          </DockPortal>
        ) : (
          <RoomConsole
            zoneId="plaza"
            topOffset={156}
            log={plazaLog}
            nearbyList={consoleTargets}
            nearbyIndex={consoleIdx}
            onCycle={cycleConsole}
            onActivate={() => activateConsoleTarget(consoleTargets[consoleIdx] || consoleTargets[0])}
          />
        )
      )}

      {/* WILLY */}
      {showWilly && (
        <WillyDialogue profile={profile} uid={uid} showToast={showToast}
          logLine={(text) => addPlazaLog({ label: "Willy", color: GM.inkGold, text })}
          onClose={() => setShowWilly(false)}
          onTutorial1={() => { setShowWilly(false); onTutorial1 && onTutorial1(); }}
          onRulebook={() => { setShowWilly(false); onRulebook && onRulebook(); }} />
      )}

      {/* PORTAL READY-UP */}
      {portalPrompt && portalActive && portalPrompt.id !== "inner" && (
        <PortalReadyUp portal={portalPrompt} players={players} localPlayerName={playerName} localUid={uid}
          readySet={readySet}
          onReady={() => set(ref(db, `${NET}/ready/${portalPrompt.id}/${uid}`), true)}
          onCancel={() => { remove(ref(db, `${NET}/ready/${portalPrompt.id}/${uid}`)); setPortalPrompt(null); setPortalActive(false); }} />
      )}

      {/* INNER EMPERIUM */}
      {portalPrompt && portalActive && portalPrompt.id === "inner" && !showDepths && (
        <InnerEmperiumSelect playerName={playerName} profile={{ ...profile, uid }}
          onClose={() => { setPortalPrompt(null); setPortalActive(false); }}
          onDepths={() => setShowDepths(true)}
          onEnterNPC={(diff, villain) => {
            setPortalPrompt(null);
            const deck = resolveDeck(profile);
            if (!deck) { showToast("No deck found. Visit The Forge first.", "#dc2626"); return; }
            onEnterNPC && onEnterNPC(playerName, { grimoire:deck.grimoire||{}, luxmoire:deck.luxmoire||{}, curios:deck.curios||[null,null] }, deck.prophecy||null, null, diff, { isInnerEmperium:true, villainId:villain?.id||null, villainName:villain?.name||null, storyUid:uid||playerName, collection: buildHoloCollectionFromDeck(profile?.collection, deck) });
          }} />
      )}

      {/* ENDLESS DEPTHS */}
      {showDepths && (
        <EndlessDepths playerName={playerName} uid={uid} profile={profile}
          onClose={() => setShowDepths(false)}
          onFight={(opponent, depth) => {
            setShowDepths(false); setPortalPrompt(null); setPortalActive(false);
            const deck = resolveDeck(profile);
            if (!deck) { showToast("No deck found. Visit The Forge first.", "#dc2626"); return; }
            onEnterNPC && onEnterNPC(playerName, { grimoire:deck.grimoire||{}, luxmoire:deck.luxmoire||{}, curios:deck.curios||[null,null] }, deck.prophecy||null, null, opponent.difficulty, { isEndlessDepths:true, depthOpponentId:opponent.id, depthOpponentName:opponent.name, depthIsGatekeeper:!!opponent.isGatekeeper, depthGatekeeperId:opponent.isGatekeeper?opponent.id:null, storyUid:uid||playerName, difficulty:opponent.difficulty, collection: buildHoloCollectionFromDeck(profile?.collection, deck) });
          }} />
      )}

      {/* PORTAL CINEMATIC */}
      {portalCinematicData && (
        <PortalCinematic portal={portalCinematicData.portal} dungeon={portalCinematicData.dungeon}
          onComplete={() => {
            const { portal, profile } = portalCinematicData;
            setPortalCinematicData(null);
            onEnterDungeon && onEnterDungeon(portal, profile);
          }} />
      )}

      {/* TOAST */}
      {toast && (
        <div style={{ position:"absolute", top:60, left:"50%", transform:"translateX(-50%)",
          background:`${GM.pageWarm}f0`, border:`1px solid ${toast.color}`,
          color:toast.color, padding:"8px 20px", borderRadius:2,
          fontSize:12, zIndex:300, fontFamily:GM.font,
          boxShadow:`0 0 16px ${toast.color}33` }}>
          {toast.msg}
        </div>
      )}

      {/* DEEDS & RENOWN — the achievement wall */}
      {showGallery && uid && (
        <AchievementGallery uid={uid} onClose={() => setShowGallery(false)} />
      )}

      {/* THE CODEX — the four Ilk, read live from beings.js */}
      {showCodex && (
        <AnnihilationCodex onClose={() => setShowCodex(false)} />
      )}

      {/* ACHIEVEMENT TOAST — global, self-queuing. Surfaces any newly
          earned achievement no matter where in the app it was granted. */}
      <AchievementToast ids={toastIds} onDrained={() => setToastIds([])} />

      {/* ── PORTRAIT LOCK CURTAIN ── topmost; blocks landscape on
          devices where the OS-level hard lock isn't granted. */}
      <RotateGate show={needsRotate} />
    </div>
  );
}